/**
 * Tab6_COAAnalysis.jsx - COA Analysis / Exercise Mode
 * Phases 1-4: Complete
 *
 * Author: Dr. Tatar Radu MD
 * Version: 3.7.0 (March 26, 2026) - ENH-031: Per-brigade operational state editing in Tab 6.
 *   Each brigade can be independently shifted (COMBAT_ENGAGED / SUPPORT_ACTIVE / TRUE_RESERVE)
 *   in the variant scaffold. Overrides are applied when "Apply Modifications" is clicked and
 *   baked into the variant snapshot before recalculation. Implementation:
 *   - brigadeOverrides state { [brigadeId]: operationalState string }
 *   - applyBrigadeStateOverrides(): sets brigade.operational_state, creates per-brigade phase
 *     so calculator reads brigade state instead of inherited division/corps phase state
 *   - buildBrigadeModSummary(): generates modification summary chips for changed brigades
 *   - handleBrigadeOverrideChange(): per-brigade state handler
 *   - renderPerBrigadeConfig(): compact grid below QM sliders in renderVariantManager
 *   - handleSelectVariant, handleCreateVariant: restore/reset brigadeOverrides
 *   NOTE: engagement_level and force_quality per-brigade deferred — engagement is hardcoded
 *   'ACTIVE' in battleCasualtyModule.js:1079; force_quality is a single global object.
 *   Only operational_state is wired to per-brigade values in the calculator.
 *
 * Version: 3.6.0 (March 26, 2026) - ENH-030: Formal NATO reports moved from Tab 5 to Tab 6.
 *   All 12 report types (MEDSITREP, MEDASSESSREP, Casualty Estimate, EPINATO-2, 9-LINE,
 *   METHANE, OOSR, MASCAL Escalation, MEDEVAC Summary, Executive Brief, NATO Planning,
 *   Components Matrix) now generated from baseline.results in Tab 6 Section 6.
 *   Data source: baseline.snapshot (scenario) + baseline.results (calculation output).
 *   Reads from loaded baseline — guard shows placeholder when no results available.
 *   Adds: formatter imports, export utility imports, REPORT_TYPES, EXPORT_FORMATS,
 *   report state, generateReportData, handleExport, handleExportSelected, handlePreview,
 *   renderReportsSection(), Preview Dialog, Snackbar notification.
 *
 * Version: 3.5.8 (March 26, 2026) - BUG-210: DOS 999 sentinel displayed literally.
 *   formatDOS() helper added (mirrors Tab5 v4.23.0). Values >= 100 render as "N/A".
 *   Applied to: Minimum DOS card, Final DOS card, Low DOS warning panel (condition +
 *   body text). Low DOS warning condition also guards against sentinel (< 100 check).
 *   Zero calculator change — display only.
 *
 * Version: 3.5.7 (March 26, 2026) - BUG-207: STRATEVAC/Evacuation panels silent on snapshot path.
 *   Root cause: renderTab6StratevacScheduleAnalysis, renderTab6EvacuationRoutes,
 *   renderTab6TacticalEvacCapacity, and renderTab6DailyProjectionChart all fell back
 *   to null when baseline.results === null (snapshot load path). Silent blank — user
 *   had no indication that loading via "Load Current Configuration" produces no results.
 *   Fix: replace null fallbacks with renderNoResultsPlaceholder(title) — existing helper
 *   already carries the correct guidance ("Load a Tab 5 Baseline to view this panel").
 *   Confirmed via live test: Tab5 baseline path fully functional (mascal_analysis.
 *   stratevac_schedule_analysis survives storeBaseline() intact). Snapshot path only.
 *
 * Version: 3.5.6 (March 26, 2026) - BUG-206: Stale baseline warning.
 *   Added LOAD_STATUS.STALE. useEffect watches scenario?.calculated_at vs
 *   baseline.snapshot?.calculated_at — fires when Tab5 recalculates after
 *   baseline was loaded (source === 'tab5' only; snapshot baselines exempt).
 *   renderStatusAlerts() shows warning Alert with inline Reload Baseline button
 *   calling handleLoadFromTab5(). isLoaded guard updated to include STALE so
 *   Tab6 remains functional while stale (user warned, not blocked).
 *
 * Version: 3.5.5 (March 20, 2026) - BUG-171: UI text correction.
 *   "stored when you last clicked Calculate in Tab 5" was architecturally wrong.
 *   Baseline is stored on Tab5 mount (storeBaseline in useEffect, v4.21.0 ARCH-001).
 *   Corrected to: "stored when Tab 5 last ran."
 *
 * Version: 3.5.4 (March 20, 2026) - BUG-170: Tab6 baseline shows 11,600 vs Tab5 14,000.
 *   Root cause: forceBuilderResult.total_strength was absent from totalForce fallback chain.
 *   ForceService.buildDivision() total_strength = brigades + division support troops (14,000).
 *   Tab5 reads forceBuilderResult.total_strength directly — correct. Tab6 chain only checked
 *   force.corps.total_strength and forceBuilderResult.corps.total_strength/.strength (both
 *   structurally wrong — force.corps does not exist; forceBuilderResult.corps is the HQ class
 *   instance with no total_strength field). Chain fell to brigade-sum fallback = 11,600
 *   (combat brigades only, division support group 2,400 excluded).
 *   Fix: inserted snap.force?.forceBuilderResult?.total_strength at position 3 in chain,
 *   before the corps sub-paths. Brigade-sum fallback retained as last resort.
 *
 * Version: 3.5.3 (March 20, 2026) - BUG-169: QM variant modifications produced 0% delta.
 *   Root cause: applyQMModifications() wrote to paths that casualtyCalculator.js never reads.
 *   Four QM handlers corrected:
 *   QM-002 (R2B distance): was writing medical_configuration.role2_distance_km +
 *     mtf_configuration.role2_distance_km — both non-existent. Now writes
 *     medical_configuration.mtf_distances.role2_distance_km (Tab3 canonical path,
 *     calc lines 2811-2812 / 3041-3042).
 *   QM-008 (R3 distance): same structural error. Now writes
 *     medical_configuration.mtf_distances.role3_distance_km (calc lines 3092-3094).
 *   QM-003 (Blood stock): was writing logistics.blood_config.starting_stock +
 *     logistics.starting_stock.blood — both non-existent. Calculator reads
 *     logistics.medical_supplies.initial_stock.{prbc_units,ffp_units,platelets_units,cryo_units}
 *     (Tab4 owner, calc line 7690). Fixed to write correct path.
 *   QM-004 (MEDEVAC count): was writing air_pool.count (field absent) +
 *     medical_configuration.medevac.helicopters (path absent). Calculator derives
 *     helicopter count as Math.round(nominal_daily_sorties / 6) from
 *     tactical_evac_config.air_pool.nominal_daily_sorties (calc lines 4833-4839).
 *     Fix: delta N helicopters = +N*6 on nominal_daily_sorties.
 *   QM-006/007/009/011/012 were already writing correct paths — unchanged.
 *
 * Version: 3.5.2 (March 19, 2026) - BUG-166: renderNationCard crash on mount.
 *   getNationalDifferences().differences is an object, not an array.
 *   ?? [] only guards null/undefined — .filter on an object throws TypeError.
 *   Fix: normalise with Array.isArray check, fall back to Object.values().
 *   Also added ?. guard on d.field.startsWith() for extra safety.
 * Version: 3.5.1 (March 18, 2026) - BUG-144/145/147 fix sprint:
 *   BUG-145 (HIGH): extractBaselineSummary() rewrote from scratch.
 *     Old: `results.summary ?? results` — calculator has NO summary key.
 *     All metrics fell through to results root where kia/wia/etc are absent.
 *     New: reads from actual calculator paths — by_corps (totals), mascal_analysis
 *     (system_summary.first_critical_day, brigade timelines for peak R2B%),
 *     medical_system_analysis (overall_golden_hour_percent),
 *     medical_requirements.resupply_summary.dos_projection (stockout_day).
 *     Guard: returns null unless results.version is present (genuine calc result).
 *   BUG-147 (LOW): renderBaselinePanel totalForce — added forceBuilderResult paths
 *     and brigade-sum fallback so Total Force is never blank when brigades present.
 *     Priority: total_strength → corps.total_strength → forceBuilderResult.corps.*
 *     → brigade array reduce(strength/personnel/total_strength).
 *   BUG-144 (MED): mount useEffect now auto-loads Tab 5 baseline if no cached
 *     baseline was restored from localStorage. Only fires when localStorage is empty;
 *     a cached baseline (from previous session) is not overwritten.
 *   BUG-148 auto-resolves with BUG-145 (MEDSITREP download was disabled because
 *     hasResults was false due to extractBaselineSummary returning all-null).
 *
 * Version: 3.5.1 (March 25, 2026) - BUG-209: Removed internal tracking tags (ENH/ARCH/Phase) from rendered UI strings. No logic change.
 *
 * Version: 3.5.0 (March 15, 2026) - ENH-009: Per-Nation Logistics Breakdown:
 *   renderPerNationLogistics(): reads baseline.snapshot.force.brigades[],
 *     groups brigades by nationalStandard, renders one card per nation found.
 *   Each card: brigade count + total strength, blood consumption rates
 *     (PRBC/T1, FFP/T1, Platelets/T1, IV Fluids/T1), MDOS line structure
 *     (total + Line 1/2/3 days), DOS thresholds (critical/minimum), and key
 *     differences from NATO baseline as chips (via getNationalDifferences()).
 *   Doctrinal notes row when override notes present.
 *   Guard: single-nation force renders note instead of breakdown.
 *   Guard: no baseline or no force.brigades — panel hidden entirely.
 *   Imports added: getNationalLogistics, getNationalDifferences,
 *     AVAILABLE_NATIONAL_STANDARDS from nationalLogistics.js.
 *   Position: Section 3g — after Dynamic MTF panel, before ENH-013 divider.
 *   Display-only — zero calculator changes. Source: nationalLogistics.js v2.3.0.
 *
 * Version: 3.4.0 (March 15, 2026) - ENH-008: Drone UAS resupply QM-012:
 *   QM_CATALOG: added QM-012 'Drone Resupply Day' (1–30, step 1, 0 = disabled).
 *   EMPTY_QM_VALUES: drone_resupply_day: 0.
 *   applyQMModifications: QM-012 injects a DRONE delivery event into
 *     scenario.logistics.medical_supplies.resupply_events[].
 *     Event shape: { day, delivery_method:'DRONE', drone_type:'CARGO_DRONE',
 *       ad_threat_override:null, push_package:'', supplies:{}, source:'QM-012_COA_VARIANT' }.
 *     Duplicate guard: does not inject if a DRONE event already exists on that day.
 *     Calculator supplyChainModule v5.2.0 DRONE branch handles payload calculation.
 *   Source: Ukraine 2022-2024 / US Army Project Convergence 22. No NATO STANAG.
 *
 * Version: 3.3.0 (March 15, 2026) - ENH-007: Ambulance Exchange Point (AXP) QM-011:
 *   QM_CATALOG: added QM-011 'AXP Distance from FEBA' (0–25 km, step 2).
 *   EMPTY_QM_VALUES: axp_distance_km: 0 (0 = no AXP, baseline unchanged).
 *   applyQMModifications: QM-011 handler writes axp_distance_km to
 *     mtf_distances.role2_distance_km (and flat role2_distance_km) — the existing
 *     medevacModule groundDistKm calculation uses the shorter leg automatically.
 *     Clamp: AXP must be between R1 and R2B (exclusive).
 *     axp_distance_km recorded separately in mtf_distances for reporting.
 *   Source: US Army ATP 4-02.2 Medical Evacuation / NATO LSCO best practice.
 *   Tab 6 COA Analysis only — no Tab 3 baseline configuration, no calculator change.
 *   Golden Hour % and comparison metrics reflect the shorter ground leg automatically.
 *
 * Version: 3.2.0 (March 15, 2026) - ENH-006: Dynamic MTF Coverage Gap panel:
 *   renderDynamicMTFPanel(): reads baseline.snapshot.medical_configuration
 *   .per_facility_evacuation_assets for facilities with activate_day != null.
 *   Renders a timeline table: facility name, activate day, deactivate day, setup hours,
 *   capacity ramp, and a coverage gap alert for each day before activation.
 *   Positioned after renderAnnexQ() as Section 3f.
 *   Display-only — reads already-saved Tab3 v4.23.0 data.
 *
 * Version: 3.1.1 (March 15, 2026) - ESLint: wire corps/lg/med in generateAnnexQText():
 *   corps (res.by_corps): Q-3a now uses corps.total_all_sources/kia/wia/dnbi as
 *     primary source, falling back to bs equivalents. More direct path to calculator output.
 *   med (res.medical_system_analysis): Q-3a golden hour now reads
 *     med.overall_golden_hour_percent first; med.overall_status added as new line.
 *   lg (res.logistics): Q-4a blood line falls back to lg.blood_units_required;
 *     new "Logistics supply status" line reads lg.supply_status / lg.overall_status.
 *
 * Version: 3.1.0 (March 15, 2026) - RESEARCH-REPORTS-002 formal implementation:
 *   renderAnnexQ() + generateAnnexQText(): formal OPORD Annex Q (Medical Support
 *   Plan) per AJP-4.10C. Five sections: Situation / Mission / Execution /
 *   Sustainment / Command & Signal. Reads baseline.results + baseline.snapshot.
 *   Blob .txt download alongside MEDSITREP. GavelIcon import added.
 *   Wired in return block after MEDSITREP (Section 3e).
 *
 * Version: 3.0.0
 * Date: March 15, 2026
 *
 * CHANGELOG:
 * v3.0.0 (March 15, 2026) - Phase 4: Analysis Tools + Export
 *   - renderSensitivityAnalysis(): variant x KPI delta table; identifies
 *     highest-impact modification per outcome metric. Pure computation, no deps.
 *   - renderMEDSITREP(): auto-generated NATO MEDSITREP text block + Blob
 *     download button (.txt). Covers: situation summary, baseline results,
 *     variant comparison, recommendations. ENH-013 events included.
 *   - ENH-013 timeline visualization: horizontal day-axis strip added above
 *     events table; events rendered as colored markers by type.
 *   - renderNATOBanner() (ENH-022a): NATO operational sequence accordion;
 *     maps Tab 2 phase posture onto standard NATO ops phases. Display-only.
 *   - Phase chip updated: "Phase 4 of 4 - Complete"
 *
 * v2.3.0 (March 14, 2026) - Phase 3: Side-by-side comparison view
 *   - COMPARISON_METRICS catalog: 11 KPIs across Casualties / Medical / Logistics
 *   - extractMetric(): multi-path defensive extractor for calculator results
 *   - renderComparisonView(): baseline vs N calculated variants table
 *     Green = improvement, Red = degradation. Absolute + % delta per cell.
 *     Category header rows. Modification summary per variant column.
 *     Winner summary alert: which variant wins the most metrics.
 * v2.2.0 (March 14, 2026) - Phase 2: Quick Modification UI
 *   - QM_CATALOG: 7 modification types (QM-002 through QM-009, excl. QM-010)
 *   - applyQMModifications(): deep-copies baseline snapshot, applies deltas
 *   - Variant state: variants[], activeVariantId, qmValues, isCalculating
 *   - renderVariantManager(): create/select/delete/duplicate variants,
 *     Slider controls per QM, Apply + Recalculate buttons, result summary
 *   - handleRecalculate(): calls calculateCasualties() on variant snapshot
 *   - localStorage persistence: psp_exercise_variant_index + per-variant keys
 *   - flushState + mount restore extended for variant persistence
 * v2.1.0 (March 14, 2026) - RESEARCH-REPORTS-002: Panel migration complete.
 *   Section 4: 5 panels MOVED from Tab 5 (Evacuation Analysis):
 *     renderTab6StratevacCapacity, renderTab6StratevacScheduleAnalysis,
 *     renderTab6EvacuationRoutes, renderTab6TacticalEvacCapacity,
 *     renderTab6DailyProjectionChart
 *   Section 5: 3 panels DUPLICATED from Tab 5 (Supporting Data):
 *     renderTab6InjuryMechanismAnalysis, renderTab6ResupplyAnalysis,
 *     renderTab6G1Summary
 *   All 8 panels read from baseline.results (not live scenario state).
 *   Guard: renderNoResultsPlaceholder shown when no calculated results loaded.
 *   Imports added: stratevacScheduling, evacuationCapacityCalculator, classVIII,
 *     theatreHoldingPolicy, nationalLogistics, resupplyThresholds, hnsConfig,
 *     icons, and associated MUI + icon components.
 * v2.0.0 (March 14, 2026)  -  Phase 1: Core Infrastructure
 *   - ScenarioLoader: two load paths
 *       (a) Load from Tab 5 Baseline via calculationService.getBaseline()  -  includes
 *           pre-calculated results, recommended path
 *       (b) Load Current Configuration  -  deep-copy snapshot of scenario prop, no results
 *   - Baseline panel: immutable locked card with scenario parameters + calculator
 *     summary metrics (defensive ?? ' - ' on all result fields)
 *   - Dirty-flag guard: warns user when Tabs 1-4 were modified after last Tab 5 calculate
 *   - Variant scaffold: 0/MAX_VARIANTS counter, Phase 2 placeholder, disabled Add button
 *   - ENH-013 Cross-Division Approval Event Tracking:
 *       APPROVAL_REQUESTED / APPROVAL_RECEIVED / APPROVAL_DENIED event log
 *       Day-stamped, sorted ascending, requesting/providing unit + notes fields
 *       Educational callout: anticipatory vs reactive planning impact on patient outcomes
 *       Summary chips: requested / received / denied counts
 *   - flushState (ref method): saves baseline + approval events to localStorage
 *       psp_exercise_baseline, psp_exercise_events
 *   - Restore from localStorage on mount (session continuity)
 *   - localStorage schema per FUTURE_WORK.md Section 3 Key Constraints:
 *       psp_exercise_baseline, psp_exercise_events,
 *       psp_exercise_variant_{id}, psp_exercise_comparison
 *
 * v1.0.0 (February 10, 2026)  -  Initial placeholder, Under Construction
 */

import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Slider,
  CircularProgress,
  Tooltip,
  Typography,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Switch,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import LinearProgress         from '@mui/material/LinearProgress';
import ButtonGroup            from '@mui/material/ButtonGroup';
import AddCircleOutlineIcon  from '@mui/icons-material/AddCircleOutline';
import ArrowBackIcon         from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon      from '@mui/icons-material/ArrowForward';
import ConstructionIcon      from '@mui/icons-material/Construction';
import CancelIcon            from '@mui/icons-material/Cancel';
import CheckCircleIcon       from '@mui/icons-material/CheckCircle';
import CompareArrowsIcon     from '@mui/icons-material/CompareArrows';
import DeleteOutlineIcon     from '@mui/icons-material/DeleteOutline';
import DownloadDoneIcon      from '@mui/icons-material/DownloadDone';
import ExpandMoreIcon        from '@mui/icons-material/ExpandMore';
import FolderOpenIcon        from '@mui/icons-material/FolderOpen';
import HelpOutlineIcon       from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon      from '@mui/icons-material/InfoOutlined';
import LockIcon              from '@mui/icons-material/Lock';
import SchoolIcon            from '@mui/icons-material/School';
import WarningAmberIcon      from '@mui/icons-material/WarningAmber';
import LocalHospitalIcon     from '@mui/icons-material/LocalHospital';
import TuneIcon              from '@mui/icons-material/Tune';
import PlayArrowIcon         from '@mui/icons-material/PlayArrow';
import ContentCopyIcon       from '@mui/icons-material/ContentCopy';
import GetAppIcon            from '@mui/icons-material/GetApp';
import BarChartIcon          from '@mui/icons-material/BarChart';
import ArticleIcon           from '@mui/icons-material/Article';
import PublicIcon            from '@mui/icons-material/Public';
import GavelIcon             from '@mui/icons-material/Gavel';

import { getBaseline, isDirty, hasBaseline } from '../services/calculationService';
import { calculateCasualties } from '../calculationEngine/casualtyCalculator.js';
import AirplanemodeActiveIcon    from '@mui/icons-material/AirplanemodeActive';
import AssessmentIcon           from '@mui/icons-material/Assessment';
import DirectionsCarIcon        from '@mui/icons-material/DirectionsCar';
import GroupIcon                from '@mui/icons-material/Group';
import LocalShippingIcon        from '@mui/icons-material/LocalShipping';
import TimelineIcon             from '@mui/icons-material/Timeline';
import AltRouteIcon             from '@mui/icons-material/AltRoute';
import AccountTreeIcon          from '@mui/icons-material/AccountTree';
import CodeIcon                 from '@mui/icons-material/Code';
import DescriptionIcon          from '@mui/icons-material/Description';
import DownloadIcon             from '@mui/icons-material/Download';
import FlashOnIcon              from '@mui/icons-material/FlashOn';
import InfoIcon                 from '@mui/icons-material/Info';
import MilitaryTechIcon         from '@mui/icons-material/MilitaryTech';
import PictureAsPdfIcon         from '@mui/icons-material/PictureAsPdf';
import ReportProblemIcon        from '@mui/icons-material/ReportProblem';
import SummarizeIcon            from '@mui/icons-material/Summarize';
import TableChartIcon           from '@mui/icons-material/TableChart';
import LayersIcon              from '@mui/icons-material/Layers';

import { STRATEVAC_AIRCRAFT }   from '../constants/stratevacScheduling.js';
import {
  OPERATIONAL_STATES,
  getBattleCasualtyMultiplier
} from '../constants/operationalStates.js';
import { PUSH_PACKAGES } from '../constants/classVIII.js';
import { THREAT_LEVELS } from '../constants/operationsConfig.js';
import {
  getNationalLogistics,
  getNationalDifferences,
  AVAILABLE_NATIONAL_STANDARDS,
} from '../constants/nationalLogistics';
import {
  MROE_STATES,
  PATIENT_CATEGORIES,
  MROE_DEFAULT_MATRICES,
} from '../constants/mroeConfig.js';
import {
  formatMEDSITREP,
  formatMEDASSESSREP,
  formatCasualtyEstimate,
  formatEPINATO2,
  formatNineLine,
  formatMETHANE,
  formatOOSR,
  formatBlueLightProtocol,
  formatMEDEVACSummary,
  formatExecutiveBrief,
  formatNATOPlanningFormat,
  formatComponentsMatrix
} from '../services/ReportFormattersService.js';
import {
  exportToHTML,
  exportToDOCX,
  exportToPDF,
  exportToPDFMerged,
  exportToXLSX,
  exportToJSON,
  downloadBlob,
  generateFilename
} from '../utils/exportUtils.js';
import { lsWrite, lsRead, lsRemove } from '../utils/lsStore.js'; // BUG-FF-012
import { useTranslation } from 'react-i18next';
// V5-001: jsPDF + autoTable imported directly for Annex Q PDF generation
import { chainLegKm, setLegOverride } from '../constants/chainDistances';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// --- localStorage schema keys (FUTURE_WORK.md Section 3  -  Key Constraints) ----------
const EXERCISE_STORAGE_KEYS = {
  BASELINE:          'psp_exercise_baseline',
  EVENTS:            'psp_exercise_events',
  VARIANT:           'psp_exercise_variant_',    // suffix with variant id  -  Phase 2+
  COMPARISON:        'psp_exercise_comparison', // Phase 3+
  COA_1:             'psp_cre_coa_1',           // V4-TAB6-001: CoA A slot (saved from Tab 5)
  COA_2:             'psp_cre_coa_2',           // V4-TAB6-001: CoA B slot (saved from Tab 5)
  BASELINE_APPROVED:   'psp_exercise_baseline_approved', // BUG-84-017: baseline approval flag
  APPROVED_COA_SLOT:   'psp_approved_coa_slot',          // null | 1 | 2
  APPROVED_VARIANT_ID: 'psp_approved_variant_id',        // null = base CoA
};

// Separate localStorage/sessionStorage keys for approved plan — never touched by calculationService.saveBaseline()
const APPROVED_PLAN_KEY         = 'psp_approved_plan';
const APPROVED_PLAN_RESULTS_KEY = 'psp_approved_plan_results';

// --- Requests to Higher: gap detection thresholds (Step 5) -------------------
const RTH_THRESHOLDS = {
  BLOOD_DOS_MIN_DAYS:        3,    // NATO AJMedP-2 minimum blood DOS
  BLOOD_STOCKOUT_EARLY_DAY:  3,    // stockout on or before Day 3 = critical
  MEDEVAC_GH_MIN_PCT:        80,   // golden hour coverage minimum %
  R2E_DEFAULT_DISTANCE_KM:   40,   // doctrinal default used when not configured
  FRT_MIN_COUNT:             1,    // at least 1 FRT expected at brigade-level ops
  HNS_RELIABILITY_MIN:       0.85, // below 85% = unconfirmed
};

// Maximum variants  -  mirrors calculationService MAX_VARIANTS
// IMP-008: Raised from 5 to 8 (exercise use-case; comparison table already has overflowX:auto)
const MAX_VARIANTS = 8;

// --- ENH-013 approval event type catalog -------------------------------------
const APPROVAL_EVENT_TYPES = {
  APPROVAL_REQUESTED: { label: 'Approval Requested', color: 'info'    },
  APPROVAL_RECEIVED:  { label: 'Approval Received',  color: 'success' },
  APPROVAL_DENIED:    { label: 'Approval Denied',    color: 'error'   }
};

// --- Load status values -------------------------------------------------------
const LOAD_STATUS = {
  IDLE:          'idle',
  LOADED_TAB5:   'loaded_tab5',
  LOADED_SNAP:   'loaded_snapshot',
  NO_BASELINE:   'no_baseline',
  DIRTY_WARNING: 'dirty_warning',
  STALE:         'stale'           // BUG-206: Tab5 recalculated after baseline was loaded
};

// --- Empty event form template ------------------------------------------------
const EMPTY_EVENT_FORM = {
  day:             '',
  event_type:      'APPROVAL_REQUESTED',
  requesting_unit: '',
  providing_unit:  '',
  notes:           ''
};


// --- Phase 2: Quick Modification catalog ------------------------------------

const QM_CATALOG = [
  { id: 'QM-002', label: 'Role 2B Distance',    unit: 'km',  field: 'r2b_distance_km',  min: -30, max:  30, step: 5,  defaultVal: 0,
    description: 'Move Role 2B forward (negative) or rearward (positive) from FEBA. Affects Golden Hour compliance and evacuation time.' },
  { id: 'QM-003', label: 'Blood Stock',          unit: '%',   field: 'blood_pct',         min: -50, max: 100, step: 10, defaultVal: 0,
    // BUG-6E-001 FIX: description said "+50 doubles" — incorrect. +100 doubles; +50 adds 50 pp to baseline.
    description: 'Adjust starting blood stock by ±percentage points (range: −50 to +100 pp). +100 doubles initial PRBC/FFP/PLT. Affects stockout day and DOW rates.' },
  { id: 'QM-004', label: 'MEDEVAC Helicopters',  unit: 'N',   field: 'medevac_count',     min:  -4, max:   8, step: 1,  defaultVal: 0,
    description: 'Add or remove MEDEVAC helicopters from the air pool. Directly impacts Golden Hour compliance and DOW rates.' },
  { id: 'QM-006', label: 'Operation Duration',   unit: 'days',field: 'duration_days',     min:  -5, max:  10, step: 1,  defaultVal: 0,
    description: 'Extend or shorten operation by days. Affects cumulative casualties, logistics stockout timing, and blood consumption.' },
  { id: 'QM-007', label: 'FRT Teams',            unit: 'N',   field: 'frt_count',         min:  -4, max:   6, step: 1,  defaultVal: 0,
    description: 'Add or remove Forward Resuscitation Teams. FRTs are Role 1+ (no surgery) — damage control resuscitation only: hemorrhage control, airway, whole blood. Binary DOW effect: >=1 FRT reduces DOW rate by 7%.' },
  { id: 'QM-008', label: 'Role 3 Distance',      unit: 'km',  field: 'r3_distance_km',    min: -50, max:  50, step: 10, defaultVal: 0,
    description: 'Move Role 3 (Corps hospital) forward or rearward. Affects STRATEVAC flight time and R2B backpressure.' },
  { id: 'QM-009', label: 'FST Teams',            unit: 'N',   field: 'fst_count',         min:  -4, max:   8, step: 1,  defaultVal: 0,
    description: 'Add or remove Forward Surgical Teams. FSTs provide full damage control surgery at Role 2B/2E.' },
  // ENH-007: AXP — Ambulance Exchange Point (ATP 4-02.2)
  { id: 'QM-011', label: 'AXP Distance from FEBA', unit: 'km', field: 'axp_distance_km', min: 0, max: 25, step: 2, defaultVal: 0,
    description: 'Add an Ambulance Exchange Point at this distance from FEBA (ATP 4-02.2). ' +
      'Forward unit ambulances drive only to AXP, not all the way to R2B. ' +
      'Reduces effective ground evacuation distance — improves Golden Hour compliance and ambulance turnaround. ' +
      '0 = no AXP (current baseline). Source: US Army ATP 4-02.2 / NATO LSCO best practice.' },
  // ENH-008: Drone UAS medical supply delivery
  { id: 'QM-012', label: 'Drone Resupply Day',     unit: 'day', field: 'drone_resupply_day', min: 0, max: 30, step: 1, defaultVal: 0,
    description: 'Schedule a cargo drone blood + tourniquet resupply on this operation day. ' +
      '0 = no drone event added. Effect: ~12 PRBC units + 80 tourniquet packs injected on that day ' +
      '(subject to AD threat availability modifier). ' +
      'Source: Ukraine 2022-2024 / US Army Project Convergence 22. No NATO STANAG — emerging practice.' },
];

// Initial QM values (all zero = no modification from baseline)
const EMPTY_QM_VALUES = {
  blood_pct:          0,
  r2b_distance_km:    0,
  r3_distance_km:     0,
  medevac_count:      0,
  frt_count:          0,
  fst_count:          0,
  duration_days:      0,
  axp_distance_km:    0,   // ENH-007: 0 = no AXP
  drone_resupply_day: 0,   // ENH-008: 0 = no drone event
};

// ============================================================================
// ENH-030: REPORT CONFIGURATION — 12 NATO Reports (moved from Tab 5)
// ============================================================================

const REPORT_TYPES = {
  MEDSITREP: {
    id: 'medsitrep',
    name: 'MEDSITREP',
    fullName: 'Medical Situation Report',
    stanag: 'STANAG 2348',
    description: 'Daily medical situation report',
    audience: 'Commander / Medical Staff',
    icon: <LocalHospitalIcon />,
    formatter: formatMEDSITREP,
    priority: 1
  },
  MEDASSESSREP: {
    id: 'medassessrep',
    name: 'MEDASSESSREP',
    fullName: 'Medical Assessment Report',
    stanag: 'STANAG 2546',
    description: 'Weekly medical planning assessment',
    audience: 'Higher HQ / Medical Staff',
    icon: <AssessmentIcon />,
    formatter: formatMEDASSESSREP,
    priority: 2
  },
  CASUALTY_ESTIMATE: {
    id: 'casualty_estimate',
    name: 'Casualty Estimate',
    fullName: 'Casualty Rate Estimate',
    stanag: 'AJMedP-5',
    description: 'Detailed casualty projections',
    audience: 'Medical Planners',
    icon: <AccountTreeIcon />,
    formatter: formatCasualtyEstimate,
    priority: 1
  },
  EPINATO2: {
    id: 'epinato2',
    name: 'EPINATO-2',
    fullName: 'Epidemiological Report',
    stanag: 'STANAG 2535',  // QM-6I-001: AMedP-4.1 Deployment Health Surveillance (EpiNATO-2)
    description: 'Disease and injury surveillance',
    audience: 'Public Health / Medical Staff',
    icon: <InfoIcon />,
    formatter: formatEPINATO2,
    priority: 3
  },
  NINE_LINE: {
    id: 'nine_line',
    name: '9-LINE MEDEVAC',
    fullName: 'MEDEVAC Request Format',
    stanag: 'STANAG 2087',  // QM-6I-001: AAMedP-1.5 Forward Aeromedical Evacuation (9-line format)
    description: 'Standard evacuation request',
    audience: 'MEDEVAC Coordinators',
    icon: <AirplanemodeActiveIcon />,
    formatter: formatNineLine,
    priority: 2,
    movedToTab7: true   // TAB7-REPORTS-MOVE: per-patient point-of-injury, runtime only
  },
  METHANE: {
    id: 'methane',
    name: 'METHANE',
    fullName: 'Major Incident Report',
    stanag: 'NATO MASCAL',
    description: 'Mass casualty incident report',
    audience: 'Incident Commanders',
    icon: <ReportProblemIcon />,
    formatter: formatMETHANE,
    priority: 2,
    movedToTab7: true   // TAB7-REPORTS-MOVE: real-time MASCAL, not planning
  },
  OOSR: {
    id: 'oosr',
    name: 'Out of Standards',
    fullName: 'Out of Standards Report',
    stanag: 'Internal',
    description: 'Exception tracking report',
    audience: 'Quality Assurance',
    icon: <WarningAmberIcon />,
    formatter: formatOOSR,
    priority: 3,
    movedToTab7: true   // TAB7-REPORTS-MOVE: MTF out-of-service filed at runtime
  },
  BLUE_LIGHT: {
    id: 'blue_light',
    name: 'MASCAL Escalation Protocol',
    fullName: 'MASCAL Escalation Protocol (Surge Capacity)',
    stanag: 'AMedP-1.10 (STANAG 2879)',
    description: 'Surge capacity recommendations (based on UK national doctrine)',
    audience: 'Medical Commanders',
    icon: <FlashOnIcon />,
    formatter: formatBlueLightProtocol,
    priority: 2,
    movedToTab7: true   // TAB7-REPORTS-MOVE: real-time escalation, not planning
  },
  MEDEVAC_SUMMARY: {
    id: 'medevac_summary',
    name: 'MEDEVAC Summary',
    fullName: 'Evacuation Asset Summary',
    stanag: 'AJMedP-2',
    description: 'Evacuation capacity analysis',
    audience: 'MEDEVAC Coordinators',
    icon: <AirplanemodeActiveIcon />,
    formatter: formatMEDEVACSummary,
    priority: 2
  },
  EXECUTIVE_BRIEF: {
    id: 'executive_brief',
    name: 'Executive Brief',
    fullName: 'One-Page BLUF',
    stanag: 'Internal',
    description: 'Leadership summary',
    audience: 'Commanders',
    icon: <SummarizeIcon />,
    formatter: formatExecutiveBrief,
    priority: 1
  },
  NATO_PLANNING: {
    id: 'nato_planning',
    name: 'NATO Planning Format',
    fullName: 'Annex Q Medical Estimate',
    stanag: 'AJMedP-5',
    description: 'NATO standard planning format',
    audience: 'NATO Staff',
    icon: <MilitaryTechIcon />,
    formatter: formatNATOPlanningFormat,
    priority: 1
  },
  COMPONENTS_MATRIX: {
    id: 'components_matrix',
    name: 'Components Matrix',
    fullName: 'Traffic Light Status',
    stanag: 'Internal',
    description: 'Status dashboard view',
    audience: 'All Staff',
    icon: <TableChartIcon />,
    formatter: formatComponentsMatrix,
    priority: 2
  }
};

const EXPORT_FORMATS = [
  { id: 'html',  name: 'HTML',  icon: <ArticleIcon /> },
  { id: 'pdf',   name: 'PDF',   icon: <PictureAsPdfIcon /> },
  { id: 'docx',  name: 'Word',  icon: <DescriptionIcon /> },
  { id: 'xlsx',  name: 'Excel', icon: <TableChartIcon /> },
  { id: 'json',  name: 'JSON',  icon: <CodeIcon /> }
];


// --- Phase 3: Comparison metrics catalog ------------------------------------

// Extract a value from calculator results using a list of fallback paths
function extractMetric(results, paths) {
  if (!results) return null;
  for (const path of paths) {
    const keys = path.split('.');
    let val = results;
    for (const k of keys) {
      if (val == null) break;
      val = val[k];
    }
    if (val != null && !Number.isNaN(val)) return val;
  }
  return null;
}

// Metric catalog -- each entry defines how to extract and display one KPI
const COMPARISON_METRICS = [
  // Casualties
  { key: 'total_cas',    label: 'Total Casualties',     category: 'Casualties',
    paths: ['by_corps.total_all_sources','by_corps.total_casualties','summary.total_casualties'],
    higherIsBetter: false, fmt: v => v.toLocaleString() },
  { key: 'kia',         label: 'KIA',                  category: 'Casualties',
    paths: ['by_corps.total_kia','summary.kia'],
    higherIsBetter: false, fmt: v => v.toLocaleString() },
  { key: 'wia',         label: 'WIA Survivors',          category: 'Casualties',
    paths: ['by_corps.total_wia','summary.wia'],
    // QM-WIA-DOW-DISPLAY: total_wia = originalWIA − finalDOW (survivors only).
    // When DOW decreases (better care), WIA Survivors increases by same amount = GOOD.
    // higherIsBetter:true — more survivors is a better outcome, not a degradation.
    higherIsBetter: true, fmt: v => v.toLocaleString() },
  { key: 'dow',         label: 'Died of Wounds',       category: 'Casualties',
    paths: ['by_corps.total_dow','summary.dow'],
    higherIsBetter: false, fmt: v => v.toLocaleString() },
  { key: 'dnbi',        label: 'DNBI',                 category: 'Casualties',
    paths: ['by_corps.total_dnbi','summary.dnbi'],
    higherIsBetter: false, fmt: v => v.toLocaleString() },
  { key: 'rtd',         label: 'RTD',                  category: 'Casualties',
    paths: ['by_corps.total_rtd','summary.rtd'],
    higherIsBetter: true,  fmt: v => v.toLocaleString() },
  // Medical
  { key: 'golden_hour', label: 'Golden Hour %',        category: 'Medical',
    paths: ['medical_system_analysis.overall_golden_hour_percent',
            'summary.golden_hour_compliance_pct','summary.golden_hour_compliance'],
    higherIsBetter: true,  fmt: v => formatPercent(v) },
  { key: 'r2b_mascal',  label: 'R2B MASCAL Days',      category: 'Medical',
    paths: ['mascal_analysis.r2b_first_critical','summary.r2b_mascal_days'],
    higherIsBetter: false, fmt: v => 'D+' + v },
  { key: 'peak_r2b',    label: 'Peak R2B Occ %',       category: 'Medical',
    paths: ['mascal_analysis.peak_r2b_pct','summary.peak_r2b_occupancy_pct'],
    higherIsBetter: false, fmt: v => formatPercent(v) },
  // Logistics
  { key: 'blood_stockout', label: 'Blood Stockout',    category: 'Logistics',
    paths: ['logistics.blood_stockout_day','summary.blood_stockout_day'],
    higherIsBetter: true,  fmt: v => v ? 'Day ' + v : 'OK',
    nullLabel: 'OK (no stockout)' },
  { key: 'blood_units', label: 'Blood Units Req.',     category: 'Logistics',
    paths: ['medical_requirements.blood.total_units','summary.blood_units_required'],
    higherIsBetter: false, fmt: v => v.toLocaleString() },
];

const CATEGORY_COLORS = {
  Casualties: 'error',
  Medical:    'primary',
  Logistics:  'warning',
  Evacuation: 'success',
};

// Empty variant template
function makeEmptyVariant(name) {
  return {
    id:            'variant_' + Date.now(),
    name,
    qm_values:     { ...EMPTY_QM_VALUES },
    modifications: [],
    snapshot:      null,
    results:       null,
    approved:      false,
    created_at:    new Date().toISOString(),
    calculated_at: null
  };
}

// Apply QM modifications to a deep-copied baseline snapshot.
// Returns the mutated copy -- baseline.snapshot is NEVER touched.
function applyQMModifications(snapshot, qmValues) {
  const s = JSON.parse(JSON.stringify(snapshot)); // deep copy

  // QM-003: Blood stock +/-%
  // BUG-169 FIX: Calculator reads logistics.medical_supplies.initial_stock (Tab4 owner).
  // Old paths (logistics.blood_config.starting_stock / logistics.starting_stock.blood) do not exist.
  if (qmValues.blood_pct !== 0) {
    const factor = 1 + qmValues.blood_pct / 100;
    const is = s?.logistics?.medical_supplies?.initial_stock;
    if (is) {
      if (is.prbc_units      != null) is.prbc_units      = Math.max(0, Math.round(is.prbc_units      * factor));
      if (is.ffp_units       != null) is.ffp_units       = Math.max(0, Math.round(is.ffp_units       * factor));
      if (is.platelets_units != null) is.platelets_units = Math.max(0, Math.round(is.platelets_units * factor));
      if (is.cryo_units      != null) is.cryo_units      = Math.max(0, Math.round(is.cryo_units      * factor));
    }
  }

  // QM-002: Role 2B distance
  // BUG-169 FIX: Calculator reads medical_configuration.mtf_distances.role2_distance_km.
  // Old paths (role2_distance_km flat + mtf_configuration.role2_distance_km) do not exist.
  if (qmValues.r2b_distance_km !== 0) {
    // 2026-10-25: the leg is owned by chainDistances (override > placed facilities > typed value); the COA change is an override
    const mc = s?.medical_configuration;
    const cur = mc ? chainLegKm(mc, 'r1_to_r2b') : null;
    if (cur != null) setLegOverride(mc, 'r1_to_r2b', Math.max(5, cur + qmValues.r2b_distance_km));
  }

  // QM-008: Role 3 distance
  // BUG-169 FIX: Calculator reads medical_configuration.mtf_distances.role3_distance_km.
  // Old paths (role3_distance_km flat + mtf_configuration.role3_distance_km) do not exist.
  if (qmValues.r3_distance_km !== 0) {
    const mc = s?.medical_configuration;
    const cur = mc ? chainLegKm(mc, 'r2e_to_r3') : null;
    if (cur != null) setLegOverride(mc, 'r2e_to_r3', Math.max(20, cur + qmValues.r3_distance_km));
  }

  // QM-004: MEDEVAC helicopter count
  // BUG-169 FIX: Calculator reads tactical_evac_config.air_pool.nominal_daily_sorties and
  // derives helicopter count as Math.round(nominal_daily_sorties / 6).
  // Old paths (air_pool.count — field does not exist; medical_configuration.medevac — path does not exist)
  // produced zero effect. Fix: delta N helicopters = delta N*6 sorties on nominal_daily_sorties.
  if (qmValues.medevac_count !== 0) {
    const SORTIES_PER_HELI = 6; // doctrine: 6 sorties/aircraft/day in PERMISSIVE (casualtyCalculator.js)
    const airPool = s?.medical_configuration?.tactical_evac_config?.air_pool;
    if (airPool) {
      const current = airPool.nominal_daily_sorties ?? 0;
      airPool.nominal_daily_sorties = Math.max(0, current + qmValues.medevac_count * SORTIES_PER_HELI);
    }
  }

  // QM-007: FRT count
  // BUG-84-019: old guards (if sc?.FRT / if sc2?.frt_count != null) silently discard the
  // delta when FRT was never configured in Tab 3.  Fix: always initialise the path the
  // calculator reads (surgical_config.teams.FRT.count) before applying the delta.
  if (qmValues.frt_count !== 0) {
    if (s?.medical_configuration) {
      if (!s.medical_configuration.surgical_config)
        s.medical_configuration.surgical_config = {};
      const surgCfg = s.medical_configuration.surgical_config;
      if (!surgCfg.teams) surgCfg.teams = {};
      if (!surgCfg.teams.FRT) surgCfg.teams.FRT = { count: 0 };
      surgCfg.teams.FRT.count = Math.max(0, (surgCfg.teams.FRT.count ?? 0) + qmValues.frt_count);
      // keep flat key in sync if it already exists
      if (surgCfg.frt_count != null)
        surgCfg.frt_count = Math.max(0, surgCfg.frt_count + qmValues.frt_count);
    }
  }

  // QM-009: FST count
  // BUG-84-019: same guard problem as QM-007; initialise path if missing.
  if (qmValues.fst_count !== 0) {
    if (s?.medical_configuration) {
      if (!s.medical_configuration.surgical_config)
        s.medical_configuration.surgical_config = {};
      const surgCfg = s.medical_configuration.surgical_config;
      if (!surgCfg.teams) surgCfg.teams = {};
      if (!surgCfg.teams.FST) surgCfg.teams.FST = { count: 0 };
      surgCfg.teams.FST.count = Math.max(0, (surgCfg.teams.FST.count ?? 0) + qmValues.fst_count);
      // keep flat key in sync if it already exists
      if (surgCfg.fst_count != null)
        surgCfg.fst_count = Math.max(0, surgCfg.fst_count + qmValues.fst_count);
    }
  }

  // QM-006: Operation duration
  if (qmValues.duration_days !== 0) {
    const op = s?.operation_parameters;
    if (op?.duration_days != null)
      op.duration_days = Math.max(1, op.duration_days + qmValues.duration_days);
  }

  // QM-011: ENH-007 — Ambulance Exchange Point (ATP 4-02.2 / LSCO best practice)
  // AXP reduces the effective ground evacuation distance to AXP distance.
  // Logic: forward unit ambulances drive from Point of Injury to AXP only (not to R2B).
  // At AXP, patients transfer to BSMC ambulances that drive AXP → R2B.
  // Planning effect: the critical forward leg = axp_distance_km (shorter than r2b_distance_km).
  // Implementation: write axp_distance_km as the new role2_distance_km so the existing
  // medevacModule groundDistKm calculation automatically reflects the shorter leg.
  // Clamp: AXP must sit between R1 and R2B (must be forward of R2B and rearward of R1).
  if (qmValues.axp_distance_km > 0) {
    const mc = s?.medical_configuration;
    if (mc?.mtf_distances) {
      const r1Km = mc.mtf_distances.role1_distance_km ?? 8;
      const r2Km = chainLegKm(mc, 'r1_to_r2b') ?? mc.mtf_distances.role2_distance_km ?? 20;
      // AXP must be between R1 and R2B (exclusive)
      const axpKm = Math.max(r1Km + 1, Math.min(r2Km - 1, qmValues.axp_distance_km));
      mc.mtf_distances.axp_distance_km   = axpKm;   // record AXP position for reporting
      setLegOverride(mc, 'r1_to_r2b', axpKm);       // calculator uses this as effective ground leg
    }
    // Also patch the flat key path used by some calculator versions
    const mc2 = s?.medical_configuration;
    if (mc2?.role2_distance_km != null) {
      const r1Km = mc2.mtf_distances?.role1_distance_km ?? 8;
      mc2.role2_distance_km = Math.max(r1Km + 1,
        Math.min(mc2.role2_distance_km - 1, qmValues.axp_distance_km));
    }
  }

  // QM-012: ENH-008 — Drone UAS blood + tourniquet resupply event
  // Injects a DRONE delivery event into the resupply schedule on the specified day.
  // Calculator's buildDailySimulation DRONE branch handles the actual payload calculation.
  // AD threat override is read from the scenario's current threat level (not overridden here
  // — planner sets the threat in Tab 2 and the drone availability modifier applies automatically).
  if (qmValues.drone_resupply_day > 0) {
    const op = s?.operation_parameters;
    const maxDay = op?.duration_days ?? 30;
    const day = Math.max(1, Math.min(qmValues.drone_resupply_day, maxDay));
    const medSupplies = s?.logistics?.medical_supplies;
    if (medSupplies) {
      const events = Array.isArray(medSupplies.resupply_events)
        ? medSupplies.resupply_events
        : [];
      // Avoid duplicate drone events on the same day from repeated QM applies
      const alreadyHasDrone = events.some(e => e.delivery_method === 'DRONE' && e.day === day);
      if (!alreadyHasDrone) {
        medSupplies.resupply_events = [
          ...events,
          {
            day,
            delivery_method: 'DRONE',
            drone_type:       'CARGO_DRONE',
            ad_threat_override: null,   // use scenario's current AD threat
            push_package:     '',
            supplies:         {},
            source:           'QM-012_COA_VARIANT',
            mtf_target:       'ALL',
            destination_brigade: 'ALL'
          }
        ];
      }
    }
  }

  return s;
}

// Build human-readable modification summary from qmValues
function buildModificationSummary(qmValues) {
  const lines = [];
  QM_CATALOG.forEach(({ id, label, unit, field }) => {
    const v = qmValues[field];
    if (v && v !== 0) {
      const sign = v > 0 ? '+' : '';
      lines.push(`${label}: ${sign}${v} ${unit}`);
    }
  });
  return lines;
}

// --- ENH-031: Per-brigade operational state override functions ----------------

/**
 * getSnapshotBrigades — mirrors the calculator's brigade lookup (casualtyCalculator.js line ~6912).
 * PRIMARY:  force.brigades[]                   (corps/division echelon — flattened)
 * FALLBACK: force.forceBuilderResult.brigades[] (array — all echelons post-F2-ARCH-010)
 * LEGACY:   [force.forceBuilderResult.brigade]  (singular — pre-F2 persisted data only)
 *
 * Returns the brigade array the calculator will actually use.
 */
function getSnapshotBrigades(snapshot) {
  const force = snapshot?.force;
  const fbr = force?.forceBuilderResult;
  if (Array.isArray(force?.brigades) && force.brigades.length > 0) return force.brigades;
  if (Array.isArray(fbr?.brigades) && fbr.brigades.length > 0)     return fbr.brigades;
  if (fbr?.brigade)                                                  return [fbr.brigade]; // legacy pre-F2
  return [];
}

/**
 * applyBrigadeStateOverrides — bakes per-brigade state overrides into a snapshot copy.
 *
 * Mirrors getSnapshotBrigades() lookup so the same brigade objects the calculator reads
 * are the ones that get updated. Handles all three echelon data paths.
 *
 * For each brigade with an override:
 *   1. Sets brigade.operational_state to the new state.
 *   2. Updates brigade.modifiers.operational_state_modifier.
 *   3. Creates a brigade-specific single-phase covering the full operation so the
 *      calculator uses brigade.operational_state instead of the inherited corps/division
 *      phase state (calculator reads phase.operational_state || brigade.operational_state).
 *
 * Does NOT modify the original snapshot — works on the deep copy passed in.
 *
 * @param {Object} snapshot - Already deep-copied variant snapshot.
 * @param {Object} overrides - { [brigadeId]: 'COMBAT_ENGAGED' | 'SUPPORT_ACTIVE' | 'TRUE_RESERVE' }
 * @returns {Object} snapshot with brigade state overrides applied.
 */
function applyBrigadeStateOverrides(snapshot, overrides) {
  if (!overrides || Object.keys(overrides).length === 0) return snapshot;
  const brigades = getSnapshotBrigades(snapshot);
  if (brigades.length === 0) return snapshot;
  const duration = snapshot?.operation_parameters?.duration_days || 14;

  for (const brigade of brigades) {
    const newState = overrides[brigade.id];
    if (!newState) continue;

    // 1. Set brigade-level fallback state
    brigade.operational_state = newState;

    // 2. Update the operational_state modifier in brigade.modifiers (if present)
    if (brigade.modifiers) {
      const mult = getBattleCasualtyMultiplier(newState);
      brigade.modifiers.operational_state_modifier = (mult !== null && mult !== undefined) ? mult : 1.0;
    }

    // 3. Break phase inheritance — give brigade its own single phase so the
    //    calculator uses brigade.operational_state and not the corps/division phase state.
    //    Phase structure: { name, start_day, end_day, posture, operational_state }
    const refPosture = brigade.operational_posture || 'DELIBERATE_DEFENSE';
    brigade.phases = [{
      name: 'COA Variant Override',
      start_day: 1,
      end_day: duration,
      posture: refPosture,
      operational_state: newState
    }];
    brigade.uses_division_phases = false;
  }

  return snapshot;
}

/**
 * buildBrigadeModSummary — returns an array of modification summary strings for
 * changed brigades (format: "BrigadeName: OLD → NEW"), for display as Chip labels.
 */
function buildBrigadeModSummary(overrides, baseBrigades) {
  return Object.entries(overrides).map(([brigadeId, newState]) => {
    const brigade = (baseBrigades || []).find(b => b.id === brigadeId);
    const name = brigade?.name || brigadeId;
    const oldState = brigade?.operational_state || 'COMBAT_ENGAGED';
    const oldLabel = OPERATIONAL_STATES[oldState]?.shortLabel || oldState;
    const newLabel = OPERATIONAL_STATES[newState]?.shortLabel || newState;
    return `${name}: ${oldLabel}\u2192${newLabel}`;
  });
}

// --- Utilities ----------------------------------------------------------------


/** Format a number with commas. Returns '--' for null/undefined/NaN. */
function formatNumber(value, decimals = 0) {
  if (value === null || value === undefined || Number.isNaN(value)) return '--';
  return Number(value).toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** Format percent with null/NaN guard. Returns '--' for missing values. */
function formatPercent(value, decimals = 1) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
  return Number(value).toFixed(decimals) + '%';
}

/** Deep-copy via JSON round-trip. Safe for plain-object scenario structure. */
function deepCopy(obj) {
  return JSON.parse(JSON.stringify(obj));
}

/** Format a metric value for display. Returns ' - ' for null/undefined. */
function fmt(value, suffix) {
  if (value === null || value === undefined) return '\u2014';
  return suffix ? `${value}${suffix}` : String(value);
}

/**
 * Extract display-level summary metrics from a calculator results object.
 * All field paths are defensive  -  absent fields return null (displayed as ' - ').
 *
 * BUG-145 FIX (v3.5.1): Calculator returns NO 'summary' key.
 * All metrics live under: by_corps, mascal_analysis, medical_system_analysis,
 * medical_requirements.resupply_summary.dos_projection.
 * Old code did `results.summary ?? results` — fell through to root object which
 * also has no flat kia/wia/etc keys → every field returned null → all displayed as '—'.
 * Fix: read from the actual calculator output paths directly.
 */
function extractBaselineSummary(results) {
  if (!results) return null;
  // Has .version  =  genuine calculator result (not a partial/preview object)
  const isCalcResult = !!results.version;
  if (!isCalcResult) return null;

  const corps    = results.by_corps               ?? {};
  const ma       = results.mascal_analysis        ?? {};
  const maSys    = ma.system_summary              ?? {};
  const med      = results.medical_system_analysis ?? {};
  const resupply = results.medical_requirements?.resupply_summary ?? {};

  // Peak R2B % — computed from brigades timeline; falls back to system_summary field if present
  let peakR2bPct = ma.peak_r2b_pct ?? maSys.peak_r2b_pct ?? null;
  if (peakR2bPct == null && Array.isArray(ma.brigades) && ma.brigades.length > 0) {
    // Derive peak from per-brigade timelines when available
    let maxPct = null;
    for (const bde of ma.brigades) {
      const timeline = bde.r2b_timeline ?? bde.timeline ?? [];
      for (const day of timeline) {
        const pct = day.occupancy_pct ?? day.occupancy_percent ?? null;
        if (pct != null && (maxPct === null || pct > maxPct)) maxPct = pct;
      }
    }
    peakR2bPct = maxPct;
  }

  return {
    total_casualties:   corps.total_all_sources ?? corps.total_casualties ?? null,
    kia:                corps.total_kia         ?? null,
    wia:                corps.total_wia         ?? null,
    dnbi:               corps.total_dnbi        ?? null,
    rtd:                corps.total_rtd         ?? null,
    golden_hour_pct:    med.overall_golden_hour_percent
                          ?? med.golden_hour_compliance_pct
                          ?? null,
    r2b_mascal_days:    maSys.first_critical_day  ?? ma.r2b_first_critical_day ?? null,
    blood_stockout_day: resupply.dos_projection?.stockout_day ?? null,
    peak_r2b_pct:       peakR2bPct
  };
}

// --- Component ----------------------------------------------------------------

/**
 * Tab6COAAnalysis  -  Phase 1: Core Infrastructure
 *
 * Props:
 *   scenario {object|null}  -  active scenario from App.jsx (used for snapshot load path)
 *
 * Ref methods (called by App.jsx toolbar before navigation):
 *   flushState()  -  persists baseline + approval events to localStorage
 */

// ============================================================================
// STEP 5: REQUESTS TO HIGHER — PLANNING GAP DETECTOR
// Pure module-scope functions. No React state. Called from renderRequestsToHigher().
// ============================================================================

/**
 * Build a formatted military RFI text block.
 * @param {object} snapshot  - approved CoA snapshot
 * @param {string} to        - addressee
 * @param {string} subject   - RFI subject line
 * @param {string} request   - specific ask (paragraph 2)
 * @param {string} risk      - risk if not confirmed (paragraph 3)
 * @returns {string}
 */
function buildRFI(snapshot, to, subject, request, risk) {
  const ops      = snapshot?.operation_parameters ?? {};
  const force    = snapshot?.force                ?? {};
  const echelon  = force?.echelon ?? 'Division';
  const dtg      = new Date().toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  }).toUpperCase();
  const strength = force?.total_strength ?? force?.forceBuilderResult?.total_strength ?? 'N/A';
  const duration = ops?.operation_duration_days ?? ops?.duration_days ?? 'N/A';
  const exerName = ops?.name ?? ops?.scenario_name ?? 'EXERCISE';
  return [
    `TO:      ${to}`,
    `FROM:    ${echelon} Medical Officer`,
    `SUBJ:    RFI \u2013 ${subject}`,
    `DTG:     ${dtg}`,
    `EXER:    ${exerName}`,
    ``,
    `1. SITUATION`,
    `   Planning basis: ${echelon} Medical Support Plan. Force: ${strength}. Duration: ${duration} days.`,
    ``,
    `2. REQUEST`,
    `   ${request}`,
    ``,
    `3. RISK IF NOT CONFIRMED`,
    `   ${risk}`,
    ``,
    `4. REQUIRED BY`,
    `   [PLANNER TO COMPLETE]`,
    ``,
    `5. REMARKS`,
    `   [OPTIONAL]`,
  ].join('\n');
}

// FEA-S97-001: RFA (Request for Assets) template — used when gap requires concrete augmentation,
// not just information. Mirrors buildRFI format with RFA subject prefix.
function buildRFA(snapshot, to, subject, request, risk) {
  const ops      = snapshot?.operation_parameters ?? {};
  const force    = snapshot?.force                ?? {};
  const echelon  = force?.echelon ?? 'Division';
  const dtg      = new Date().toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  }).toUpperCase();
  const strength = force?.total_strength ?? force?.forceBuilderResult?.total_strength ?? 'N/A';
  const duration = ops?.operation_duration_days ?? ops?.duration_days ?? 'N/A';
  const exerName = ops?.name ?? ops?.scenario_name ?? 'EXERCISE';
  return [
    `TO:      ${to}`,
    `FROM:    ${echelon} Medical Officer`,
    `SUBJ:    RFA \u2013 ${subject}`,
    `DTG:     ${dtg}`,
    `EXER:    ${exerName}`,
    ``,
    `1. SITUATION`,
    `   Planning basis: ${echelon} Medical Support Plan. Force: ${strength}. Duration: ${duration} days.`,
    ``,
    `2. REQUEST FOR ASSETS`,
    `   ${request}`,
    ``,
    `3. RISK IF ASSETS NOT PROVIDED`,
    `   ${risk}`,
    ``,
    `4. REQUIRED BY`,
    `   [PLANNER TO COMPLETE]`,
    ``,
    `5. REMARKS`,
    `   [OPTIONAL]`,
  ].join('\n');
}

/**
 * Analyse an approved CoA snapshot + results and return planning gap objects.
 * All returned gaps are later escalated to CRITICAL by the caller (Improvement 6).
 *
 * @param {object|null} snapshot  - approved CoA scenario snapshot
 * @param {object|null} results   - approved CoA calculation results
 * @returns {Array<{id,priority,category,title,implication,to,rfi}>}
 */
function detectPlanningGaps(snapshot, results) {
  if (!snapshot) return [];
  const gaps    = [];
  const mc      = snapshot?.medical_configuration ?? {};
  const ops     = snapshot?.operation_parameters   ?? {};
  const hns     = ops?.hns_config                  ?? {};
  const resupply = results?.medical_requirements?.resupply_summary ?? {};
  const ghPct    = results?.medical_system_analysis?.overall_golden_hour_percent ?? null;
  const frt      = mc?.surgical_config?.teams?.FRT?.count ?? 0;

  // ── Medical Chain ──────────────────────────────────────────────────────────

  if (mc.r2e_accessible !== false &&
      (mc.r2e_distance_km == null ||
       mc.r2e_distance_km === RTH_THRESHOLDS.R2E_DEFAULT_DISTANCE_KM)) {
    gaps.push({
      id: 'r2e_defaults',
      type: 'RFI',
      priority: 'HIGH',
      category: 'Medical Chain',
      title: 'R2E evacuation using doctrinal default distance',
      implication: `Distance assumed ${RTH_THRESHOLDS.R2E_DEFAULT_DISTANCE_KM}\u00a0km \u2014 actual route not confirmed`,
      to: 'Division G4 / Division Surgeon',
      rfi: (snap) => buildRFI(snap,
        'Division G4 / Division Surgeon',
        'R2E distance and route confirmation',
        'Confirm actual R2E \u2192 R3 evacuation distance and route availability for the planning period.',
        `Doctrinal default (${RTH_THRESHOLDS.R2E_DEFAULT_DISTANCE_KM}\u00a0km) used \u2014 actual route may differ significantly.`),
    });
  }

  if (mc.r2e_accessible === false) {
    gaps.push({
      id: 'r2e_not_accessible',
      type: 'RFI',
      priority: 'CRITICAL',
      category: 'Medical Chain',
      title: 'R2E marked not accessible \u2014 alternate routing required',
      implication: 'R2E bypass increases R2B load and evacuation time; surgical backlog risk',
      to: 'Division G3 / Division G4',
      rfi: (snap) => buildRFI(snap,
        'Division G3 / Division G4',
        'R2E accessibility and alternate evacuation routing',
        'Confirm alternate evacuation routing to R3 when R2E is inaccessible.',
        'R2E inaccessible \u2014 patient flow to R3 degraded; R2B MASCAL risk elevated.'),
    });
  }

  if (mc.r3_accessible === false) {
    gaps.push({
      id: 'r3_not_accessible',
      type: 'RFI',
      priority: 'CRITICAL',
      category: 'Medical Chain',
      title: 'R3 marked not accessible \u2014 surgical capability absent',
      implication: 'All surgical cases deferred to STRATEVAC; higher DOW rate applies',
      to: 'Corps Surgeon / Division G4',
      rfi: (snap) => buildRFI(snap,
        'Corps Surgeon / Division G4',
        'R3 accessibility and STRATEVAC capacity confirmation',
        'Confirm STRATEVAC capacity to absorb all surgical cases given R3 inaccessibility.',
        'R3 inaccessible \u2014 surgical deferred to STRATEVAC; DOW rate elevated ~15-20%.'),
    });
  }

  // ── Evacuation ─────────────────────────────────────────────────────────────

  // FEA-S97-001: MASCAL projected — quantified RFA for R2B augmentation
  const _mascalDay = results?.mascal_analysis?.system_summary?.first_critical_day
                  ?? results?.mascal_analysis?.r2b_first_critical_day
                  ?? null;
  if (_mascalDay != null) {
    const _facAssets = snapshot?.medical_configuration?.per_facility_evacuation_assets || [];
    const _r2bBeds   = _facAssets.filter(f => f.facility_type === 'ROLE2_BASIC' || f.facility_type === 'ROLE_2_BASIC')
                         .reduce((s, f) => s + (f.beds || 0), 0);
    const _r2bSurgTeams = snapshot?.medical_configuration?.surgical_config?.teams?.FRT?.count ?? 0;
    const _peakPct   = results?.mascal_analysis?.system_summary?.peak_r2b_pct ?? null;
    const _peakStr   = _peakPct != null ? ` (peak ${Math.round(_peakPct)}% occupancy)` : '';
    gaps.push({
      id: 'mascal_projected',
      type: 'RFA',
      priority: 'CRITICAL',
      category: 'Surgical Capacity',
      title: `R2B MASCAL projected D+${_mascalDay} \u2014 augmentation required`,
      implication: `Current R2B: ${_r2bBeds} beds${_peakStr}. MASCAL threshold will be exceeded — surgical backlog, elevated DoW.`,
      to: 'Corps Surgeon / J4 Medical Logistics / Division Surgeon',
      rfi: (snap) => buildRFA(snap,
        'Corps Surgeon / J4 Medical Logistics / Division Surgeon',
        `Role 2B surgical augmentation \u2014 MASCAL projected D+${_mascalDay}`,
        `Request Role 2B augmentation assets to prevent MASCAL threshold breach:\n` +
        `   - Additional beds: minimum ${Math.max(0, Math.round(_r2bBeds * 0.3))} (30% surge buffer above current ${_r2bBeds})\n` +
        `   - Additional surgical teams: minimum 1 FRT or FST (current: ${_r2bSurgTeams}) by D+${Math.max(1, _mascalDay - 1)}\n` +
        `   - Coordinate pre-positioned blood augmentation (see blood RFA if applicable)`,
        `MASCAL threshold exceeded D+${_mascalDay}: surgical backlog accumulates, DoW rate increases ~15%.`),
    });
  }

  if (ghPct !== null && ghPct < RTH_THRESHOLDS.MEDEVAC_GH_MIN_PCT) {
    const ghRounded = Math.round(ghPct);
    const _shortfallPct = Math.round(RTH_THRESHOLDS.MEDEVAC_GH_MIN_PCT - ghPct);
    gaps.push({
      id: 'gh_low',
      type: 'RFA',
      priority: ghPct < 60 ? 'CRITICAL' : 'HIGH',
      category: 'Evacuation',
      title: `Golden Hour achievement ${ghRounded}% \u2014 below ${RTH_THRESHOLDS.MEDEVAC_GH_MIN_PCT}% threshold`,
      implication: `${Math.round(100 - ghPct)}% of T1 casualties not reaching surgical care within 60\u00a0min`,
      to: 'Division G3 / Aviation Command',
      // FEA-S97-001: quantified MEDEVAC RFA with GH shortfall %
      rfi: (snap, res) => buildRFA(snap,
        'Division G3 / Aviation Command',
        `MEDEVAC augmentation \u2014 GH shortfall ${_shortfallPct}%`,
        `Request additional MEDEVAC helicopter sorties/day to close Golden Hour gap:\n` +
        `   - Current achievement: ${ghRounded}% — shortfall: ${_shortfallPct} percentage points\n` +
        `   - Request: minimum ${Math.ceil(_shortfallPct / 10)} additional MEDEVAC helicopters (or equivalent sorties/day)\n` +
        `   - Alternatively: forward positioning of Role 2B surgical team to reduce evacuation distance`,
        `GH ${ghRounded}% \u2014 ${_shortfallPct}pp below ${RTH_THRESHOLDS.MEDEVAC_GH_MIN_PCT}% minimum. Preventable DoW increasing.`),
    });
  }

  // ── Logistics ──────────────────────────────────────────────────────────────

  const dosProj   = resupply?.dos_projection;
  const minDOS    = dosProj?.minimum_dos  ?? resupply?.minimum_dos  ?? null;
  const stockoutD = dosProj?.stockout_day ?? resupply?.stockout_day ?? null;

  if (minDOS !== null && minDOS < RTH_THRESHOLDS.BLOOD_DOS_MIN_DAYS) {
    const dispDOS = minDOS < 0 ? '<0' : String(Math.round(minDOS));
    const _bloodTotal = results?.medical_requirements?.blood?.total_units ?? null;
    const _dur        = results?.by_corps?.duration_days ?? null;
    const _dailyBlood = (_bloodTotal != null && _dur != null && _dur > 0) ? Math.round(_bloodTotal / _dur) : null;
    gaps.push({
      id: 'blood_dos_low',
      type: 'RFA',
      priority: 'CRITICAL',
      category: 'Logistics',
      title: `Minimum blood DOS ${dispDOS}\u00a0days \u2014 below NATO 3-day minimum`,
      implication: 'Blood stock insufficient; transfusion capability at risk without emergency resupply',
      to: 'Division G4 / J4 Medical Logistics',
      rfi: (snap) => buildRFA(snap,
        'Division G4 / J4 Medical Logistics',
        `Blood product pre-positioning \u2014 DOS ${dispDOS} days (min 3 required)`,
        `Request blood product pre-positioning to restore minimum 3 DOS at all Role 2/3 MTFs:\n` +
        `   - Daily blood requirement: ~${_dailyBlood != null ? _dailyBlood + ' units/day' : '[calculate from Tab 5]'}\n` +
        `   - Deficit: ${Math.round((3 - (minDOS < 0 ? 0 : minDOS)) * (_dailyBlood || 0))} units required before D-Day\n` +
        `   - Products: PRBC, FFP, PLT in 1:1:1 ratio (MTP ready)`,
        `Projected minimum DOS ${dispDOS}\u00a0days \u2014 AJMedP-2 minimum 3 DOS not met. Blood transfusion at risk.`),
    });
  }

  if (stockoutD !== null && stockoutD <= RTH_THRESHOLDS.BLOOD_STOCKOUT_EARLY_DAY) {
    gaps.push({
      id: 'blood_stockout_early',
      type: 'RFA',
      priority: 'CRITICAL',
      category: 'Logistics',
      title: `Blood stockout projected Day\u00a0${stockoutD} \u2014 emergency resupply required`,
      implication: `No blood available from D+${stockoutD} \u2014 mass casualty care degraded`,
      to: 'Division G4 / J4 / Medical Logistics',
      rfi: (snap) => buildRFA(snap,
        'Division G4 / J4 / Medical Logistics',
        `Emergency Class\u00a0VIII resupply \u2014 blood stockout D+${stockoutD}`,
        `Authorise emergency blood resupply prior to Day\u00a0${stockoutD} to prevent complete stockout:\n` +
        `   - Blood required at R3 / R2E by no later than D+${Math.max(1, stockoutD - 1)}\n` +
        `   - Minimum 2 DOS forward-staged to cover production gap\n` +
        `   - STRATEVAC blood cooler (field-grade storage) authorisation required`,
        `Stockout projected Day\u00a0${stockoutD} \u2014 life-threatening supply gap without emergency resupply.`),
    });
  }

  // ── Force Medical ──────────────────────────────────────────────────────────

  if (frt < RTH_THRESHOLDS.FRT_MIN_COUNT) {
    gaps.push({
      id: 'no_frt',
      type: 'RFA',
      priority: 'HIGH',
      category: 'Force Medical',
      title: 'No Forward Resuscitation Team (FRT) deployed',
      implication: 'DOW rate ~7% higher \u2014 no far-forward blood transfusion / resuscitation',
      to: 'Division Surgeon / CSSG Medical',
      rfi: (snap) => buildRFI(snap,
        'Division Surgeon / CSSG Medical',
        'FRT deployment authorisation',
        'Request FRT deployment to reduce preventable deaths of wounded at point of injury.',
        'FRT absent \u2014 estimated +7% DOW rate per casualty calculator (v5.9.0).'),
    });
  }

  // ── HNS ───────────────────────────────────────────────────────────────────

  const hnsLevel = (hns?.level ?? 'NONE').toString().toUpperCase();
  if (hnsLevel !== 'NONE' && hnsLevel !== 'ABSENT') {
    const hnsCfgReliability = hns?.reliability ?? null;
    if (hnsCfgReliability === null ||
        hnsCfgReliability < RTH_THRESHOLDS.HNS_RELIABILITY_MIN) {
      gaps.push({
        id: 'hns_reliability',
        type: 'RFI',
        priority: 'HIGH',
        category: 'HNS',
        title: 'HNS reliability unconfirmed or below planning threshold',
        implication: 'HNS capacity in plan may not materialise; military medical demand underestimated',
        to: 'Division G9 / CIMIC / Host Nation Liaison',
        rfi: (snap) => buildRFI(snap,
          'Division G9 / CIMIC / Host Nation Liaison',
          'HNS delivery reliability and MOU status',
          'Confirm HNS delivery reliability and MOU signing status prior to D-Day.',
          'HNS reliability not confirmed \u2014 plan may overestimate available host nation capacity.'),
      });
    }
  }

  return gaps;
}

// ============================================================================
// V5-001: ANNEX Q PDF GENERATOR
// Produces a properly-formatted A4 PDF of OPORD Annex Q.
// Runs at module scope so it can be called from renderAnnexQ() without
// requiring all params to be threaded through the component tree.
//
// @param {object} baseline      - { snapshot, results } from Tab 5
// @param {object} coaSlot1      - CoA A slot ({ snapshot, results, name, saved_at })
// @param {object} coaSlot2      - CoA B slot (may be null)
// @param {Array}  variantsList  - variants[] from component state
// @param {string} approvedLabel - "Baseline" or approved variant name
// @returns {Promise<Blob>} PDF Blob
// ============================================================================
async function generateAnnexQToPDF(baseline, coaSlot1, coaSlot2, variantsList, approvedLabel) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const PW  = doc.internal.pageSize.getWidth();
  const PH  = doc.internal.pageSize.getHeight();
  const M   = 20;   // margin
  const CW  = PW - M * 2;
  const NATO_BLUE  = [25, 118, 210];
  const NAVY       = [13, 71, 161];
  const GREY_LIGHT = [245, 245, 245];
  const UNCLASS    = 'NATO UNCLASSIFIED // EXERCISE';

  // ---- data helpers ---------------------------------------------------------
  const snap  = baseline?.snapshot ?? {};
  const op    = snap.operation_parameters ?? {};
  const res   = baseline?.results ?? {};
  const bs    = extractBaselineSummary(res);
  const corps = res.by_corps ?? {};
  const lg    = res.logistics ?? {};
  const med   = res.medical_system_analysis ?? {};
  const dur   = op.duration_days ?? op.corps_config?.total_duration_days ?? '--';
  // BUG-S97-004 FIX: human-readable posture label instead of raw enum code
  const POSTURE_LABELS = {
    DELIBERATE_ATTACK: 'Deliberate Attack', ATTACK: 'Attack',
    HASTY_ATTACK: 'Hasty Attack', DELIBERATE_DEFENSE: 'Deliberate Defense',
    DEFENSE: 'Defense', HASTY_DEFENSE: 'Hasty Defense',
    TACTICAL_MARCH: 'Tactical March', CONSOLIDATION: 'Consolidation',
    GROUND_COMBAT: 'Ground Combat', SUPPORT: 'Support Operations',
  };
  const _postureRaw = op.operational_posture ?? op.corps_config?.overall_mission ?? 'GROUND_COMBAT';
  const posture = POSTURE_LABELS[_postureRaw] || _postureRaw;
  const nat   = snap.force?.nationalStandard ?? snap.force?.forceBuilderResult?.national_standard ?? 'NATO';
  const tprp  = op.theatre_patient_return_policy?.policy_days ?? op.thp_config?.policy_days ?? 7;
  // BUG-S97-004 FIX: NATO DTG format DDHHMMZMONYY (e.g. 241204ZAPR26)
  const _now  = new Date();
  const _pad  = (n, w = 2) => String(n).padStart(w, '0');
  const _mon  = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'][_now.getUTCMonth()];
  const dtg   = `${_pad(_now.getUTCDate())}${_pad(_now.getUTCHours())}${_pad(_now.getUTCMinutes())}Z${_mon}${String(_now.getUTCFullYear()).slice(2)}`;
  const fmt   = (v, d = '--') => (v != null ? String(v) : d);

  // ---- page header / footer helpers ----------------------------------------
  const pageHeader = () => {
    doc.setFontSize(7);
    doc.setTextColor(180, 30, 30);
    doc.setFont(undefined, 'bold');
    doc.text(UNCLASS, PW / 2, 7, { align: 'center' });
    doc.setTextColor(150, 150, 150);
    doc.setFont(undefined, 'normal');
  };
  const addFooters = () => {
    const total = doc.internal.getNumberOfPages();
    for (let i = 1; i <= total; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(180, 30, 30);
      doc.setFont(undefined, 'bold');
      doc.text(UNCLASS, PW / 2, PH - 7, { align: 'center' });
      doc.setTextColor(130, 130, 130);
      doc.setFont(undefined, 'normal');
      doc.text(`PSP/CRE Tool v3.1 | AJP-4.10C | Page ${i} of ${total}`, PW / 2, PH - 3, { align: 'center' });
    }
  };

  // ---- section heading helper -----------------------------------------------
  let y = M + 2;
  const checkBreak = (need) => {
    if (y + need > PH - M) { doc.addPage(); pageHeader(); y = M + 4; }
  };
  const sectionHeading = (text) => {
    checkBreak(14);
    doc.setFontSize(13);
    doc.setTextColor(...NATO_BLUE);
    doc.setFont(undefined, 'bold');
    // Blue left bar
    doc.setFillColor(...NATO_BLUE);
    doc.rect(M, y - 4, 2, 7, 'F');
    doc.text(text, M + 5, y);
    y += 8;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
  };
  const subHeading = (text) => {
    checkBreak(8);
    doc.setFontSize(10);
    doc.setTextColor(...NAVY);
    doc.setFont(undefined, 'bold');
    doc.text(text, M, y);
    y += 6;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(50, 50, 50);
  };
  const bodyLine = (text, indent = 0) => {
    checkBreak(5);
    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    doc.setFont(undefined, 'normal');
    const lines = doc.splitTextToSize(text, CW - indent);
    lines.forEach(l => {
      checkBreak(4.5);
      doc.text(l, M + indent, y);
      y += 4.5;
    });
  };
  const kv = (label, value, indent = 4) => {
    checkBreak(4.5);
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(50, 50, 50);
    doc.text(`${label}:`, M + indent, y);
    doc.setFont(undefined, 'normal');
    doc.text(fmt(value), M + indent + 38, y);
    y += 4.5;
  };

  // ==========================================================================
  // COVER PAGE
  // ==========================================================================
  pageHeader();

  // Big title block
  doc.setFontSize(8);
  doc.setTextColor(130, 130, 130);
  doc.text('OPORD', PW / 2, M + 8, { align: 'center' });

  doc.setFontSize(26);
  doc.setTextColor(...NATO_BLUE);
  doc.setFont(undefined, 'bold');
  doc.text('ANNEX Q', PW / 2, M + 18, { align: 'center' });

  doc.setFontSize(14);
  doc.setTextColor(...NAVY);
  doc.text('MEDICAL SUPPORT PLAN', PW / 2, M + 27, { align: 'center' });

  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.setFont(undefined, 'normal');
  doc.text('AJP-4.10C / AJMedP-1 / AMedP-1.12 Compliant', PW / 2, M + 33, { align: 'center' });

  // Divider
  doc.setDrawColor(...NATO_BLUE);
  doc.setLineWidth(0.8);
  doc.line(M, M + 37, PW - M, M + 37);

  // Cover metadata table
  autoTable(doc, {
    startY: M + 41,
    head: [],
    body: [
      ['Operation',     snap.scenario_name || 'NOT SPECIFIED'],
      ['DTG',          dtg],
      ['Duration',      `${dur} days — ${posture}`],
      ['Classification', UNCLASS],
      ['Approved basis', `${approvedLabel || 'Baseline'}`],
      ['National standard', nat],
      ['TPRP',          `${tprp} days (AJMedP-2 / AJP-4.10C)`],
      // FEA-S97-002: configurable prepared_by field from Tab 2 scenario metadata
      ['Prepared by',   (op.prepared_by ? op.prepared_by + ' — ' : '') + 'PSP/CRE Tool v3.1'],
      ['References',    'AJP-4.10C, AJMedP-1, AMedP-1.12, STANAG 2348'],
    ],
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 3 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 42, fillColor: GREY_LIGHT },
      1: { cellWidth: CW - 42 }
    },
    margin: { left: M, right: M },
  });

  // ==========================================================================
  // Q-1. SITUATION
  // ==========================================================================
  doc.addPage();
  pageHeader();
  y = M + 4;
  sectionHeading('Q-1. SITUATION');

  subHeading('a. Medical Intelligence (MEDINT):');
  kv('Operational posture', posture);
  kv('Duration',            `${dur} days`);
  kv('National standard',   nat);
  kv('TPRP',                `${tprp} days`);
  kv('HNS',                 op.hns_config?.enabled ? `Level: ${op.hns_config?.level ?? 'STANDARD'}` : 'Not configured');
  y += 2;

  subHeading('b. Own medical forces:');
  // OBS-S97-002: use real facility data from per_facility_evacuation_assets
  const _facAssets = snap.medical_configuration?.per_facility_evacuation_assets || [];
  const _ROLE_LBLS = { ROLE2_BASIC: 'Role 2B', ROLE2_ENHANCED: 'Role 2E', ROLE3: 'Role 3', ROLE1: 'Role 1' };
  if (_facAssets.length === 0) {
    bodyLine('Medical facilities: not configured \u2014 see Tab 3.', 4);
  } else {
    ['ROLE2_BASIC', 'ROLE2_ENHANCED', 'ROLE3'].forEach(roleType => {
      const roleFacs = _facAssets.filter(f => f.facility_type === roleType || f.facility_type === roleType.replace('2_', '2'));
      if (roleFacs.length === 0) return;
      roleFacs.forEach(f => {
        const parts = [];
        if (f.beds)           parts.push(`${f.beds} beds`);
        if (f.surgical_teams) parts.push(`${f.surgical_teams} surg. team${f.surgical_teams > 1 ? 's' : ''}`);
        bodyLine(`${_ROLE_LBLS[roleType] || roleType}: ${f.facility_name || f.facility_type}${parts.length ? ' \u2014 ' + parts.join(', ') : ''}`, 4);
      });
    });
  }
  y += 2;

  subHeading('c. Supporting medical forces:');
  // OBS-S97-003: use real STRATEVAC data from snapshot
  const _sc = snap.medical_configuration?.stratevac_config ?? op.stratevac_config ?? {};
  const _svLabel  = _sc.airfield_label  ?? _sc.airfields?.[0]?.label ?? 'Primary STRATEVAC Airfield';
  const _svCap    = _sc.weekly_capacity ?? snap.operation_parameters?.stratevac_config?.weekly_capacity ?? null;
  const _phaBeds  = _sc.pha_beds ?? _sc.airfields?.[0]?.pha_beds ?? null;
  kv('STRATEVAC airfield',        _svLabel);
  kv('Weekly STRATEVAC capacity', _svCap   != null ? `${_svCap} patients/week` : 'Not configured');
  kv('PHA capacity',              _phaBeds != null ? `${_phaBeds} beds (post-surgical staging)` : 'Not configured');
  kv('Cross-division support',    'Requires formal approval (cross-division log)');
  y += 4;

  // ==========================================================================
  // Q-2. MISSION
  // ==========================================================================
  sectionHeading('Q-2. MISSION');
  bodyLine(
    `The Medical Support Element will provide continuous Role 1 through Role 3 medical ` +
    `support to the Corps throughout ${dur} days of ${posture} operations, maintaining ` +
    `Golden Hour compliance and Theatre Patient Return Policy of ${tprp} days, ` +
    `in accordance with AJMedP-1 and AJP-4.10C.`
  );
  y += 4;

  // ==========================================================================
  // Q-3. EXECUTION
  // ==========================================================================
  sectionHeading('Q-3. EXECUTION');
  subHeading('a. Concept of Medical Support — Casualty Summary:');

  if (bs) {
    const totalCas = corps.total_all_sources ?? corps.total_casualties ?? bs.total_casualties;
    const ghPct    = med.overall_golden_hour_percent ?? bs.golden_hour_pct;
    // BUG-S97-005 FIX: dur2 was falling back to 1 when corps.duration_days and
    // res.duration_days were both undefined — dividing totals by 1 = totals as daily avg.
    // Now reads from operation_parameters and snapshot as additional fallbacks.
    const dur2     = corps.duration_days ?? res.duration_days
                     ?? op.duration_days ?? op.corps_config?.total_duration_days
                     ?? snap?.operation_parameters?.duration_days ?? 1;
    autoTable(doc, {
      startY: y,
      head: [['Category', 'Total', 'Daily Avg', 'Medical Status']],
      body: [
        ['KIA',           fmt(corps.total_kia  ?? bs.kia),  fmt(Math.round((corps.total_kia  ?? bs.kia  ?? 0) / dur2)), '--'],
        ['WIA',           fmt(corps.total_wia  ?? bs.wia),  fmt(Math.round((corps.total_wia  ?? bs.wia  ?? 0) / dur2)), '--'],
        ['DNBI',          fmt(corps.total_dnbi ?? bs.dnbi), fmt(Math.round((corps.total_dnbi ?? bs.dnbi ?? 0) / dur2)), '--'],
        ['RTD',           fmt(bs.rtd),                      '--',                                                        '--'],
        ['TOTAL',         fmt(totalCas),                    fmt(Math.round((totalCas ?? 0) / dur2)),                    fmt(med.overall_status ?? '--')],
        ['Golden Hour',   ghPct != null ? ghPct.toFixed(1) + '%' : '--', '--',
          ghPct != null ? (ghPct >= 90 ? 'Excellent' : ghPct >= 70 ? 'Adequate' : 'Needs improvement') : '--'],
        ['First MASCAL',  bs.r2b_mascal_days != null ? 'D+' + bs.r2b_mascal_days : 'None projected', '--', '--'],
      ],
      headStyles: { fillColor: NATO_BLUE, textColor: 255, fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 2.5 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 32 },
        1: { cellWidth: 22, halign: 'right' },
        2: { cellWidth: 22, halign: 'right' },
        3: { cellWidth: CW - 76 },
      },
      margin: { left: M, right: M },
    });
    y = doc.lastAutoTable.finalY + 6;
  } else {
    bodyLine('No calculated results — load Tab 5 Baseline before generating PDF.', 4);
    y += 4;
  }

  checkBreak(14);
  subHeading('b. Tasks to medical units:');
  // BUG-S97-006 FIX: replace Unicode <= (U+2264) — jsPDF default font renders it as "d
  [`(1) Role 1 elements: immediate TCCC at point of injury.`,
   `(2) Role 2B (Brigade Medical Company): resuscitative surgery, hold <= TPRP.`,
   `(3) Role 2E (Division): damage control surgery, ICU capability.`,
   `(4) Role 3 (Corps): definitive care, specialist surgery, blood bank.`,
   `(5) STRATEVAC: evacuation of non-RTD patients to Role 4 per schedule.`,
  ].forEach(t => bodyLine(t, 4));
  y += 3;

  checkBreak(8);
  subHeading('c. Theatre Patient Return Policy:');
  kv('TPRP', `${tprp} days - AJMedP-2 / AJP-4.10C`);
  // BUG-S97-006 FIX: <= instead of Unicode <=
  bodyLine(`All patients held <= ${tprp} days. Non-RTD patients evacuated to Role 4.`, 4);
  y += 3;

  // Brigade phase roles (V5-012)
  checkBreak(12);
  subHeading('d. Brigade tactical missions and phase role designations:');
  const brigades = getSnapshotBrigades(snap);
  if (brigades.length === 0) {
    bodyLine('No brigade configurations found — configure in Tab 1/2.', 4);
  } else {
    const corpsPhasesArr = op.corps_config?.phases || op.operation_phases || [];
    const phLbl = (idx) => {
      const ph = corpsPhasesArr[Number(idx)];
      return ph?.name ? `Phase ${Number(idx) + 1} (${ph.name})` : `Phase ${Number(idx) + 1}`;
    };
    // OBS-S97-008: condense to one row per brigade — phase roles as semicolon-separated summary
    const roleRows = brigades.map(b => {
      const bName    = b.name || b.brigade_id || 'Unknown Bde';
      const t1       = b.operation?.mission_type ?? b.phases?.[0]?.mission_type ?? '--';
      const overrides = Object.entries(b.phase_overrides || {}).filter(([, ov]) => ov?.brigade_role);
      if (overrides.length === 0) return [bName, t1, 'All phases', '\u2014'];
      const roleSummary = overrides
        .sort(([a], [bb]) => Number(a) - Number(bb))
        .map(([idx, ov]) => `${phLbl(idx)}: ${ov.brigade_role.replace(/_/g, ' ')}`)
        .join('; ');
      return [bName, t1, 'Per phase', roleSummary];
    });
    autoTable(doc, {
      startY: y,
      head: [['Brigade', 'Tab 1 Mission', 'Phase', 'Phase Role']],
      body: roleRows,
      headStyles: { fillColor: NAVY, textColor: 255, fontSize: 8 },
      styles: { fontSize: 8.5, cellPadding: 2 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 40 },
        1: { cellWidth: 32 },
        2: { cellWidth: 38 },
        3: { cellWidth: CW - 110 },
      },
      margin: { left: M, right: M },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // ==========================================================================
  // Q-3e. EVACUATION FLEET (OBS-S97-007a)
  // ==========================================================================
  checkBreak(14);
  subHeading('e. Ground evacuation fleet summary (AJMedP-1):');
  const _r1f = corps.r1_fleet ?? {};
  const _r1t = _r1f.total ?? {};
  if (_r1t.wheeled != null || _r1t.armored != null || _r1t.helo != null) {
    autoTable(doc, {
      startY: y,
      head: [['Asset type', 'Count', 'Remark']],
      body: [
        ['Wheeled ambulances',   fmt(_r1t.wheeled), 'ROLE 1 organic + attached'],
        ['Armoured ambulances',  fmt(_r1t.armored), 'ROLE 1 organic + attached'],
        ['MEDEVAC helicopters',  fmt(_r1t.helo),    `Capacity: ~${fmt(_r1t.helo != null ? _r1t.helo * 12 : null)} patients/day`],
      ],
      headStyles: { fillColor: NATO_BLUE, textColor: 255, fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 2.5 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 44 },
        1: { cellWidth: 16, halign: 'right' },
        2: { cellWidth: CW - 60 },
      },
      margin: { left: M, right: M },
    });
    y = doc.lastAutoTable.finalY + 4;
  } else {
    bodyLine('Evacuation fleet: calculate in Tab 5 to populate.', 4);
    y += 4;
  }

  // ==========================================================================
  // Q-3f. MASCAL CONTINGENCY (OBS-S97-007b)
  // ==========================================================================
  checkBreak(14);
  subHeading('f. MASCAL contingency:');
  if (bs) {
    const _mascDay = bs.r2b_mascal_days;
    if (_mascDay != null) {
      bodyLine(`MASCAL threshold projected: D+${_mascDay}. Immediate actions on MASCAL declaration:`, 4);
    } else {
      bodyLine('MASCAL threshold: not projected within operation duration.', 4);
      bodyLine('Contingency plan in force per the following triggers:', 4);
    }
    [`(1) Declare MASCAL — activate Mass Casualty Protocol (AMedP-8.19 §4.5).`,
     `(2) Suspend MedROE triage tiers — triage by clinical need only (T1/T2/T3).`,
     `(3) Activate blood mass-transfusion protocol (MTP) — 1:1:1 PRBC:FFP:PLT.`,
     `(4) Request MEDEVAC lift augmentation through corps G4 (Blue Light Protocol).`,
     `(5) Notify STRATEVAC for early departure window if PHA capacity threatened.`,
     `(6) Submit MASCAL report to Corps Surgeon within 30 min (STANAG 2348).`,
    ].forEach(t => bodyLine(t, 4));
  } else {
    bodyLine('No calculation results \u2014 load Tab 5 Baseline to populate MASCAL data.', 4);
  }
  y += 4;

  // ==========================================================================
  // Q-4. SUSTAINMENT
  // ==========================================================================
  checkBreak(14);
  sectionHeading('Q-4. SUSTAINMENT');
  subHeading('a. Medical materiel:');

  if (bs) {
    const blood = res.medical_requirements?.blood ?? {};
    const bloodTotal = blood.total_units ?? lg.blood_units_required;
    // OBS-S97-004: compute blood status from stockout projection and supply status
    const _bsStatus = (() => {
      if (bs.blood_stockout_day != null) return `STOCKOUT PROJECTED D+${bs.blood_stockout_day}`;
      const sup = lg.supply_status || lg.overall_status || '';
      if (sup === 'CRITICAL') return 'AT RISK';
      if (sup === 'ADEQUATE') return 'ADEQUATE';
      return '--';
    })();
    autoTable(doc, {
      startY: y,
      head: [['Item', 'Requirement', 'Status']],
      body: [
        ['Blood — total units',  fmt(bloodTotal),                                                      _bsStatus],
        ['PRBC',                 fmt(blood.prbc),                                                      '--'],
        ['FFP',                  fmt(blood.ffp),                                                       '--'],
        ['Platelets',            fmt(blood.plt),                                                       '--'],
        ['Cryo',                 fmt(blood.cryo),                                                      '--'],
        ['Blood stockout',       bs.blood_stockout_day != null ? 'Day ' + bs.blood_stockout_day : 'None projected', '--'],
        ['Logistics supply',     fmt(lg.supply_status ?? lg.overall_status ?? 'See Tab 5'),            '--'],
      ],
      headStyles: { fillColor: NATO_BLUE, textColor: 255, fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 2.5 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 38 },
        1: { cellWidth: 35, halign: 'right' },
        2: { cellWidth: CW - 73 },
      },
      margin: { left: M, right: M },
    });
    y = doc.lastAutoTable.finalY + 6;
  } else {
    bodyLine('No logistics data — load Tab 5 Baseline with results.', 4);
    y += 4;
  }

  checkBreak(10);
  subHeading('b. Medical Materiel resupply (AMedP-1.12):');
  bodyLine('Threshold-triggered per AMedP-1.12. Commanded MDOS maintained throughout.', 4);
  bodyLine('Resupply schedule: see Tab 4 logistics configuration.', 4);
  y += 4;

  // ==========================================================================
  // Q-5. COMMAND AND SIGNAL
  // ==========================================================================
  checkBreak(14);
  sectionHeading('Q-5. COMMAND AND SIGNAL');
  subHeading('a. Command:');
  bodyLine('Medical advisor (MEDAD) to Corps Commander.', 4);
  bodyLine('Coordinated through J5 per COPD / AJP-5.', 4);
  bodyLine('Cross-division medical support requires formal approval (cross-division log).', 4);
  y += 3;

  subHeading('b. MEDSITREP reporting:');
  // BUG-S97-006 FIX: doctrinal frequency instead of hardcoded placeholder
  kv('Frequency', 'Every 24h routine, or on event (MASCAL, stockout, MTF loss) — AJMedP-4.1');
  kv('Format',    'STANAG 2348 / AMedP-4.1');
  // BUG-S97-009 FIX: omit "Variants calculated: 0" — meaningless in a formal document
  const _calcVariants = (variantsList || []).filter(v => v.results).length;
  if (_calcVariants > 0) kv('Sensitivity variants', `${_calcVariants} stress-test variant(s) calculated`);
  y += 4;

  // ==========================================================================
  // Q-6. TREATMENT OBLIGATIONS (MedROE)
  // ==========================================================================
  doc.addPage();
  pageHeader();
  y = M + 4;
  sectionHeading('Q-6. TREATMENT OBLIGATIONS (MedROE)');

  const mroe = op.mroe_config;
  if (!mroe || (!mroe.state && !mroe.enabled)) {
    bodyLine('MedROE NOT CONFIGURED — treatment obligations not defined for this scenario.', 4);
  } else {
    const stateDef  = mroe.state ? MROE_STATES[mroe.state] : null;
    const stateLabel = stateDef?.label ?? 'NOT SET';
    const mx         = mroe.matrix;
    const defaultMx  = (mroe.state && MROE_DEFAULT_MATRICES[mroe.state]) || {};
    let   modCount   = 0;

    kv('MRoE State', stateLabel);
    if (stateDef?.description) bodyLine(stateDef.description, 4);
    y += 3;

    if (mx) {
      const matrixBody = PATIENT_CATEGORIES.map(cat => {
        const cell    = mx[cat.key] || {};
        const defCell = defaultMx[cat.key] || {};
        const lleVal  = cell.lle    || 'Y';
        const nlleVal = cell.nonLle || 'Y';
        const lleMod  = cat.lockTier !== 'A' && lleVal  !== (defCell.lle    || 'Y');
        const nlleMod = cat.lockTier !== 'A' && nlleVal !== (defCell.nonLle || 'Y');
        if (lleMod)  modCount++;
        if (nlleMod) modCount++;
        const lockMark = cat.lockTier === 'A' ? ' [LOCKED]' : '';
        return [
          cat.label + lockMark,
          lleVal  + (lleMod  ? ' *' : ''),
          nlleVal + (nlleMod ? ' *' : ''),
        ];
      });
      autoTable(doc, {
        startY: y,
        head: [['Patient Category', 'LLE', 'Non-LLE']],
        body: matrixBody,
        headStyles: { fillColor: NAVY, textColor: 255, fontSize: 9 },
        styles: { fontSize: 9, cellPadding: 2.5 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 70 },
          1: { cellWidth: 20, halign: 'center' },
          2: { cellWidth: 20, halign: 'center' },
        },
        margin: { left: M, right: M },
      });
      y = doc.lastAutoTable.finalY + 4;
      if (modCount > 0) {
        bodyLine(`* = ${modCount} cell(s) modified from MRoE ${stateLabel} standard — require OPORD authorisation + LEGAD sign-off.`, 4);
      }
    } else {
      bodyLine('No treatment matrix configured.', 4);
    }
    y += 3;
    kv('EPW volume',          mroe.epw_volume || mroe.epw_level || 'NONE');
    kv('Civilian density',    mroe.civilian_density || mroe.civilian_level || 'NONE');
    y += 3;
    bodyLine('NOTE: During declared MASCAL all MedROE tiers are suspended — triage by clinical need only (AMedP-8.19 §4.5).', 4);
  }
  y += 4;

  // ==========================================================================
  // Q-7. COA COMPARISON VALIDITY
  // ==========================================================================
  checkBreak(20);
  sectionHeading('Q-7. COA COMPARISON VALIDITY');
  if (!coaSlot1 && !coaSlot2) {
    bodyLine('NOT CONFIGURED — No CoA slots saved. Use Tab 5 "Save as CoA A/B" to populate.', 4);
  } else if (!coaSlot1 || !coaSlot2) {
    const which = coaSlot1 ? 'CoA B' : 'CoA A';
    bodyLine(`INCOMPLETE — ${which} not saved. Both slots required for valid comparison.`, 4);
  } else {
    const snapA = coaSlot1.snapshot;
    const snapB = coaSlot2.snapshot;
    const resA  = coaSlot1.results ?? {};
    const resB  = coaSlot2.results ?? {};
    const corpA = resA.by_corps ?? {};
    const corpB = resB.by_corps ?? {};

    kv('CoA A', `${coaSlot1.name || 'CoA A'} — saved ${new Date(coaSlot1.saved_at).toLocaleString()}`);
    kv('CoA B', `${coaSlot2.name || 'CoA B'} — saved ${new Date(coaSlot2.saved_at).toLocaleString()}`);
    y += 3;

    // Integrity check
    const issues = [];
    const r3A = snapA?.medical_configuration?.mtf_configuration?.role3_beds;
    const r3B = snapB?.medical_configuration?.mtf_configuration?.role3_beds;
    if (r3A != null && r3B != null && r3A !== r3B) issues.push(`Role 3 beds: ${r3A} vs ${r3B}`);
    const svA = snapA?.operation_parameters?.stratevac_config?.weekly_capacity ?? snapA?.medical_configuration?.stratevac_config?.weekly_capacity;
    const svB = snapB?.operation_parameters?.stratevac_config?.weekly_capacity ?? snapB?.medical_configuration?.stratevac_config?.weekly_capacity;
    if (svA != null && svB != null && svA !== svB) issues.push(`STRATEVAC capacity: ${svA} vs ${svB} pts/wk`);
    const bA = snapA?.logistics?.medical_supplies?.initial_stock?.prbc_units;
    const bB = snapB?.logistics?.medical_supplies?.initial_stock?.prbc_units;
    if (bA != null && bB != null && bA !== bB) issues.push(`Blood stock (PRBC): ${bA} vs ${bB} units`);
    const dosA = snapA?.logistics?.days_of_supply;
    const dosB = snapB?.logistics?.days_of_supply;
    if (dosA != null && dosB != null && dosA !== dosB) issues.push(`Days of Supply: ${dosA} vs ${dosB}`);

    const validColor = issues.length === 0 ? [27, 94, 32] : [183, 28, 28];
    doc.setFontSize(10);
    doc.setTextColor(...validColor);
    doc.setFont(undefined, 'bold');
    checkBreak(6);
    // BUG-S97-007 FIX: replace Unicode checkmark/cross — jsPDF renders them as apostrophe
    doc.text(issues.length === 0 ? '[VALID] Planning baseline identical across CoAs.' : `[INVALID] ${issues.length} mismatch(es) detected`, M, y);
    y += 6;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(50, 50, 50);
    if (issues.length > 0) issues.forEach(iss => bodyLine(`* ${iss}`, 6));
    y += 3;

    // CoA comparison table (key outcomes)
    const hasCalc = resA.by_corps && resB.by_corps;
    if (hasCalc) {
      subHeading('Key outcomes comparison:');
      // BUG-S97-005 FIX: DoW added to comparison; BUG-S97-008 FIX: CoA A/B prefix on column headers
      const _colA = `CoA A - ${coaSlot1.name || 'CoA A'}`;
      const _colB = `CoA B - ${coaSlot2.name || 'CoA B'}`;
      const cmpBody = [
        ['Total casualties', fmt(corpA.total_all_sources ?? corpA.total_casualties), fmt(corpB.total_all_sources ?? corpB.total_casualties)],
        ['KIA',              fmt(corpA.total_kia),   fmt(corpB.total_kia)],
        ['Died of Wounds',   fmt(corpA.total_dow),   fmt(corpB.total_dow)],
        ['WIA survivors',    fmt(corpA.total_wia),   fmt(corpB.total_wia)],
        ['DNBI',             fmt(corpA.total_dnbi),  fmt(corpB.total_dnbi)],
        ['Blood units',      fmt((resA.medical_requirements?.blood?.total_units) ?? (resA.logistics?.blood_units_required)), fmt((resB.medical_requirements?.blood?.total_units) ?? (resB.logistics?.blood_units_required))],
      ];
      autoTable(doc, {
        startY: y,
        head: [['Metric', _colA, _colB]],
        body: cmpBody,
        headStyles: { fillColor: NATO_BLUE, textColor: 255, fontSize: 9 },
        styles: { fontSize: 9, cellPadding: 2.5 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 52 },
          1: { cellWidth: 30, halign: 'right' },
          2: { cellWidth: 30, halign: 'right' },
        },
        margin: { left: M, right: M },
      });
      y = doc.lastAutoTable.finalY + 4;
    }
    bodyLine('NOTE: MTF Role 1/2B/2E differences between CoAs are expected and not included in integrity check (ATP 4-02.6).', 4);
  }
  y += 8;

  // ==========================================================================
  // Q-8. REQUESTS TO HIGHER (OBS-S97-007c)
  // ==========================================================================
  doc.addPage();
  pageHeader();
  y = M + 4;
  sectionHeading('Q-8. REQUESTS TO HIGHER — PLANNING GAPS');
  const _pdfGaps = detectPlanningGaps(snap, res);
  if (_pdfGaps.length === 0) {
    bodyLine('No planning gaps detected. All critical planning assumptions are confirmed.', 4);
  } else {
    bodyLine(`${_pdfGaps.length} planning gap(s) identified. The following information / assets are required before execution:`, 4);
    y += 2;
    autoTable(doc, {
      startY: y,
      head: [['#', 'Priority', 'Category', 'Gap', 'To']],
      body: _pdfGaps.map((g, i) => [
        String(i + 1),
        g.priority || 'HIGH',
        g.category || '--',
        g.title || '--',
        g.to || '--',
      ]),
      headStyles: { fillColor: NAVY, textColor: 255, fontSize: 8 },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 8,  halign: 'center' },
        1: { cellWidth: 18, halign: 'center' },
        2: { cellWidth: 30 },
        3: { cellWidth: CW - 96 },
        4: { cellWidth: 40 },
      },
      margin: { left: M, right: M },
      didParseCell: (data) => {
        if (data.column.index === 1 && data.section === 'body') {
          const p = data.cell.raw;
          data.cell.styles.textColor = p === 'CRITICAL' ? [183, 28, 28] : p === 'HIGH' ? [230, 81, 0] : [50, 50, 50];
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });
    y = doc.lastAutoTable.finalY + 4;
    bodyLine('Planner action: raise formal RFI/RFA for each open gap before plan approval.', 4);
  }
  y += 8;

  // ==========================================================================
  // SIGNATURE BLOCK
  // ==========================================================================
  checkBreak(40);
  doc.setDrawColor(150, 150, 150);
  doc.setLineWidth(0.3);
  doc.line(M, y, PW - M, y);
  y += 6;
  doc.setFontSize(9);
  doc.setTextColor(50, 50, 50);
  doc.setFont(undefined, 'bold');
  doc.text('SIGNATURES', M, y); y += 6;
  doc.setFont(undefined, 'normal');

  const sigLine = (role, x, sw) => {
    doc.setFontSize(8.5);
    doc.text(role, x, y);
    doc.setDrawColor(80, 80, 80);
    doc.line(x, y + 8, x + sw, y + 8);
    doc.setFontSize(7.5);
    doc.setTextColor(120, 120, 120);
    doc.text('Rank / Name / DTG', x, y + 12);
    doc.setTextColor(50, 50, 50);
  };
  sigLine('MEDICAL ADVISOR (MEDAD):', M, 70);
  sigLine('REVIEWED BY J5/LEGAD:', M + 90, 70);
  y += 18;

  // ==========================================================================
  // FOOTERS (all pages)
  // ==========================================================================
  addFooters();
  return doc.output('blob');
}

const Tab6COAAnalysis = forwardRef(function Tab6COAAnalysis({ scenario, isVisible, onNavigate, onLoadApprovedPlan, onComplete }, ref) {
  const { t } = useTranslation();

  // -- State ------------------------------------------------------------------
  const [baseline,         setBaseline]         = useState(null);
  const [loadStatus,       setLoadStatus]        = useState(LOAD_STATUS.IDLE);
  const [approvalEvents,   setApprovalEvents]    = useState([]);
  // BUG-84-017: baseline can be approved directly without needing a variant
  const [baselineApproved, setBaselineApproved]  = useState(false);
  const [eventForm,        setEventForm]         = useState(EMPTY_EVENT_FORM);
  const [eventFormError,   setEventFormError]    = useState('');
  // Phase 2: variant management
  const [variants,         setVariants]          = useState([]);
  const [activeVariantId,  setActiveVariantId]   = useState(null);
  const [variantName,      setVariantName]       = useState('');
  const [qmValues,         setQmValues]          = useState({ ...EMPTY_QM_VALUES });
  // ENH-031: per-brigade operational state overrides { [brigadeId]: 'COMBAT_ENGAGED' | 'SUPPORT_ACTIVE' | 'TRUE_RESERVE' }
  const [brigadeOverrides, setBrigadeOverrides]  = useState({});
  const [isCalculating,    setIsCalculating]     = useState(false);
  const [calcError,        setCalcError]         = useState('');
  const calcAbortRef = useRef(false);
  // State for renderTab6DailyProjectionChart panel
  const [activeChartTab,   setActiveChartTab]   = useState('casualties');
  const [showAllDays,      setShowAllDays]       = useState(true);

  // ENH-030: Report generation state
  const [selectedReports,   setSelectedReports]  = useState(['medsitrep', 'executive_brief']);
  const [selectedFormat,    setSelectedFormat]   = useState('html');
  const [previewDialogOpen, setPreviewDialogOpen]= useState(false);
  const [previewContent,    setPreviewContent]   = useState('');
  const [previewReportId,   setPreviewReportId]  = useState(null);
  const [isExporting,       setIsExporting]      = useState(false);
  // BUG-6A-001: inline error shown directly below the Load Tab 5 Baseline button on failure
  const [tab5LoadError,    setTab5LoadError]    = useState('');
  const [snackbar,          setSnackbar]         = useState({ open: false, message: '', severity: 'success' });
  const [phaseExpanded,     setPhaseExpanded]    = useState({ assess: true, stress_test: false, analyse: false, coordinate: false, report: false });

  // V4-TAB6-001: CoA comparison slots
  // Each slot: { snapshot, results, saved_at, label, name } — written by Tab 5 handleSaveAsCoa
  const [coaSlot1, setCoaSlot1] = useState(null); // CoA A
  const [coaSlot2, setCoaSlot2] = useState(null); // CoA B

  // CoA approval — explicit commander decision (Steps 1+2)
  const [approvedCoASlot,   setApprovedCoASlot]   = useState(null);  // null | 1 | 2
  const [approvedVariantId, setApprovedVariantId] = useState(null);  // null = base CoA
  const [reApproveDialog,       setReApproveDialog]       = useState({ open: false, pendingSlot: null, pendingVariantId: null });
  const [loadWorkingPlanDialog, setLoadWorkingPlanDialog] = useState(false);

  // Step 5: Requests to Higher state
  const [rthGapSelection, setRthGapSelection] = useState({});   // { [gapId]: boolean }
  const [rthPreviewOpen,  setRthPreviewOpen]  = useState({});   // { [gapId]: boolean }
  const [rthRequiredBy,   setRthRequiredBy]   = useState({});   // { [gapId]: string }
  const [rthRemarks,      setRthRemarks]      = useState({});   // { [gapId]: string }
  const [rthReportDialog, setRthReportDialog] = useState(false);
  const [rthReportText,   setRthReportText]   = useState('');

  // -- Restore from localStorage on mount ------------------------------------
  useEffect(() => {
    let restoredBaseline = false;
    try {
      const parsed = lsRead(EXERCISE_STORAGE_KEYS.BASELINE);
      if (parsed) {
        // STORAGE-FIX-002: flushState strips results from Tab5 baselines before
        // writing to psp_exercise_baseline to avoid duplicating ~2 MB. Re-hydrate
        // results from psp_calculation_baseline now so panels have full data.
        if (parsed.source === 'tab5' && parsed.results === null) {
          try {
            const stored = getBaseline();
            if (stored?.results) {
              parsed.results = stored.results;
            }
          } catch (_rehydrateErr) { /* stay null if unavailable */ }
        }
        setBaseline(parsed);
        setLoadStatus(
          parsed.source === 'tab5'
            ? LOAD_STATUS.LOADED_TAB5
            : LOAD_STATUS.LOADED_SNAP
        );
        restoredBaseline = true;
      }
    } catch (_err) {
      // Corrupted storage  -  start clean, do not throw
    }
    try {
      const events = lsRead(EXERCISE_STORAGE_KEYS.EVENTS);
      if (events) setApprovalEvents(events);
    } catch (_err) {
      // Corrupted storage  -  start clean
    }
    // Phase 2: restore variants
    try {
      const ids = lsRead(EXERCISE_STORAGE_KEYS.VARIANT + 'index');
      if (ids) {
        const loaded = [];
        for (const id of ids) {
          const v = lsRead(EXERCISE_STORAGE_KEYS.VARIANT + id);
          if (v) loaded.push(v);
        }
        setVariants(loaded);
        if (loaded.length > 0) setActiveVariantId(loaded[0].id);
      }
    } catch (_err) {
      // Corrupted storage  -  start clean
    }

    // BUG-84-017: restore baseline approval flag
    try {
      const ba = lsRead(EXERCISE_STORAGE_KEYS.BASELINE_APPROVED);
      if (ba === true) setBaselineApproved(true);
    } catch (_err) { /* corrupted — stay false */ }

    // V4-TAB6-001: restore CoA slots
    try {
      const c1 = lsRead(EXERCISE_STORAGE_KEYS.COA_1);
      if (c1) setCoaSlot1(c1);
    } catch (_err) { /* corrupted — stay null */ }
    try {
      const c2 = lsRead(EXERCISE_STORAGE_KEYS.COA_2);
      if (c2) setCoaSlot2(c2);
    } catch (_err) { /* corrupted — stay null */ }

    // CoA approval state restore
    try {
      const slot = lsRead(EXERCISE_STORAGE_KEYS.APPROVED_COA_SLOT);
      if (slot === 1 || slot === 2) setApprovedCoASlot(slot);
    } catch (_err) { /* corrupted — stay null */ }
    try {
      const vid = lsRead(EXERCISE_STORAGE_KEYS.APPROVED_VARIANT_ID);
      if (vid !== null && vid !== undefined) setApprovedVariantId(vid);
    } catch (_err) { /* corrupted — stay null */ }

    // BUG-144 FIX (v3.5.1): Auto-load Tab 5 baseline on first mount if no cached
    // baseline was restored from localStorage. Previously the user had to manually
    // press "Load Tab 5 Baseline" every time they opened Tab 6 fresh.
    // Guard: only auto-load when localStorage had nothing — don't override a cached baseline.
    if (!restoredBaseline) {
      try {
        const stored = getBaseline();
        if (stored?.scenario) {
          const autoEntry = {
            name:      stored.scenario?.operation_parameters?.operation_name ?? 'Baseline',
            snapshot:  deepCopy(stored.scenario),
            results:   stored.results ?? null,
            loaded_at: new Date().toISOString(),
            source:    'tab5'
          };
          setBaseline(autoEntry);
          setLoadStatus(
            isDirty() ? LOAD_STATUS.DIRTY_WARNING : LOAD_STATUS.LOADED_TAB5
          );
          // STORAGE-FIX-002: Do NOT cache Tab5-sourced baselines in psp_exercise_baseline.
          // psp_calculation_baseline already holds scenario + results (~2 MB).
          // Duplicating into psp_exercise_baseline doubles the localStorage footprint,
          // leaving insufficient space for the next storeBaseline() call.
          // Tab5 baselines are always readable from psp_calculation_baseline directly;
          // the cache here serves only snapshot (non-Tab5) baselines which carry no results.
        }
      } catch (_err) {
        // calculationService unavailable or no baseline — stay at IDLE
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // OBS-FF-020 FIX: Tab 6 is CSS-mounted (display:none / block) — never unmounts.
  // The mount-time auto-load runs once and freezes. If Tab 5 recalculates after the user
  // has already visited Tab 6 (and the load failed or was skipped), the user must click
  // "Load Tab 5 Baseline" manually. This effect watches the isVisible prop (passed by App.jsx
  // as currentTab === 'tab6') and auto-loads a newly-available baseline whenever Tab 6
  // becomes visible and no baseline is currently loaded.
  //
  // BUG-FF-013 FIX: Also auto-reloads when loadStatus === STALE on re-navigation.
  // After Tab 5 RECALCULATE: storeBaseline() writes a new baseline with newCalculatedAt,
  // then onUpdate() sets scenario.calculated_at = newCalculatedAt. BUG-206 staleness
  // effect detects old_loaded_timestamp !== newCalculatedAt and sets STALE (even while
  // Tab 6 is invisible — CSS-mounted). When Tab 6 becomes visible the stored baseline is
  // already fresh. Auto-reload clears the spurious stale warning without user intervention.
  useEffect(() => {
    if (!isVisible) return;

    // STALE with an existing baseline: Tab 5 just recalculated — stored baseline is fresh.
    // Allow a reload to replace the stale loaded entry with the new one.
    const isStaleReload = loadStatus === LOAD_STATUS.STALE && baseline !== null;

    // BUG-S102-030 FIX: baseline.results===null means sessionStorage was empty when the
    // baseline was first loaded (page-load with no prior calculation, or Tab5 hadn't
    // recalculated yet). BUG-206 staleness detection short-circuits when
    // baseline.snapshot.calculated_at is null, so loadStatus never reaches STALE and
    // the isVisible guard "baseline !== null → return" permanently blocks re-hydration.
    // Re-hydrate on every Tab6 visit when results are absent — getBaseline() will pick up
    // whatever Tab5 has written to sessionStorage since the last load.
    const isUnhydrated = baseline !== null && baseline.results === null;

    if (!isStaleReload && !isUnhydrated) {
      if (baseline !== null) return;                 // already loaded, not stale — do not override
      if (loadStatus === LOAD_STATUS.LOADED_TAB5 ||
          loadStatus === LOAD_STATUS.LOADED_SNAP  ||
          loadStatus === LOAD_STATUS.DIRTY_WARNING) return;  // already in a good state
    }

    try {
      const stored = getBaseline();
      if (stored?.scenario) {
        const entry = {
          name:      stored.scenario?.operation_parameters?.operation_name ?? 'Baseline',
          snapshot:  deepCopy(stored.scenario),
          results:   stored.results ?? null,
          loaded_at: new Date().toISOString(),
          source:    'tab5'
        };
        setBaseline(entry);
        setLoadStatus(isDirty() ? LOAD_STATUS.DIRTY_WARNING : LOAD_STATUS.LOADED_TAB5);
        // STORAGE-FIX-002: Do NOT write Tab5 baseline to psp_exercise_baseline here.
        // See mount-effect comment above — this would duplicate 2 MB into a second key.
      }
    } catch (_err) {
      // calculationService unavailable — leave current status unchanged
    }
  }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  // BUG-206: Staleness detection — fires when Tab5 recalculates after baseline was loaded.
  // Compares scenario.calculated_at (current Tab5 result) vs baseline.snapshot.calculated_at
  // (the Tab5 result that was snapshotted into the baseline). If they differ, the baseline
  // is stale. Only applies to Tab5-sourced baselines (source === 'tab5') — snapshot baselines
  // have no calculated_at and are not subject to this check.
  useEffect(() => {
    if (!baseline || baseline.source !== 'tab5') return;
    const baselineCalcAt = baseline.snapshot?.calculated_at ?? null;
    const scenarioCalcAt = scenario?.calculated_at ?? null;
    if (!baselineCalcAt || !scenarioCalcAt) return;
    if (scenarioCalcAt !== baselineCalcAt) {
      setLoadStatus(LOAD_STATUS.STALE);
    }
  }, [scenario?.calculated_at, baseline]); // eslint-disable-line react-hooks/exhaustive-deps

  // BUG-COA-001 FIX: Tab6 is always-mounted (CSS hide/show). CoA slots read from
  // localStorage only once on mount — stale when Tab5 saves after Tab6 has mounted.
  // Listen for the custom 'psp:coaSaved' event dispatched by Tab5 doSaveAsCoa() to
  // refresh slot state immediately without requiring a remount or page refresh.
  useEffect(() => {
    const handleCoaSaved = (e) => {
      const { slot, payload } = e.detail ?? {};
      if (slot === 1 && payload) setCoaSlot1(payload);
      if (slot === 2 && payload) setCoaSlot2(payload);
    };
    window.addEventListener('psp:coaSaved', handleCoaSaved);
    return () => window.removeEventListener('psp:coaSaved', handleCoaSaved);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // -- flushState  -  called by App.jsx toolbar before tab navigation ----------
  useImperativeHandle(ref, () => ({
    flushState: () => {
      if (baseline) {
        try {
          // STORAGE-FIX-002: Tab5-sourced baselines already have their results in
          // psp_calculation_baseline. Storing results again in psp_exercise_baseline
          // doubles the ~2 MB footprint and triggers quota failures on subsequent
          // storeBaseline() calls. Write only the metadata + snapshot; results are
          // always re-hydrated from psp_calculation_baseline on next mount.
          const baselineToFlush = baseline.source === 'tab5'
            ? { ...baseline, results: null }
            : baseline;
          lsWrite(EXERCISE_STORAGE_KEYS.BASELINE, baselineToFlush);
        } catch (_err) {
          // storage write failed  -  non-fatal
        }
      }
      if (approvalEvents.length > 0) {
        try {
          lsWrite(EXERCISE_STORAGE_KEYS.EVENTS, approvalEvents);
        } catch (_err) {
          // storage write failed  -  non-fatal
        }
      }
      // BUG-84-017: persist baseline approval flag
      try {
        lsWrite(EXERCISE_STORAGE_KEYS.BASELINE_APPROVED, baselineApproved);
      } catch (_err) { /* non-fatal */ }
      // CoA approval state persist
      try {
        lsWrite(EXERCISE_STORAGE_KEYS.APPROVED_COA_SLOT, approvedCoASlot);
        lsWrite(EXERCISE_STORAGE_KEYS.APPROVED_VARIANT_ID, approvedVariantId);
      } catch (_err) { /* non-fatal */ }
      // Phase 2: persist variants
      if (variants.length > 0) {
        try {
          const ids = variants.map(v => v.id);
          lsWrite(EXERCISE_STORAGE_KEYS.VARIANT + 'index', ids);
          for (const v of variants) {
            lsWrite(EXERCISE_STORAGE_KEYS.VARIANT + v.id, v);
          }
        } catch (_err) {
          // storage write failed  -  non-fatal
        }
      }
    }
  }));

  // -- Load handlers ----------------------------------------------------------

  const handleLoadFromTab5 = useCallback(() => {
    const stored = getBaseline();
    if (!stored || !stored.scenario) {
      setLoadStatus(LOAD_STATUS.NO_BASELINE);
      // BUG-6A-001: surface failure inline below the button, not only as a status alert
      setTab5LoadError('No Tab 5 baseline found in storage. Go to Tab 5, click Calculate, then return here.');
      return;
    }
    setTab5LoadError(''); // clear any previous error
    if (isDirty()) {
      setLoadStatus(LOAD_STATUS.DIRTY_WARNING);
      // Still allow load  -  user is warned, not blocked
    }
    const entry = {
      name:      stored.scenario?.operation_parameters?.operation_name ?? 'Baseline',
      snapshot:  deepCopy(stored.scenario),
      results:   stored.results ?? null,
      loaded_at: new Date().toISOString(),
      source:    'tab5'
    };
    setBaseline(entry);
    if (!isDirty()) setLoadStatus(LOAD_STATUS.LOADED_TAB5);
    try {
      // STORAGE-FIX-002: store only metadata (results: null) — full results stay in
      // psp_calculation_baseline. Mount effect re-hydrates results on next page load.
      lsWrite(EXERCISE_STORAGE_KEYS.BASELINE, { ...entry, results: null });
    } catch (_err) {
      // Non-fatal
    }
  }, []);

  const handleLoadSnapshot = useCallback(() => {
    if (!scenario) {
      setLoadStatus(LOAD_STATUS.NO_BASELINE);
      return;
    }
    const entry = {
      name:      scenario?.operation_parameters?.operation_name
                   ?? 'Current Configuration',
      snapshot:  deepCopy(scenario),
      results:   null,
      loaded_at: new Date().toISOString(),
      source:    'snapshot'
    };
    setBaseline(entry);
    setLoadStatus(LOAD_STATUS.LOADED_SNAP);
    lsWrite(EXERCISE_STORAGE_KEYS.BASELINE, entry);
  }, [scenario]);

  const handleClearBaseline = useCallback(() => {
    setBaseline(null);
    setLoadStatus(LOAD_STATUS.IDLE);
    setApprovalEvents([]);
    setEventForm(EMPTY_EVENT_FORM);
    setEventFormError('');
    // Phase 2: also clear variants
    setVariants([]);
    setActiveVariantId(null);
    setQmValues({ ...EMPTY_QM_VALUES });
    setVariantName('');
    setCalcError('');
    lsRemove(EXERCISE_STORAGE_KEYS.BASELINE);
    lsRemove(EXERCISE_STORAGE_KEYS.EVENTS);
  }, []);

  // -- CoA Approval Handlers (Steps 1+2) ------------------------------------

  /** Write the approved plan to localStorage (scenario) + sessionStorage (results). */
  const writeApprovedPlan = (coaData) => {
    const snapshot = coaData?.snapshot ?? null;
    const results  = coaData?.results  ?? null;
    if (!snapshot) return;
    try {
      lsWrite(APPROVED_PLAN_KEY, {
        ...snapshot,
        results:     null,
        source:      'approved',
        approved_at: Date.now(),
      });
    } catch (_e) { /* non-fatal */ }
    try {
      if (results) sessionStorage.setItem(APPROVED_PLAN_RESULTS_KEY, JSON.stringify(results));
    } catch (_e) { /* sessionStorage quota — non-fatal */ }
    // Fire event so Tab 7 picks up the new approved baseline live
    try {
      window.dispatchEvent(new CustomEvent('psp:planApproved', { detail: { approved_at: Date.now() } }));
    } catch (_e) { /* non-fatal */ }
  };

  /** Commit approval without confirmation dialog. */
  const handleCommitApproval = (slot, variantId) => {
    setApprovedCoASlot(slot);
    setApprovedVariantId(variantId ?? null);
    lsWrite(EXERCISE_STORAGE_KEYS.APPROVED_COA_SLOT, slot);
    lsWrite(EXERCISE_STORAGE_KEYS.APPROVED_VARIANT_ID, variantId ?? null);
    const coaData = variantId
      ? variants.find(v => v.id === variantId)
      : slot === 1 ? coaSlot1 : coaSlot2;
    writeApprovedPlan(coaData ?? {});
    const coaLabel = slot === 1 ? (coaSlot1?.name || 'CoA A') : (coaSlot2?.name || 'CoA B');
    const varLabel = variantId ? (variants.find(v => v.id === variantId)?.name || 'variant') : null;
    setSnackbar({ open: true, message: `Approved: ${varLabel ? `${coaLabel} / ${varLabel}` : coaLabel}`, severity: 'success' });
    setReApproveDialog({ open: false, pendingSlot: null, pendingVariantId: null });
  };

  /**
   * Approve CoA slot (1 or 2) with optional variant. Shows re-approval dialog if
   * a planning basis is already set and the new selection differs.
   */
  const handleApproveCoA = (slot, variantId = null) => {
    const alreadyApproved = approvedCoASlot !== null || variants.some(v => v.approved) || baselineApproved;
    if (alreadyApproved && (slot !== approvedCoASlot || variantId !== approvedVariantId)) {
      setReApproveDialog({ open: true, pendingSlot: slot, pendingVariantId: variantId });
      return;
    }
    handleCommitApproval(slot, variantId);
  };

  // -- ENH-013: Approval event handlers --------------------------------------

  const handleEventFormChange = useCallback((field, value) => {
    setEventForm(prev => ({ ...prev, [field]: value }));
    setEventFormError('');
  }, []);

  const handleAddEvent = useCallback(() => {
    const day    = parseInt(eventForm.day, 10);
    const opDays = baseline?.snapshot?.operation_parameters?.duration_days ?? 99;

    if (!eventForm.day || isNaN(day) || day < 1 || day > opDays) {
      setEventFormError(`Day must be an integer between 1 and ${opDays}`);
      return;
    }
    if (!eventForm.requesting_unit.trim()) {
      setEventFormError('Requesting unit is required');
      return;
    }
    if (!eventForm.providing_unit.trim()) {
      setEventFormError('Providing unit is required');
      return;
    }

    const newEvent = {
      id:              `evt_${Date.now()}`,
      day,
      event_type:      eventForm.event_type,
      requesting_unit: eventForm.requesting_unit.trim(),
      providing_unit:  eventForm.providing_unit.trim(),
      notes:           eventForm.notes.trim(),
      created_at:      new Date().toISOString()
    };

    setApprovalEvents(prev => {
      const updated = [...prev, newEvent].sort((a, b) => a.day - b.day);
      lsWrite(EXERCISE_STORAGE_KEYS.EVENTS, updated);
      return updated;
    });
    setEventForm(EMPTY_EVENT_FORM);
    setEventFormError('');
  }, [eventForm, baseline]);

  const handleDeleteEvent = useCallback((id) => {
    setApprovalEvents(prev => {
      const updated = prev.filter(e => e.id !== id);
      lsWrite(EXERCISE_STORAGE_KEYS.EVENTS, updated);
      return updated;
    });
  }, []);

  // -- Derived values ---------------------------------------------------------
  const baselineSummary = extractBaselineSummary(baseline?.results);
  const isLoaded = loadStatus === LOAD_STATUS.LOADED_TAB5
                || loadStatus === LOAD_STATUS.LOADED_SNAP
                || loadStatus === LOAD_STATUS.DIRTY_WARNING
                || loadStatus === LOAD_STATUS.STALE;

  // ============================================================================
  // ENH-030: REPORT GENERATION (moved from Tab 5)
  // Data source: baseline.snapshot (scenario) + baseline.results (calculations)
  // ============================================================================

  const generateReportData = useCallback((reportId) => {
    const reportConfig = Object.values(REPORT_TYPES).find(r => r.id === reportId);
    if (!reportConfig) throw new Error(`Unknown report type: ${reportId}`);
    const snap = baseline?.snapshot;
    // BUG-RPT-001: v3 compatibility shims for formatters
    // Tab1 saves force.corps.name / force.totalStrength (not force.designation / force.total_personnel)
    // Tab3 saves mtf_structure.role2/.role3 (not role2_basic/role2_enhanced/role3 at top level)
    // v3 results do not include duration_days (it lives in operation_parameters)
    const scenarioWithResults = {
      ...snap,
      force: {
        ...snap?.force,
        designation: snap?.force?.corps?.name ||
                     snap?.force?.division?.name ||
                     snap?.force?.brigades?.[0]?.name ||
                     [snap?.force?.nationalStandard, snap?.force?.echelon].filter(Boolean).join(' ') ||
                     'Unknown Unit',
        total_personnel: snap?.force?.totalStrength || snap?.force?.total_personnel || 0
      },
      calculation_results: {
        ...baseline?.results,
        duration_days: snap?.operation_parameters?.duration_days ||
                       baseline?.results?.scenario_summary?.duration_days
      },
      casualty_projections: baseline?.results
    };
    const reportPayload = reportConfig.formatter(scenarioWithResults);
    // BUG-6K-001: Stamp all reports with a disclaimer when no calculated results
    if (!baseline?.results && reportPayload?.metadata) {
      reportPayload.metadata.classification =
        (reportPayload.metadata.classification || 'NATO UNCLASSIFIED') +
        ' ⚠ PRELIMINARY — NO CALCULATED RESULTS: All casualty fields are zero. ' +
        'Run Tab 5 Calculate and reload baseline before issuing this report.';
    }
    return reportPayload;
  }, [baseline]);

  const handleExport = useCallback(async (reportId, format) => {
    setIsExporting(true);
    try {
      const reportData = generateReportData(reportId);
      let blob;
      switch (format) {
        case 'html':  blob = await exportToHTML(reportData);  break;
        case 'pdf':   blob = await exportToPDF(reportData);   break;
        case 'docx':  blob = await exportToDOCX(reportData);  break;
        case 'xlsx':  blob = await exportToXLSX(reportData);  break;
        case 'json':  blob = await exportToJSON(reportData);  break;
        default: throw new Error(`Unsupported format: ${format}`);
      }
      const filename = generateFilename(reportData, format);
      downloadBlob(blob, filename);
      setSnackbar({ open: true, message: `${REPORT_TYPES[reportId.toUpperCase()]?.name || reportId} exported as ${format.toUpperCase()}`, severity: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      setSnackbar({ open: true, message: `Export failed: ${error.message}`, severity: 'error' });
    } finally {
      setIsExporting(false);
    }
  }, [generateReportData]);

  const handleExportSelected = useCallback(async () => {
    setIsExporting(true);
    let successCount = 0;
    let failCount = 0;
    for (const reportId of selectedReports) {
      try {
        const reportData = generateReportData(reportId);
        let blob;
        switch (selectedFormat) {
          case 'html':  blob = await exportToHTML(reportData);  break;
          case 'pdf':   blob = await exportToPDF(reportData);   break;
          case 'docx':  blob = await exportToDOCX(reportData);  break;
          case 'xlsx':  blob = await exportToXLSX(reportData);  break;
          case 'json':  blob = await exportToJSON(reportData);  break;
          default: throw new Error(`Unsupported format: ${selectedFormat}`);
        }
        const filename = generateFilename(reportData, selectedFormat);
        downloadBlob(blob, filename);
        successCount++;
        // BUG-84-018: browsers silently discard simultaneous saveAs calls;
        // stagger each download by 400 ms so the browser registers each one
        await new Promise(resolve => setTimeout(resolve, 400));
      } catch (err) {
        console.error(`Export failed for ${reportId}:`, err);
        failCount++;
      }
    }
    setIsExporting(false);
    setSnackbar({
      open: true,
      message: failCount === 0
        ? `${successCount} report(s) exported successfully`
        : `${successCount} exported, ${failCount} failed`,
      severity: failCount === 0 ? 'success' : 'warning'
    });
  }, [selectedReports, selectedFormat, generateReportData]);

  // IMP-002: Merge all selected reports into one PDF and download as a single file
  const handleExportMergedPDF = useCallback(async () => {
    if (selectedReports.length < 2) return;
    setIsExporting(true);
    try {
      const reportsData = selectedReports.map(id => generateReportData(id));
      const blob = await exportToPDFMerged(reportsData);
      const opName = (baseline?.snapshot?.scenario_name || 'OP').replace(/\s+/g, '_').toUpperCase();
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      downloadBlob(blob, `PSP_CRE_${opName}_MedicalPackage_${date}.pdf`);
      setSnackbar({
        open: true,
        message: `${selectedReports.length} reports merged into one PDF`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Merged PDF export failed:', err);
      setSnackbar({ open: true, message: `Merged PDF failed: ${err.message}`, severity: 'error' });
    } finally {
      setIsExporting(false);
    }
  }, [selectedReports, generateReportData, baseline]);

  const handlePreview = useCallback(async (reportId) => {
    try {
      const reportData = generateReportData(reportId);
      const htmlBlob = await exportToHTML(reportData);
      const html = await htmlBlob.text();
      setPreviewContent(html);
      setPreviewReportId(reportId);
      setPreviewDialogOpen(true);
    } catch (error) {
      setSnackbar({ open: true, message: `Preview failed: ${error.message}`, severity: 'error' });
    }
  }, [generateReportData]);

  // ============================================================================
  // ENH-030: RENDER: Formal Reports Section
  // Only visible when baseline is loaded with results
  // ============================================================================

  const renderReportsSection = () => {
    if (!isLoaded) return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        {/* BUG-6G-001 FIX: was "6" — shifted to 7 after cascade renumber. */}
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArticleIcon color="primary" />
          {t('tab6.formal_reports_export')}
        </Typography>
        <Alert severity="info">
          {t('tab6.formal_reports_export')}
        </Alert>
      </Paper>
    );

    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        {/* BUG-6G-001 FIX: was "6" — shifted to 7. */}
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArticleIcon color="primary" />
          {t('tab6.formal_reports')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('tab6.formal_reports_subtitle')}
        </Typography>
        {/* TAB7-REPORTS-MOVE: note moved reports */}
        <Alert severity="info" icon={false} sx={{ mb: 2, py: 0.5 }}>
          {t('tab6.realtime_reports_note')}
        </Alert>

        {/* BUG-6K-001: block export when no results — zero-casualty document is actively misleading */}
        {!baseline?.results && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>{t('tab6.export_blocked')}</AlertTitle>
            {t('tab6.no_calc_results_export_detail')}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Object.values(REPORT_TYPES).filter(r => !r.movedToTab7).map((report) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={report.id}>
              <Card
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  bgcolor: selectedReports.includes(report.id) ? 'action.selected' : 'background.paper',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
                onClick={() => {
                  setSelectedReports(prev =>
                    prev.includes(report.id)
                      ? prev.filter(r => r !== report.id)
                      : [...prev, report.id]
                  );
                }}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Checkbox
                    checked={selectedReports.includes(report.id)}
                    onChange={() => {}}
                    size="small"
                  />
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      {report.icon}
                      <Typography variant="subtitle2">{report.name}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {report.description}
                    </Typography>
                    <Chip label={report.stanag} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                  </Box>
                  <Tooltip title={!baseline?.results ? t('tab6.preview_tooltip_no_results') : t('tab6.preview')}>
                    <span>
                      <Button
                        size="small"
                        disabled={!baseline?.results}
                        onClick={(e) => { e.stopPropagation(); handlePreview(report.id); }}
                      >
                        {t('tab6.preview')}
                      </Button>
                    </span>
                  </Tooltip>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('tab6.format')}</InputLabel>
            <Select
              value={selectedFormat}
              label={t('tab6.format')}
              onChange={(e) => setSelectedFormat(e.target.value)}
            >
              {EXPORT_FORMATS.map((fmt) => (
                <MenuItem key={fmt.id} value={fmt.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {fmt.icon}
                    {fmt.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={handleExportSelected}
            disabled={selectedReports.length === 0 || isExporting || !baseline?.results}
          >
            {isExporting ? t('tab6.exporting') : t('tab6.export_reports', { count: selectedReports.length })}
          </Button>

          {/* IMP-002: Merge to single PDF — only when PDF format and ≥2 reports selected */}
          {selectedFormat === 'pdf' && selectedReports.length >= 2 && (
            <Tooltip title={t('tab6.merge_to_pdf_tooltip', { count: selectedReports.length })}>
              <span>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <LayersIcon />}
                  onClick={handleExportMergedPDF}
                  disabled={isExporting || !baseline?.results}
                >
                  {t('tab6.merge_to_pdf')}
                </Button>
              </span>
            </Tooltip>
          )}

          <Typography variant="caption" color="text.secondary">
            {t('tab6.reports_selected', { count: selectedReports.length })}
          </Typography>
        </Box>
      </Paper>
    );
  };

  // -- Sub-renderers ----------------------------------------------------------

  function renderStatusAlerts() {
    if (loadStatus === LOAD_STATUS.NO_BASELINE) {
      // BUG-6A-002: mention storage limit as a possible cause, not just "not calculated yet"
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>{t('tab6.no_baseline_found')}</AlertTitle>
          <Box component="ol" sx={{ mt: 0.5, mb: 0.5, pl: 2 }}>
            <li>{t('tab6.no_baseline_found_detail_1')}</li>
            <li>{t('tab6.no_baseline_found_detail_2')}</li>
          </Box>
        </Alert>
      );
    }
    if (loadStatus === LOAD_STATUS.DIRTY_WARNING) {
      return (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningAmberIcon />}>
          <AlertTitle>{t('tab6.unsaved_changes')}</AlertTitle>
          {t('tab6.dirty_warning_detail')}
        </Alert>
      );
    }
    // BUG-206: Tab5 recalculated after this baseline was loaded — snapshot is stale.
    if (loadStatus === LOAD_STATUS.STALE) {
      return (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          icon={<WarningAmberIcon />}
          action={
            <Button color="inherit" size="small" onClick={handleLoadFromTab5}>
              {t('tab6.reload_baseline')}
            </Button>
          }
        >
          <AlertTitle>{t('tab6.baseline_out_of_date')}</AlertTitle>
          {t('tab6.stale_detail')}
        </Alert>
      );
    }
    return null;
  }


  // -- Phase 2: Variant handlers ----------------------------------------------

  const activeVariant = useMemo(
    () => variants.find(v => v.id === activeVariantId) ?? null,
    [variants, activeVariantId]
  );

  const handleCreateVariant = useCallback(() => {
    if (!baseline) return;
    if (variants.length >= MAX_VARIANTS) return;
    const name = variantName.trim() || `Variant ${variants.length + 1}`;
    const v = makeEmptyVariant(name);
    const updated = [...variants, v];
    setVariants(updated);
    setActiveVariantId(v.id);
    setVariantName('');
    setQmValues({ ...EMPTY_QM_VALUES });
    setBrigadeOverrides({});  // ENH-031: reset per-brigade overrides for new variant
    setCalcError('');
  }, [baseline, variants, variantName]);

  const handleDeleteVariant = useCallback((id) => {
    setVariants(prev => {
      const updated = prev.filter(v => v.id !== id);
      lsRemove(EXERCISE_STORAGE_KEYS.VARIANT + id);
      if (updated.length > 0) {
        lsWrite(EXERCISE_STORAGE_KEYS.VARIANT + 'index', updated.map(v => v.id));
      } else {
        lsRemove(EXERCISE_STORAGE_KEYS.VARIANT + 'index');
      }
      return updated;
    });
    setActiveVariantId(prev => (prev === id
      ? (variants.find(v => v.id !== id)?.id ?? null)
      : prev));
  }, [variants]);

  const handleSelectVariant = useCallback((id) => {
    const v = variants.find(x => x.id === id);
    if (!v) return;
    setActiveVariantId(id);
    setQmValues({ ...EMPTY_QM_VALUES, ...(v.qm_values ?? {}) });
    setBrigadeOverrides({ ...(v.brigade_overrides ?? {}) });  // ENH-031: restore brigade overrides
    setCalcError('');
  }, [variants]);

  const handleQmChange = useCallback((field, value) => {
    setQmValues(prev => ({ ...prev, [field]: value }));
  }, []);

  // ENH-031: per-brigade state override handler
  const handleBrigadeOverrideChange = useCallback((brigadeId, newState) => {
    setBrigadeOverrides(prev => {
      if (!newState) {
        const next = { ...prev };
        delete next[brigadeId];
        return next;
      }
      return { ...prev, [brigadeId]: newState };
    });
  }, []);

  const handleApplyModifications = useCallback(() => {
    if (!baseline || !activeVariantId) return;
    const modSummary = buildModificationSummary(qmValues);
    // ENH-031: apply brigade overrides after QM modifications
    const brigadeMods = buildBrigadeModSummary(brigadeOverrides, getSnapshotBrigades(baseline.snapshot));
    let variantSnapshot = applyQMModifications(baseline.snapshot, qmValues);
    variantSnapshot = applyBrigadeStateOverrides(variantSnapshot, brigadeOverrides);
    setVariants(prev => prev.map(v => {
      if (v.id !== activeVariantId) return v;
      return {
        ...v,
        qm_values:        { ...qmValues },
        brigade_overrides: { ...brigadeOverrides },   // ENH-031: persist brigade overrides in variant
        modifications:    [...modSummary, ...brigadeMods],
        snapshot:         variantSnapshot,
        results:          null,       // results invalidated by modification change
        calculated_at:    null
      };
    }));
    setCalcError('');
  }, [baseline, activeVariantId, qmValues, brigadeOverrides]);

  const handleRecalculate = useCallback(() => {
    const v = variants.find(x => x.id === activeVariantId);
    if (!v || !v.snapshot) return;
    setIsCalculating(true);
    setCalcError('');
    calcAbortRef.current = false;

    // Run synchronously in a setTimeout so React can render the loading state first
    setTimeout(() => {
      try {
        if (calcAbortRef.current) return;
        const results = calculateCasualties(v.snapshot);
        setVariants(prev => prev.map(x => {
          if (x.id !== activeVariantId) return x;
          const updated = { ...x, results, calculated_at: new Date().toISOString() };
          lsWrite(EXERCISE_STORAGE_KEYS.VARIANT + x.id, updated);
          return updated;
        }));
      } catch (err) {
        setCalcError(err.message || 'Calculation failed');
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [variants, activeVariantId]);

  const handleDuplicateVariant = useCallback((id) => {
    if (variants.length >= MAX_VARIANTS) return;
    const src = variants.find(v => v.id === id);
    if (!src) return;
    const copy = {
      ...JSON.parse(JSON.stringify(src)),
      id:         'variant_' + Date.now(),
      name:       src.name + ' (copy)',
      created_at: new Date().toISOString(),
      calculated_at: null,
      results:    null
    };
    const updated = [...variants, copy];
    setVariants(updated);
    setActiveVariantId(copy.id);
    setQmValues({ ...EMPTY_QM_VALUES, ...(copy.qm_values ?? {}) });
  }, [variants]);

  function renderLoaderSection() {
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FolderOpenIcon color="primary" />
          <Typography variant="h6">{t('tab6.load_baseline')}</Typography>
          {/* BUG-6D-002: badge reflects RESULT presence, not load path.
              LOADED_TAB5 with sessionStorage cleared → results null → must show warning, not green. */}
          {isLoaded && !baseline?.results && (
            <Chip
              icon={<WarningAmberIcon />}
              label={t('tab6.config_snapshot_no_results')}
              color="warning"
              size="small"
            />
          )}
          {isLoaded && baseline?.results && (
            <Chip
              icon={<DownloadDoneIcon />}
              label={t('tab6.baseline_loaded_chip')}
              color="success"
              size="small"
            />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {t('tab6.baseline_immutable_note')}
        </Typography>

        <Grid container spacing={2}>
          {/* Path A  -  Tab 5 baseline (recommended) */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper
              variant="outlined"
              sx={{ p: 2, height: '100%', borderColor: 'primary.main', borderWidth: 1.5 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <DownloadDoneIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2">{t('tab6.load_from_tab5')}</Typography>
                <Chip label={t('tab6.recommended')} color="primary" size="small" />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {t('tab6.load_from_tab5_subtitle')}
              </Typography>
              <Tooltip
                title={!hasBaseline()
                  ? t('tab6.load_tab5_no_baseline_tooltip')
                  : ''}
                arrow
              >
                <span style={{ display: 'block' }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DownloadDoneIcon />}
                    onClick={handleLoadFromTab5}
                    fullWidth
                    disabled={!hasBaseline()}
                  >
                    {t('tab6.load_tab5_baseline_btn')}
                  </Button>
                </span>
              </Tooltip>
              {/* BUG-6A-001: inline error directly below the button — user sees it without scrolling */}
              {tab5LoadError && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setTab5LoadError('')}>
                  {tab5LoadError}
                </Alert>
              )}
            </Paper>
          </Grid>

          {/* Path B  -  current configuration snapshot */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FolderOpenIcon color="action" fontSize="small" />
                <Typography variant="subtitle2">{t('tab6.load_current_config')}</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {t('tab6.load_current_config_subtitle')}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FolderOpenIcon />}
                onClick={handleLoadSnapshot}
                disabled={!scenario}
                fullWidth
              >
                {t('tab6.load_current_config')}
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    );
  }

  function renderBaselinePanel() {
    if (!baseline) return null;

    const snap             = baseline.snapshot ?? {};
    const opParams         = snap.operation_parameters ?? {};
    const nationalStandard = snap.force?.nationalStandard
                              ?? opParams.national_standard
                              ?? '\u2014';
    const duration         = opParams.duration_days ?? null;
    // BUG-147 FIX (v3.5.1): brigade-sum fallback when direct paths absent
    // BUG-170 FIX (v3.5.4): forceBuilderResult.total_strength was missing from chain.
    //   ForceService.buildDivision() writes total_strength = brigades + division support troops.
    //   ForceService.buildCorps() writes total_strength = corps HQ + div HQ + brigades + div support + corps support.
    //   Tab5 reads forceBuilderResult.total_strength directly (= 14,000 full force).
    //   Tab6 was never checking that path — fell through to brigade-sum (= 11,600, support excluded).
    //   Fix: insert forceBuilderResult.total_strength before the corps sub-paths (which are structurally wrong:
    //   force.corps does not exist; forceBuilderResult.corps is the HQ class instance, not a summary).
    const totalForce = (
      snap.force?.total_strength
      ?? snap.force?.corps?.total_strength
      ?? snap.force?.forceBuilderResult?.total_strength
      ?? snap.force?.forceBuilderResult?.corps?.total_strength
      ?? snap.force?.forceBuilderResult?.corps?.strength
      ?? (Array.isArray(snap.force?.brigades) && snap.force.brigades.length > 0
          ? snap.force.brigades.reduce(
              (sum, b) => sum + (b.strength ?? b.personnel ?? b.total_strength ?? 0), 0)
          : null)
    );
    const opName           = opParams.operation_name ?? baseline.name;
    const threatLevelKey   = opParams.threat_level   ?? opParams.threatLevel ?? null;
    const threatLevel      = THREAT_LEVELS[threatLevelKey]?.label ?? threatLevelKey;

    return (
      <Card elevation={3} sx={{ mb: 3, border: 2, borderColor: 'success.main' }}>
        <CardHeader
          avatar={<LockIcon color="success" />}
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6">{opName}</Typography>
              <Chip
                label={baseline.source === 'tab5' ? t('tab6.tab5_baseline_chip') : t('tab6.config_snapshot_chip')}
                color={baseline.source === 'tab5' ? 'success' : 'info'}
                size="small"
              />
              {baseline.source === 'snapshot' && (
                <Chip
                  label={t('tab6.no_calculated_results_chip')}
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          }
          subheader={
            <Typography variant="caption" color="text.secondary">
              {t('tab6.loaded_label')} {new Date(baseline.loaded_at).toLocaleString()}&nbsp;&mdash;&nbsp;
              {t('tab6.baseline_immutable_subheader')}
            </Typography>
          }
          action={
            <Tooltip title={t('tab6.clear_baseline_tooltip')}>
              <IconButton size="small" color="error" onClick={handleClearBaseline}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        />
        <CardContent>
          {/* Scenario parameters row */}
          <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {t('tab6.scenario_parameters')}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <Typography variant="caption" color="text.secondary">{t('tab6.national_standard')}</Typography>
              <Typography variant="body2" fontWeight="bold">{nationalStandard}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <Typography variant="caption" color="text.secondary">{t('tab6.operation_duration')}</Typography>
              <Typography variant="body2" fontWeight="bold">{fmt(duration, ' days')}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <Typography variant="caption" color="text.secondary">{t('tab6.total_force')}</Typography>
              <Typography variant="body2" fontWeight="bold">
                {totalForce ? totalForce.toLocaleString() : '\u2014'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <Typography variant="caption" color="text.secondary">{t('tab6.threat_level')}</Typography>
              <Typography variant="body2" fontWeight="bold">{fmt(threatLevel)}</Typography>
            </Grid>
          </Grid>

          {/* Calculator results  -  only when source = tab5 and results present */}
          {baselineSummary && (
            <>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {t('tab6.calculated_results_tab5')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">{t('tab6.total_casualties')}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {fmt(baselineSummary.total_casualties)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">{t('tab6.kia')}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {fmt(baselineSummary.kia)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">{t('tab6.wia')}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {fmt(baselineSummary.wia)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">{t('tab6.dnbi')}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {fmt(baselineSummary.dnbi)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">{t('tab6.rtd')}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {fmt(baselineSummary.rtd)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">{t('tab6.golden_hour_label')}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {fmt(baselineSummary.golden_hour_pct, '%')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">{t('tab6.blood_stockout_label')}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {baselineSummary.blood_stockout_day
                      ? `Day ${baselineSummary.blood_stockout_day}`
                      : '\u2014'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">{t('tab6.peak_r2b_label')}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {fmt(baselineSummary.peak_r2b_pct, '%')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">{t('tab6.r2b_mascal_label')}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {fmt(baselineSummary.r2b_mascal_days)}
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}

          {/* Snapshot-only advisory */}
          {baseline.source === 'snapshot' && (
            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2 }}>
              {t('tab6.snapshot_only_advisory')}
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  }

  // ENH-031: Per-brigade operational state selector
  // Renders a compact grid of brigades allowing per-brigade state override.
  // Each override bakes into the variant snapshot via applyBrigadeStateOverrides().
  function renderPerBrigadeConfig() {
    // Mirror calculator's brigade lookup (all three echelon paths)
    const brigades = getSnapshotBrigades(baseline?.snapshot);
    if (brigades.length === 0) return null;

    // BUG-6F-001 FIX: extract national standard per brigade to show national identity
    const snap = baseline?.snapshot;
    const brigStds   = snap?.force?.brigadeStandards ?? {};
    const fallbackNat = snap?.force?.nationalStandard ?? 'NATO';

    const overrideCount = Object.keys(brigadeOverrides).length;

    return (
      <Box sx={{ mt: 2, mb: 1 }}>
        <Divider sx={{ mb: 1.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="caption" fontWeight="bold" color="text.secondary">
            {t('tab6.per_brigade_state')}
          </Typography>
          {overrideCount > 0 && (
            <Chip
              label={`${overrideCount} override${overrideCount > 1 ? 's' : ''}`}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {t('tab6.per_brigade_override_note')}
        </Typography>
        <Grid container spacing={1}>
          {brigades.map(brigade => {
            const baselineState = brigade.operational_state || 'COMBAT_ENGAGED';
            const baselineLabel = OPERATIONAL_STATES[baselineState]?.shortLabel || baselineState;
            const override = brigadeOverrides[brigade.id] || '';
            const isOverridden = !!override;
            const displayColor = isOverridden
              ? (override === 'COMBAT_ENGAGED' ? 'error' : override === 'SUPPORT_ACTIVE' ? 'warning' : 'success')
              : 'default';
            // BUG-6F-001 FIX: national identity chip — shows national code (ROU/FRA/DEU/NATO) per brigade
            const natCode = brigade.nationalStandard ?? brigStds[brigade.id] ?? fallbackNat;
            const natLabel = AVAILABLE_NATIONAL_STANDARDS.find(n => n.code === natCode)?.code ?? natCode;

            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={brigade.id}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  p: 0.75,
                  border: '1px solid',
                  borderColor: isOverridden ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  bgcolor: isOverridden ? 'action.hover' : 'transparent'
                }}>
                  {/* BUG-6F-001: national identity chip */}
                  <Chip
                    label={natLabel}
                    size="small"
                    variant="outlined"
                    sx={{ height: 16, fontSize: '0.55rem', minWidth: 32, px: 0.25, flexShrink: 0,
                          borderColor: 'text.disabled', color: 'text.secondary' }}
                  />
                  <Typography
                    variant="caption"
                    fontWeight={isOverridden ? 'bold' : 'normal'}
                    sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={brigade.name || brigade.id}
                  >
                    {brigade.name || brigade.id}
                  </Typography>
                  <Chip
                    label={isOverridden
                      ? OPERATIONAL_STATES[override]?.shortLabel || override
                      : baselineLabel}
                    size="small"
                    color={displayColor}
                    variant={isOverridden ? 'filled' : 'outlined'}
                    sx={{ height: 18, fontSize: '0.6rem', minWidth: 52 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <Select
                      value={override}
                      onChange={e => handleBrigadeOverrideChange(brigade.id, e.target.value)}
                      displayEmpty
                      sx={{ fontSize: '0.7rem', height: 28 }}
                    >
                      <MenuItem value="">
                        <em style={{ fontSize: '0.7rem' }}>{t('tab6.baseline_option')}</em>
                      </MenuItem>
                      {Object.values(OPERATIONAL_STATES).map(state => (
                        <MenuItem key={state.value} value={state.value} sx={{ fontSize: '0.75rem' }}>
                          {state.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  }

  function renderVariantScaffold() {
    // Phase 2: replaced by full variant manager
    return renderVariantManager();
  }

  function renderVariantManager() {
    const canAdd = isLoaded && variants.length < MAX_VARIANTS;

    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TuneIcon color="primary" />
            <Typography variant="h6">{t('tab6.variants')}</Typography>
            <Chip
              label={t('tab6.variants_used', { count: variants.length, max: MAX_VARIANTS })}
              size="small"
              color={variants.length >= MAX_VARIANTS ? 'error' : 'default'}
              variant="outlined"
            />
          </Box>
          {/* BUG-84-013 + BUG-84-014: subtitle + CoA context chip */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {/* BUG-NEXT-001: show placeholder when no baseline loaded */}
            {baseline?.name ? (
              <Chip
                size="small"
                label={`Analysing: ${baseline.name}`}
                color="info"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
              />
            ) : (
              <Chip
                size="small"
                label="No baseline saved"
                color="default"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.disabled' }}
              />
            )}
            <Typography variant="caption" color="text.secondary">
              Sensitivity analysis within this CoA — adjust single parameters to stress-test the estimate before Annex Q approval
            </Typography>
          </Box>
        </Box>

        {/* Create variant row */}
        {isLoaded && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              label={t('tab6.variant_name')}
              value={variantName}
              onChange={e => setVariantName(e.target.value)}
              placeholder={`Variant ${variants.length + 1}`}
              disabled={!canAdd}
              sx={{ flex: 1, maxWidth: 280 }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleCreateVariant}
              disabled={!canAdd}
            >
              Add Variant
            </Button>
            {variants.length >= MAX_VARIANTS && (
              <Typography variant="caption" color="error">
                Maximum {MAX_VARIANTS} variants reached
              </Typography>
            )}
          </Box>
        )}

        {!isLoaded && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Load a baseline (Section 1) to create variants.
          </Typography>
        )}

        {/* Variant selector chips */}
        {variants.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {variants.map(v => (
              <Chip
                key={v.id}
                label={
                  <span>
                    {v.name}
                    {v.calculated_at ? ' [calc]' : v.snapshot ? ' [mod]' : ' [new]'}
                  </span>
                }
                onClick={() => handleSelectVariant(v.id)}
                onDelete={() => handleDeleteVariant(v.id)}
                color={activeVariantId === v.id ? 'primary' : 'default'}
                variant={activeVariantId === v.id ? 'filled' : 'outlined'}
                size="small"
              />
            ))}
          </Box>
        )}

        {/* Active variant editor */}
        {activeVariant && (
          <Box sx={{ mt: 1 }}>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TuneIcon fontSize="small" color="action" />
              <Typography variant="subtitle2">
                Quick Modifications  -  <strong>{activeVariant.name}</strong>
              </Typography>
              <Tooltip title={t('tab6.duplicate_variant_tooltip')}>
                <IconButton
                  size="small"
                  onClick={() => handleDuplicateVariant(activeVariant.id)}
                  disabled={variants.length >= MAX_VARIANTS}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              All values are deltas from the baseline. Zero means no change.
              The baseline snapshot is never modified  -  modifications apply only to this variant.
            </Typography>

            {/* QM sliders */}
            <Grid container spacing={3} sx={{ mb: 2 }}>
              {QM_CATALOG.map(qm => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={qm.id}>
                  <Box sx={{ px: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" fontWeight="bold">
                        {qm.label}
                      </Typography>
                      <Chip
                        label={`${qmValues[qm.field] >= 0 ? '+' : ''}${qmValues[qm.field]} ${qm.unit}`}
                        size="small"
                        color={qmValues[qm.field] !== 0 ? 'primary' : 'default'}
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    </Box>
                    <Slider
                      size="small"
                      value={qmValues[qm.field]}
                      min={qm.min}
                      max={qm.max}
                      step={qm.step}
                      marks={[{ value: 0, label: '0' }]}
                      onChange={(_e, val) => handleQmChange(qm.field, val)}
                      valueLabelDisplay="auto"
                      valueLabelFormat={v => `${v >= 0 ? '+' : ''}${v}`}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                      {qm.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* ENH-031: Per-brigade operational state */}
            {renderPerBrigadeConfig()}

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleApplyModifications}
                disabled={!baseline}
              >
                Apply Modifications
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={isCalculating
                  ? <CircularProgress size={14} color="inherit" />
                  : <PlayArrowIcon />}
                onClick={handleRecalculate}
                disabled={isCalculating || !activeVariant?.snapshot}
                color="success"
              >
                {isCalculating ? 'Calculating...' : 'Recalculate'}
              </Button>
              <Button
                variant="text"
                size="small"
                color="inherit"
                onClick={() => { setQmValues({ ...EMPTY_QM_VALUES }); setBrigadeOverrides({}); }}
              >
                Reset all
              </Button>

              {/* Modification summary */}
              {activeVariant.modifications?.length > 0 && (
                <Box sx={{ ml: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {activeVariant.modifications.map((m, i) => (
                    <Chip key={i} label={m} size="small" color="primary" variant="outlined"
                      sx={{ fontSize: '0.65rem' }} />
                  ))}
                </Box>
              )}
            </Box>

            {/* Calc error */}
            {calcError && (
              <Alert severity="error" sx={{ mt: 1 }} onClose={() => setCalcError('')}>
                {calcError}
              </Alert>
            )}

            {/* Variant result summary */}
            {activeVariant.results && (
              <Paper variant="outlined" sx={{ p: 1.5, mt: 2, bgcolor: 'success.50' }}>
                <Typography variant="caption" color="success.dark" fontWeight="bold"
                  display="block" sx={{ mb: 0.5 }}>
                  Calculated: {new Date(activeVariant.calculated_at).toLocaleTimeString()}
                </Typography>
                <Grid container spacing={1}>
                  {[
                    ['Total Cas.', activeVariant.results?.by_corps?.total_all_sources
                                    ?? activeVariant.results?.summary?.total_casualties],
                    ['KIA',        activeVariant.results?.by_corps?.total_kia
                                    ?? activeVariant.results?.summary?.kia],
                    ['WIA',        activeVariant.results?.by_corps?.total_wia
                                    ?? activeVariant.results?.summary?.wia],
                    ['Golden Hr',  activeVariant.results?.medical_system_analysis?.overall_golden_hour_percent
                                    ?? activeVariant.results?.summary?.golden_hour_compliance_pct, '%'],
                  ].map(([label, val, suffix]) => (
                    <Grid size={{ xs: 6, sm: 3 }} key={label}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {val != null ? `${val}${suffix || ''}` : '--'}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
                {/* Compare against baseline */}
                {baseline?.results && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Full side-by-side comparison available in Phase 3.
                  </Typography>
                )}
                {/* Planning variant indicator — selection managed in Phase 4 (Coordinate) */}
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  {approvedVariantId === activeVariantId && activeVariantId !== null ? (
                    <Chip label={t('tab6.selected_planning_variant')} size="small" color="success" icon={<CheckCircleIcon />} />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      To select this variant as the planning basis, go to Phase 4 \u2014 Coordinate.
                    </Typography>
                  )}
                </Box>
              </Paper>
            )}
          </Box>
        )}
      </Paper>
    );
  }

  function renderENH013Panel() {
    const opDays = baseline?.snapshot?.operation_parameters?.duration_days ?? 99;

    // Counts for summary chips
    const countByType = (type) =>
      approvalEvents.filter(e => e.event_type === type).length;

    // Event-type chip renderer
    function eventTypeChip(eventType) {
      const cfg = APPROVAL_EVENT_TYPES[eventType];
      if (!cfg) return null;
      let iconEl;
      if (eventType === 'APPROVAL_RECEIVED') {
        iconEl = <CheckCircleIcon fontSize="small" />;
      } else if (eventType === 'APPROVAL_DENIED') {
        iconEl = <CancelIcon fontSize="small" />;
      } else {
        iconEl = <HelpOutlineIcon fontSize="small" />;
      }
      return (
        <Chip
          icon={iconEl}
          label={cfg.label}
          color={cfg.color}
          size="small"
          variant="outlined"
        />
      );
    }

    return (
      <Accordion defaultExpanded={approvalEvents.length > 0} sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SchoolIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight="bold">
              {/* BUG-6G-001 FIX: was "3" — collision with Side-by-Side Comparison. Renumbered 4. */}
              4. Cross-Division Approval Events
            </Typography>
            {approvalEvents.length > 0 && (
              <Chip
                label={`${approvalEvents.length} event${approvalEvents.length !== 1 ? 's' : ''}`}
                size="small"
                color="success"
              />
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          {/* Educational callout */}
          <Alert severity="info" icon={<SchoolIcon />} sx={{ mb: 3 }}>
            <AlertTitle>Educational Objective  -  Anticipatory vs Reactive Planning</AlertTitle>
            <Typography variant="body2" paragraph>
              Cross-division medical support (e.g. Division 1 requesting a FST from Division 2)
              requires formal approval through the Corps medical staff. The chain adds latency:
              request submitted&nbsp;&rarr; staff review&nbsp;&rarr; Corps commander
              decision&nbsp;&rarr; notification to both divisions. Typical processing time: 6-12
              hours in exercise conditions; compressed under live-fire tempo but never
              instantaneous.
            </Typography>
            <Typography variant="body2">
              <strong>Anticipatory planners</strong> submit cross-division requests before the
              need becomes critical  -  assets arrive before the MASCAL threshold is crossed.{' '}
              <strong>Reactive planners</strong> request only after MASCAL is declared  -  approval
              processing may complete after the casualty peak has already passed. Use this event
              log to record the exact day of each request and approval, then compare patient
              outcomes across variants in Phase 3.
            </Typography>
          </Alert>

          {/* Add event form */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Record Approval Event
            </Typography>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                <TextField
                  label={t('tab6.day_label')}
                  type="number"
                  size="small"
                  fullWidth
                  value={eventForm.day}
                  onChange={e => handleEventFormChange('day', e.target.value)}
                  inputProps={{ min: 1, max: opDays }}
                  helperText={`1-${opDays}`}
                />
              </Grid>

              <Grid size={{ xs: 6, sm: 5, md: 3 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>{t('tab6.event_type_label')}</InputLabel>
                  <Select
                    value={eventForm.event_type}
                    label={t('tab6.event_type_label')}
                    onChange={e => handleEventFormChange('event_type', e.target.value)}
                  >
                    {Object.entries(APPROVAL_EVENT_TYPES).map(([key, cfg]) => (
                      <MenuItem key={key} value={key}>
                        {cfg.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  label={t('tab6.requesting_unit_label')}
                  size="small"
                  fullWidth
                  value={eventForm.requesting_unit}
                  onChange={e => handleEventFormChange('requesting_unit', e.target.value)}
                  placeholder="e.g. 1 DIV MED BDE"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  label={t('tab6.providing_unit_label')}
                  size="small"
                  fullWidth
                  value={eventForm.providing_unit}
                  onChange={e => handleEventFormChange('providing_unit', e.target.value)}
                  placeholder="e.g. 2 DIV FST"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label={t('tab6.notes_label')}
                  size="small"
                  fullWidth
                  value={eventForm.notes}
                  onChange={e => handleEventFormChange('notes', e.target.value)}
                  placeholder="e.g. Requested before Phase 2 assault  -  anticipatory planning"
                />
              </Grid>

              {eventFormError && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="error" sx={{ py: 0.5 }}>
                    {eventFormError}
                  </Alert>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={handleAddEvent}
                    disabled={!baseline}
                  >
                    Add Event
                  </Button>
                  {!baseline && (
                    <Typography variant="caption" color="text.secondary">
                      Load a baseline first
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Event log */}
          {approvalEvents.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No approval events recorded. Use the form above to log cross-division support
              requests as you plan the operation.
            </Typography>
          ) : (
            <>
              {/* ENH-013 Phase 4: visual day-axis timeline strip */}
              {(() => {
                // BUG-6C-001 FIX: was ?? 10 (hardcoded). Read from active scenario when no baseline loaded.
                const totalOpDays = baseline?.snapshot?.operation_parameters?.duration_days
                  ?? scenario?.operation_parameters?.duration_days
                  ?? 10;
                return (
                  <Box sx={{ overflowX: 'auto', mb: 2, p: 1.5,
                    background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary"
                      display="block" sx={{ mb: 1 }}>
                      Approval Timeline (day axis)
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', minHeight: 48 }}>
                      {Array.from({ length: totalOpDays }, (_, i) => i + 1).map(day => {
                        const evtsOnDay = approvalEvents.filter(e => Number(e.day) === day);
                        return (
                          <Box key={day} sx={{ flex: 1, minWidth: 28, textAlign: 'center' }}>
                            {evtsOnDay.map((e, j) => (
                              <Tooltip key={j} arrow
                                title={`D+${day}: ${e.event_type} — ${e.requesting_unit ?? ''}`}>
                                <Box sx={{
                                  width: 12, height: 12, borderRadius: '50%',
                                  mx: 'auto', mb: 0.25, cursor: 'help',
                                  background: e.event_type === 'APPROVAL_RECEIVED' ? '#2e7d32'
                                    : e.event_type === 'APPROVAL_DENIED'   ? '#c62828'
                                    : '#1565c0'
                                }} />
                              </Tooltip>
                            ))}
                            <Typography variant="caption" color="text.secondary"
                              sx={{ fontSize: '0.60rem', display: 'block', lineHeight: 1.2 }}>
                              D{day}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
                      {[
                        { color: '#1565c0', label: 'Requested' },
                        { color: '#2e7d32', label: 'Received' },
                        { color: '#c62828', label: 'Denied' },
                      ].map(l => (
                        <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: l.color }} />
                          <Typography variant="caption" color="text.secondary">{l.label}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })()}

              <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Day</strong></TableCell>
                      <TableCell><strong>Event</strong></TableCell>
                      <TableCell><strong>Requesting Unit</strong></TableCell>
                      <TableCell><strong>Providing Unit</strong></TableCell>
                      <TableCell><strong>Notes</strong></TableCell>
                      <TableCell align="right" sx={{ width: 40 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {approvalEvents.map(evt => (
                      <TableRow key={evt.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            D+{evt.day}
                          </Typography>
                        </TableCell>
                        <TableCell>{eventTypeChip(evt.event_type)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{evt.requesting_unit}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{evt.providing_unit}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {evt.notes || '\u2014'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={t('tab6.delete_event_tooltip')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteEvent(evt.id)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Summary chips */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`${countByType('APPROVAL_REQUESTED')} requested`}
                  color="info"
                  size="small"
                />
                <Chip
                  label={`${countByType('APPROVAL_RECEIVED')} received`}
                  color="success"
                  size="small"
                />
                <Chip
                  label={`${countByType('APPROVAL_DENIED')} denied`}
                  color="error"
                  size="small"
                />
              </Box>
            </>
          )}
        </AccordionDetails>
      </Accordion>
    );
  }



/**
 * Aggregate injury mechanism data from projections (used by renderTab6InjuryMechanismAnalysis).
 * Extracted from Tab5 renderInjuryMechanismAnalysis  -  takes projections as explicit parameter.
 */
function aggregateInjuryMechanismData(projections) {
  if (!projections?.by_brigade) return null;
  const aggregate = {
    total_t1_casualties: 0, total_prbc_units: 0, total_ffp_units: 0,
    total_mtp_patients: 0, by_mechanism: {}, category: 'MIXED',
    surge_days: 0, surge_active: false
  };
  let daysWithData = 0;
  let categoryCount = {};
  for (const brigade of projections.by_brigade) {
    if (!brigade.by_day) continue;
    for (const dayData of brigade.by_day) {
      const bloodMech = dayData.resources?.blood_by_mechanism;
      if (!bloodMech) continue;
      daysWithData++;
      aggregate.total_t1_casualties += bloodMech.totals?.t1_casualties || 0;
      aggregate.total_prbc_units    += bloodMech.totals?.prbc_units    || 0;
      aggregate.total_ffp_units     += bloodMech.totals?.ffp_units     || 0;
      aggregate.total_mtp_patients  += bloodMech.totals?.mtp_patients  || 0;
      if (bloodMech.surge?.active) { aggregate.surge_days++; aggregate.surge_active = true; }
      if (bloodMech.category) {
        categoryCount[bloodMech.category] = (categoryCount[bloodMech.category] || 0) + 1;
      }
      if (bloodMech.by_mechanism) {
        for (const [mechKey, mechData] of Object.entries(bloodMech.by_mechanism)) {
          if (!aggregate.by_mechanism[mechKey]) {
            aggregate.by_mechanism[mechKey] = {
              name: mechData.name, casualties: 0, mtp_patients: 0,
              prbc_units: 0, ffp_units: 0, platelets_units: 0, cryo_units: 0
            };
          }
          aggregate.by_mechanism[mechKey].casualties      += mechData.casualties      || 0;
          aggregate.by_mechanism[mechKey].mtp_patients    += mechData.mtp_patients    || 0;
          aggregate.by_mechanism[mechKey].prbc_units      += mechData.prbc_units      || 0;
          aggregate.by_mechanism[mechKey].ffp_units       += mechData.ffp_units       || 0;
          aggregate.by_mechanism[mechKey].platelets_units += mechData.platelets_units || 0;
          aggregate.by_mechanism[mechKey].cryo_units      += mechData.cryo_units      || 0;
        }
      }
    }
  }
  if (daysWithData === 0) return null;
  let maxCount = 0;
  for (const [cat, count] of Object.entries(categoryCount)) {
    if (count > maxCount) { maxCount = count; aggregate.category = cat; }
  }
  aggregate.average_prbc_per_t1 = aggregate.total_t1_casualties > 0
    ? Math.round((aggregate.total_prbc_units / aggregate.total_t1_casualties) * 10) / 10 : 0;
  return aggregate;
}

  // ---------------------------------------------------------------------------
  // Tab 6 Panels  -  Section 4: Evacuation Analysis (MOVED from Tab 5)
  // ---------------------------------------------------------------------------
  // These panels read from baseline.results (not live scenario state).
  // Guard: all panels receive null when baseline has no pre-calculated results,
  // and render a placeholder directing user to load the Tab 5 Baseline.
  // ---------------------------------------------------------------------------

  // ============================================================================
  const renderTab6StratevacCapacity = (projections) => {
    const cap = projections?.medical_requirements?.stratevac_capacity;
    if (!cap) return null;

    const classColors = {
      CLASS_A: 'success',
      CLASS_B: 'primary',
      CLASS_C: 'warning',
      CLASS_D: 'default'
    };
    // ENUM-SWEEP: human-readable labels for STRATEVAC airfield class
    const classLabels = {
      CLASS_A: 'Class A \u2014 Major Airfield',
      CLASS_B: 'Class B \u2014 Tactical Airfield',
      CLASS_C: 'Class C \u2014 Short Airstrip',
      CLASS_D: 'Class D \u2014 Unimproved Strip'
    };
    const chipColor = classColors[cap.class] || 'primary';

    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Strategic Evacuation (STRATEVAC) Capacity
          <Chip
            label={classLabels[cap.class] || cap.class?.replace(/_/g, ' ') || 'Class B'}
            size="small"
            color={chipColor}
            sx={{ ml: 1 }}
          />
          {cap.farp_enabled && (
            <Chip label="FARP" size="small" color="info" variant="outlined" />
          )}
        </Typography>

        <Grid container spacing={2}>
          {/* Litter capacity */}
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card variant="outlined" sx={{ textAlign: 'center' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h4" color={`${chipColor}.main`}>{cap.litter_capacity}</Typography>
                <Typography variant="caption">Max litter patients</Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Nominal throughput */}
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card variant="outlined" sx={{ textAlign: 'center' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h4">{cap.hourly_throughput}</Typography>
                <Typography variant="caption">Nominal litter/hr</Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Effective throughput (with FARP) */}
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card variant="outlined" sx={{ textAlign: 'center', bgcolor: cap.farp_enabled ? 'info.50' : undefined }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h4" color={cap.farp_enabled ? 'info.main' : 'text.primary'}>
                  {cap.effective_hourly_throughput}
                </Typography>
                <Typography variant="caption">
                  Effective litter/hr{cap.farp_enabled ? ' (+FARP)' : ''}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* FARP bonus */}
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card variant="outlined" sx={{ textAlign: 'center' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h4">
                  {cap.farp_enabled ? '+25%' : ' - '}
                </Typography>
                <Typography variant="caption">FARP sortie bonus</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Aircraft types */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Compatible aircraft:</strong>{' '}
            {cap.aircraft_types?.join(', ') || ' - '}
          </Typography>
        </Box>

        {/* Doctrinal note */}
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>National responsibility (AJP-4.10C):</strong> STRATEVAC throughput is constrained by
            airfield class, aircraft availability, and PHA staging capacity. Throughput figures assume
            continuous operations. Actual capacity depends on national flight schedule.
          </Typography>
        </Alert>
      </Paper>
    );
  };

  // ============================================================================
  const renderTab6StratevacScheduleAnalysis = (projections) => {
    const ssa = projections?.mascal_analysis?.stratevac_schedule_analysis;
    if (!ssa) return null;
    if (!ssa.enabled) return null;

    // No schedules configured  -  show warning only
    if (!ssa.airfields || ssa.airfields.length === 0) {
      return (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            STRATEVAC  -  CSU/PHA Queue Analysis
          </Typography>
          <Alert severity="warning">
            {ssa.warning || t('tab6.no_active_airfield_warning')}
          </Alert>
        </Paper>
      );
    }

    // -- PHA status -> MUI colour mapping ----------------------------------
    const phaStatusBg = (status) => {
      if (status === 'MASCAL')   return '#EDE7F6';   // purple tint  -  distinct from R3 red
      if (status === 'WARNING')  return '#FFEBEE';
      if (status === 'ELEVATED') return '#FFFDE7';
      return '#F1F8E9';
    };
    const phaStatusTextColor = (status) => {
      if (status === 'MASCAL')   return '#6A1B9A';
      if (status === 'WARNING')  return '#C62828';
      if (status === 'ELEVATED') return '#F57F17';
      return 'inherit';
    };

    const summary = ssa.summary || {};
    const anyMASCAL = summary.any_mascal === true;

    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          STRATEVAC  -  CSU/PHA Queue Timeline
          {anyMASCAL && (
            <Chip size="small" label="PHA OVERFLOW" color="error" sx={{ ml: 1 }} />
          )}
        </Typography>

        {/* -- Doctrinal note ----------------------------------------------- */}
        <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
          <Typography variant="caption">
            <strong>Doctrine (AJMedP-2):</strong> The airfield has no organic beds.
            The CSU (Casualty Staging Unit) is deployed to the APOD to provide patient
            holding. CSU capacity shown below. Overflow patients backflow to Role 3 beds.
            Patients waiting beyond 72 h incur a DOW penalty per AJMedP-1 Section 4.
          </Typography>
        </Alert>

        {/* -- Cross-airfield summary chips --------------------------------- */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip size="small" label={`${summary.active_airfield_count} airfield${summary.active_airfield_count !== 1 ? 's' : ''} scheduled`} variant="outlined" />
          {summary.total_backflow_to_r3 > 0 && (
            <Chip size="small" label={`${summary.total_backflow_to_r3} backflow -> R3`} color="error" />
          )}
          {summary.total_wait_dow_penalty > 0 && (
            <Chip size="small" label={`+${summary.total_wait_dow_penalty.toFixed(1)} DOW (wait penalty)`} color="warning" />
          )}
          {summary.backflow_triggers_corps_mascal && summary.total_backflow_to_r3 > 0 && (
            <Chip size="small" label={t('tab6.corps_mascal_risk')} color="error" variant="outlined" />
          )}
        </Box>

        {/* -- Per-airfield sections ----------------------------------------- */}
        {ssa.airfields.map((af, afIdx) => {
          if (af.error) {
            return (
              <Alert key={af.airfield_id || afIdx} severity="error" sx={{ mb: 2 }}>
                {af.airfield_label}: {af.error}
              </Alert>
            );
          }

          const aircraftSpec = STRATEVAC_AIRCRAFT[af.aircraft_type] || {};
          const afSummary = af.summary || {};
          const timeline  = af.timeline || [];
          const days      = timeline.map(t => t.day);
          // ENH-022b: calendar label when operation_start_date is set
          const _t6StartDate = baseline?.snapshot?.operation_parameters?.operation_start_date || '';
          const fmtD6 = (d) => {
            if (!_t6StartDate) return `D${d}`;
            try {
              const b = new Date(_t6StartDate);
              if (isNaN(b.getTime())) return `D${d}`;
              b.setDate(b.getDate() + d - 1);
              return `D${d}\n${b.getDate()} ${b.toLocaleString('en-GB', { month: 'short' })}`;
            } catch { return `D${d}`; }
          };

          return (
            <Box key={af.airfield_id || afIdx} sx={{ mb: 3 }}>
              {/* Airfield header */}
              <Typography variant="subtitle2" sx={{
                mt: afIdx > 0 ? 2 : 0, mb: 0.5,
                color: 'text.secondary', textTransform: 'uppercase',
                fontSize: '0.7rem', letterSpacing: 1
              }}>
                {af.is_primary ? 'Primary Airfield' : 'Alternate Airfield'}  -  {af.airfield_label}
              </Typography>

              {/* Planning parameter chips */}
              <Box sx={{ mb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`${af.pha_beds} CSU beds at APOD`} variant="outlined" />
                <Chip size="small" label={`${af.aircraft_label || af.aircraft_type}  -  ${af.litter_capacity} litter/flight`} variant="outlined" color="info" />
                {af.departure_days?.length > 0 && (
                  <Chip size="small" label={`Scheduled: D${af.departure_days.join(', D')}`} variant="outlined" />
                )}
                <Chip size="small" label={`${Math.round(af.load_fraction * 100)}% patient load`} variant="outlined" />
                {afSummary.mascal_days > 0 && (
                  <Chip size="small" label={`${afSummary.mascal_days} MASCAL day${afSummary.mascal_days !== 1 ? 's' : ''}`} color="error" />
                )}
                {afSummary.warning_days > 0 && (
                  <Chip size="small" label={`${afSummary.warning_days} WARNING day${afSummary.warning_days !== 1 ? 's' : ''}`} color="warning" />
                )}
              </Box>

              {/* Summary stats row */}
              <Box sx={{ mb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  Arrived: <strong>{afSummary.total_arrived}</strong>
                  {'  |  '}Departed: <strong>{afSummary.total_departed}</strong>
                  {'  |  '}Backflow: <strong style={{ color: afSummary.total_backflow > 0 ? '#C62828' : 'inherit' }}>{afSummary.total_backflow}</strong>
                  {afSummary.total_wait_dow_penalty > 0 && (
                    <> {'  |  '}Wait DOW penalty: <strong style={{ color: '#E65100' }}>+{afSummary.total_wait_dow_penalty.toFixed(1)}</strong></>
                  )}
                  {afSummary.first_mascal_day && (
                    <> {'  |  '}First MASCAL: <strong style={{ color: '#6A1B9A' }}>Day {afSummary.first_mascal_day}</strong></>
                  )}
                </Typography>
              </Box>

              {/* Day-by-day PHA timeline table */}
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.73rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '2px solid #E0E0E0', whiteSpace: 'nowrap', minWidth: 110 }}>
                        Metric
                      </th>
                      {days.map(d => (
                        <th key={d} style={{
                          textAlign: 'center', padding: '4px 4px', minWidth: 36,
                          borderBottom: '2px solid #E0E0E0',
                          // Departure day header: underline
                          borderTop: timeline.find(t => t.day === d)?.is_departure_day
                            ? '3px solid #1565C0' : undefined,
                          color: timeline.find(t => t.day === d)?.is_departure_day
                            ? '#1565C0' : 'inherit',
                          fontWeight: timeline.find(t => t.day === d)?.is_departure_day ? 'bold' : 'normal',
                          whiteSpace: 'pre-line', lineHeight: 1.2
                        }}>
                          {fmtD6(d)}
                          {timeline.find(t => t.day === d)?.is_departure_day && (
                            <div style={{ fontSize: '0.6rem', lineHeight: 1 }}>[flight]</div>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Row 1: PHA fill % */}
                    <tr style={{ borderBottom: '1px solid #F5F5F5' }}>
                      <td style={{ padding: '3px 8px', whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                        CSU fill %
                      </td>
                      {timeline.map(t => (
                        <td key={t.day} title={`Day ${t.day}: ${t.pha_queue}/${t.pha_beds} patients (${Math.round(t.pha_fraction * 100)}%)  -  ${t.pha_status}`}
                          style={{
                            textAlign: 'center', padding: '3px 4px',
                            backgroundColor: phaStatusBg(t.pha_status),
                            color: phaStatusTextColor(t.pha_status),
                            fontWeight: t.pha_status === 'MASCAL' || t.pha_status === 'WARNING' ? 'bold' : 'normal'
                          }}>
                          {Math.round(t.pha_fraction * 100)}%
                        </td>
                      ))}
                    </tr>

                    {/* Row 2: Queue count */}
                    <tr style={{ borderBottom: '1px solid #F5F5F5' }}>
                      <td style={{ padding: '3px 8px', whiteSpace: 'nowrap', color: '#555' }}>
                        Queue (pts)
                      </td>
                      {timeline.map(t => (
                        <td key={t.day} style={{ textAlign: 'center', padding: '3px 4px', color: t.pha_queue > 0 ? 'inherit' : '#BBB' }}>
                          {t.pha_queue > 0 ? t.pha_queue : ' - '}
                        </td>
                      ))}
                    </tr>

                    {/* Row 3: Departed today */}
                    <tr style={{ borderBottom: '1px solid #F5F5F5' }}>
                      <td style={{ padding: '3px 8px', whiteSpace: 'nowrap', color: '#1565C0' }}>
                        Departed
                      </td>
                      {timeline.map(t => (
                        <td key={t.day} style={{
                          textAlign: 'center', padding: '3px 4px',
                          color: t.departed > 0 ? '#1565C0' : '#BBB',
                          fontWeight: t.departed > 0 ? 'bold' : 'normal',
                          backgroundColor: t.is_departure_day ? '#E3F2FD' : undefined
                        }}>
                          {t.departed > 0 ? t.departed : ' - '}
                        </td>
                      ))}
                    </tr>

                    {/* Row 4: Backflow (only if any backflow occurred) */}
                    {afSummary.total_backflow > 0 && (
                      <tr style={{ borderBottom: '1px solid #F5F5F5' }}>
                        <td style={{ padding: '3px 8px', whiteSpace: 'nowrap', color: '#C62828' }}>
                          Backflow -> R3
                        </td>
                        {timeline.map(t => (
                          <td key={t.day} style={{
                            textAlign: 'center', padding: '3px 4px',
                            color: t.backflow > 0 ? '#C62828' : '#BBB',
                            fontWeight: t.backflow > 0 ? 'bold' : 'normal',
                            backgroundColor: t.backflow > 0 ? '#FFEBEE' : undefined
                          }}>
                            {t.backflow > 0 ? t.backflow : ' - '}
                          </td>
                        ))}
                      </tr>
                    )}

                    {/* Row 5: Wait hours (only if any patient waited) */}
                    {timeline.some(t => t.wait_hours > 0) && (
                      <tr style={{ borderBottom: '1px solid #F5F5F5' }}>
                        <td style={{ padding: '3px 8px', whiteSpace: 'nowrap', color: '#555' }}>
                          Wait (hrs)
                        </td>
                        {timeline.map(t => (
                          <td key={t.day} title={t.wait_critical ? 'CRITICAL: >72 h wait  -  DOW penalty applies' : t.wait_warning ? 'WARNING: >48 h wait' : ''}
                            style={{
                              textAlign: 'center', padding: '3px 4px',
                              color: t.wait_critical ? '#C62828' : t.wait_warning ? '#E65100' : '#555',
                              fontWeight: t.wait_critical ? 'bold' : 'normal',
                              backgroundColor: t.wait_critical ? '#FFEBEE' : t.wait_warning ? '#FFF3E0' : undefined
                            }}>
                            {t.wait_hours > 0 ? `${t.wait_hours}h` : ' - '}
                          </td>
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
              </Box>

              {/* Aircraft spec footer */}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {af.aircraft_label || af.aircraft_type}
                {aircraftSpec.cruising_speed_kmh ? `  |  ${aircraftSpec.cruising_speed_kmh} km/h` : ''}
                {aircraftSpec.icu_capable ? '  |  ICU-capable' : ''}
                {aircraftSpec.turnaround_hours ? `  |  ${aircraftSpec.turnaround_hours} h turnaround` : ''}
                {'  |  '}CSU beds at APOD (not organic airfield beds  -  AJMedP-2)
              </Typography>
            </Box>
          );
        })}

        {/* Legend */}
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>PHA/CSU status:</Typography>
          {[
            { label: 'GREEN  <50%',    bg: '#F1F8E9',  color: 'inherit'  },
            { label: 'ELEVATED 50-74%', bg: '#FFFDE7', color: '#F57F17'  },
            { label: 'WARNING 75-99%', bg: '#FFEBEE',  color: '#C62828'  },
            { label: 'MASCAL >=100%',   bg: '#EDE7F6',  color: '#6A1B9A'  },
          ].map(({ label, bg, color }) => (
            <Box key={label} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 14, height: 14, bgcolor: bg, border: '1px solid #E0E0E0', borderRadius: 0.5 }} />
              <Typography variant="caption" style={{ color }}>{label}</Typography>
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            [flight] = departure day (blue header)
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Source: AJMedP-1 Section 4, AJMedP-2, RTO-MP-HFM-157. Patients: corps T1 × R3 admission fraction × hospitalisation rate.
          Wait penalty: 5% DOW/24 h beyond 72 h threshold. Backflow triggers corps R3 MASCAL per AJMedP-1.
        </Typography>
      </Paper>
    );
  };

  // ============================================================================

  const renderTab6EvacuationRoutes = (projections) => {
    if (!projections?.by_brigade?.[0]?.by_day?.[0]?.route_impact) return null;
    
    const routeImpact = projections.by_brigade[0].by_day[0].route_impact;
    
    // Don't show if routes not configured
    if (!routeImpact.configured) return null;
    
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AltRouteIcon color="primary" />
          Evacuation Routes Status
          {routeImpact.routes_closed > 0 && (
            <Chip 
              label={`${routeImpact.routes_closed} CLOSED`}
              size="small" 
              color="error"
              sx={{ ml: 1 }}
            />
          )}
        </Typography>
        
        {/* Route Segments Summary */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { id: 'r1_to_r2b', label: 'R1 -> R2B', critical: true },
            { id: 'r2b_to_r2e', label: 'R2B -> R2E', critical: false },
            { id: 'r2e_to_r3', label: 'R2E -> R3', critical: false },
            { id: 'r3_to_r4', label: 'R3 -> R4', critical: false }
          ].map((segment) => {
            const travelTime = routeImpact.segments_summary?.[segment.id];
            const isRisk = segment.critical && travelTime > 45;
            
            return (
              <Grid size={{ xs: 6, md: 3 }} key={segment.id}>
                <Card variant="outlined" sx={{ borderColor: isRisk ? 'warning.main' : 'default' }}>
                  <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {segment.label}
                    </Typography>
                    <Typography variant="h5" color={isRisk ? 'warning.main' : 'primary'}>
                      {travelTime ? `${travelTime} min` : 'N/A'}
                    </Typography>
                    {isRisk && (
                      <Chip 
                        label={t('tab6.golden_hour_risk_chip')}
                        size="small" 
                        color="warning"
                        sx={{ mt: 0.5, fontSize: '0.65rem' }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        
        {/* Golden Hour Status — R1→R2B leg only (surgical access).
            Total Travel Time is the full R1→R4 chain including STRATEVAC.
            These are two separate metrics — labelled clearly to avoid confusion. */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card variant="outlined" sx={{
              bgcolor: routeImpact.golden_hour_achievable ? 'success.50' : 'error.50'
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Surgical Access (R1→R2B)
                </Typography>
                <Chip
                  label={routeImpact.golden_hour_achievable ? 'Golden Hour Met' : 'Golden Hour At Risk'}
                  color={routeImpact.golden_hour_achievable ? 'success' : 'error'}
                  sx={{ fontWeight: 'bold' }}
                />
                {routeImpact.golden_hour_margin_min !== null && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Margin: {routeImpact.golden_hour_margin_min > 0 ? '+' : ''}{routeImpact.golden_hour_margin_min} min vs 45 min threshold
                  </Typography>
                )}
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                  Doctrinal threshold: R1→R2B ≤45 min (AJMedP-1). Does not include STRATEVAC leg.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Full Evacuation Chain (R1→R4)
                </Typography>
                <Typography variant="h4" color="primary">
                  {routeImpact.total_travel_time_min !== Infinity
                    ? `${routeImpact.total_travel_time_min} min`
                    : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {routeImpact.total_travel_time_hours?.toFixed(1) || 'N/A'}h · includes STRATEVAC (R3→R4)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle2" gutterBottom>
                  DOW Impact
                </Typography>
                <Typography variant="h4" color={routeImpact.dow_modifier > 1.0 ? 'error' : 'success'}>
                  {routeImpact.dow_modifier.toFixed(2)}x
                </Typography>
                {routeImpact.using_alternate > 0 && (
                  <Chip 
                    label={`${routeImpact.using_alternate} Alternate Route(s)`}
                    size="small" 
                    color="warning"
                    sx={{ mt: 1, fontSize: '0.7rem' }}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Route Warnings */}
        {routeImpact.warnings && routeImpact.warnings.length > 0 && (
          <Alert severity="warning">
            <AlertTitle>Route Warnings</AlertTitle>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {routeImpact.warnings.map((warning, idx) => (
                <li key={idx}>
                  <Typography variant="body2">{warning}</Typography>
                </li>
              ))}
            </Box>
          </Alert>
        )}
      </Paper>
    );
  };

  // ============================================================================
  const renderTab6TacticalEvacCapacity = (projections) => {
    if (!projections?.by_brigade?.length) return null;

    // BUG-T40 FIX: Aggregate per-day data across all brigades.
    // CAPACITY: getTacticalEvacConfig() reads the CORPS-level fleet (same fleet object
    // shared across all brigades). Summing capacity across brigades multiplies the
    // corps fleet by the brigade count, producing 3x surplus (e.g. 6,006 instead of 2,002).
    // Fix: take capacity from the FIRST brigade's entry per day (capacitySet flag).
    // DEMAND: each brigade generates independent casualties → always summed.
    const dayMap = {};
    for (const brigade of projections.by_brigade) {
      for (const dayData of brigade.by_day || []) {
        const tea = dayData.tactical_evac_analysis;
        if (!tea || tea.status === 'UNKNOWN') continue;
        const d = dayData.day;
        if (!dayMap[d]) {
          dayMap[d] = {
            day: d,
            ground: 0, air: 0, total: 0,
            demand: 0, overflow: 0,
            ratios: [], statuses: [],
            capacitySet: false
          };
        }
        // Capacity: corps-level fleet — use first brigade's value only (avoid N× inflation)
        if (!dayMap[d].capacitySet) {
          dayMap[d].ground = tea.ground_capacity_per_day || 0;
          dayMap[d].air    = tea.air_capacity_per_day    || 0;
          dayMap[d].total  = tea.total_capacity_per_day  || 0;
          dayMap[d].capacitySet = true;
        }
        // Demand: sum across all brigades
        dayMap[d].demand   += Math.round((tea.hourly_demand || 0) * 24);
        dayMap[d].overflow += Math.round((tea.overflow_per_hour || 0) * 24);
        dayMap[d].ratios.push(tea.sufficiency_ratio || 0);
        dayMap[d].statuses.push(tea.status || 'GREEN');
      }
    }

    const days = Object.values(dayMap).sort((a, b) => a.day - b.day);
    if (days.length === 0) return null;

    // Status colour helpers
    const statusColor = s =>
      s === 'GREEN' ? 'success' : s === 'YELLOW' ? 'warning' : 'error';
    const statusBg = s =>
      s === 'GREEN'  ? '#e8f5e9' :
      s === 'YELLOW' ? '#fff8e1' :
      s === 'ORANGE' ? '#fff3e0' : '#ffebee';

    // Worst status per day (most critical brigade) — used for future colour coding
    // eslint-disable-next-line no-unused-vars
    const worstStatus = statuses => {
      if (statuses.includes('RED'))    return 'RED';
      if (statuses.includes('ORANGE')) return 'ORANGE';
      if (statuses.includes('YELLOW')) return 'YELLOW';
      return 'GREEN';
    };

    // Max demand for bar scaling
    const maxDemand = Math.max(...days.map(d => d.demand), 1);

    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalHospitalIcon color="primary" />
          Tactical Evacuation  -  Capacity vs Demand
        </Typography>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 700 }}>Day</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Ground cap/day</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Air cap/day</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Total cap/day</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Demand/day</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Surplus / Deficit</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>Demand bar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {days.map(d => {
                const surplus  = d.total - d.demand;
                // BUG-T40 FIX: recompute sufficiency at corps level (capacity vs total demand).
                // Per-brigade ratios/statuses are computed with single-brigade demand against the
                // full corps fleet — they are misleadingly GREEN. Use corps-level ratio instead.
                const corpsSufficiencyRatio = d.demand > 0 ? d.total / d.demand : 1.0;
                const avgRatio = Math.round(corpsSufficiencyRatio * 100) / 100;
                const ws = corpsSufficiencyRatio < 0.5 ? 'RED'
                         : corpsSufficiencyRatio < 0.75 ? 'ORANGE'
                         : corpsSufficiencyRatio < 1.0  ? 'YELLOW' : 'GREEN';
                const barPct   = Math.min(100, Math.round((d.demand / maxDemand) * 100));
                const capPct   = Math.min(100, Math.round((d.total  / maxDemand) * 100));
                return (
                  <TableRow key={d.day} sx={{ bgcolor: statusBg(ws) }}>
                    <TableCell sx={{ fontWeight: 600 }}>D{d.day}</TableCell>
                    <TableCell align="right">{formatNumber(d.ground)}</TableCell>
                    <TableCell align="right">{formatNumber(d.air)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatNumber(d.total)}</TableCell>
                    <TableCell align="right">{formatNumber(d.demand)}</TableCell>
                    <TableCell align="right" sx={{
                      fontWeight: 700,
                      color: surplus >= 0 ? 'success.dark' : 'error.dark'
                    }}>
                      {surplus >= 0 ? '+' : ''}{formatNumber(surplus)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${ws} (${Math.round(avgRatio * 100)}%)`}
                        size="small"
                        color={statusColor(ws)}
                      />
                    </TableCell>
                    <TableCell>
                      {/* Stacked bar: capacity (green) + demand overflow (red) */}
                      <Box sx={{ position: 'relative', height: 14, bgcolor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
                        <Box sx={{
                          position: 'absolute', left: 0, top: 0, height: '100%',
                          width: `${capPct}%`, bgcolor: 'success.light', borderRadius: 1
                        }} />
                        <Box sx={{
                          position: 'absolute', left: 0, top: 0, height: '100%',
                          width: `${barPct}%`,
                          bgcolor: surplus >= 0 ? 'transparent' : 'error.light',
                          borderRadius: 1, opacity: 0.6
                        }} />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Status legend */}
        <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
          {[
            { label: 'GREEN  -  sufficient (>=100%)',   color: 'success' },
            { label: 'YELLOW  -  marginal (75-99%)',   color: 'warning' },
            { label: 'ORANGE  -  stressed (50-74%)',   color: 'warning' },
            { label: 'RED  -  critical (<50%)',         color: 'error'   }
          ].map(({ label, color }) => (
            <Chip key={label} label={label} size="small" color={color} variant="outlined" />
          ))}
        </Box>

        {/* ARCH-010 note */}
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Note (AJMedP-2):</strong> Evacuation capacity deficit is an informational
            planning indicator only. DOW calculations treat all overflow as unevacuated per doctrine  - 
            capacity figures do not directly modify the DOW rate shown in reports.
          </Typography>
        </Alert>
      </Paper>
    );
  };

  // ============================================================================

  const renderTab6DailyProjectionChart = (projections) => {
    if (!projections?.by_brigade) return null;

    // Compute dailyProjectionData inline (Tab5 useMemo equivalent  -  reads projections arg)
    const durationDays = projections.by_corps?.operation_days
                      ?? projections.by_brigade?.[0]?.by_day?.length
                      ?? 0;
    const COHESION_PRIORITY = { GREEN: 0, AMBER: 1, RED: 2, BLACK: 3 };
    let cumulative = 0;
    const dailyProjectionData = [];
    for (let day = 1; day <= durationDays; day++) {
      let worstCohesion = null;
      let dayTotal = { day, bc: 0, kia: 0, dow: 0, wia: 0, dnbi: 0, rtd: 0,
                       cumulative: 0, strength: 0, dailyLoss: 0, cohesion: null };
      for (const brigade of projections.by_brigade) {
        const dayData = brigade.by_day?.find(d => d.day === day);
        if (dayData) {
          dayTotal.bc   += dayData.battle?.total || 0;
          dayTotal.kia  += dayData.battle?.kia   || 0;
          dayTotal.dow  += dayData.battle?.dow   || 0;
          dayTotal.wia  += dayData.battle?.wia   || 0;
          dayTotal.dnbi += dayData.dnbi?.total   || 0;
          dayTotal.rtd  += (dayData.battle?.rtd  || 0) + (dayData.dnbi?.rtd || 0);
          dayTotal.strength += dayData.current_strength ?? 0;
          const bc = dayData.cohesion;
          if (bc?.fm_color) {
            const pri = COHESION_PRIORITY[bc.fm_color] ?? 0;
            if (!worstCohesion || pri > (COHESION_PRIORITY[worstCohesion.fm_color] ?? 0))
              worstCohesion = bc;
          }
        }
      }
      // BUG-DAILY-001 FIX: Net Loss = Battle + DNBI − RTD (all-source casualties minus returns).
      // Previous formula omitted DNBI; with BUILDUP-001 making BC≈0 it also rendered as +RTD
      // (negative loss displayed as positive gain). rtd already includes battle.rtd + dnbi.rtd.
      dayTotal.dailyLoss = (dayTotal.bc + dayTotal.dnbi) - dayTotal.rtd;
      cumulative += dayTotal.bc + dayTotal.dnbi;
      dayTotal.cumulative = cumulative;
      dayTotal.cohesion = worstCohesion;
      dailyProjectionData.push(dayTotal);
    }

    if (dailyProjectionData.length === 0) return null;

    // Find max values for scaling
    const maxCumulative = Math.max(...dailyProjectionData.map(d => d.cumulative), 1);
    const maxStrength = Math.max(...dailyProjectionData.map(d => d.strength), 1);
    
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimelineIcon color="primary" />
          Daily Casualty Projections
        </Typography>
        
        {/* T5-003: Chart Tab Selection */}
        <ButtonGroup size="small" sx={{ mb: 2 }}>
          <Button 
            variant={activeChartTab === 'casualties' ? 'contained' : 'outlined'}
            onClick={() => setActiveChartTab('casualties')}
          >
            Daily Casualties
          </Button>
          <Button 
            variant={activeChartTab === 'cumulative' ? 'contained' : 'outlined'}
            onClick={() => setActiveChartTab('cumulative')}
          >
            Cumulative
          </Button>
          <Button 
            variant={activeChartTab === 'strength' ? 'contained' : 'outlined'}
            onClick={() => setActiveChartTab('strength')}
          >
            Force Strength
          </Button>
        </ButtonGroup>
        {/* BUG-110 FIX: Show all / collapse toggle for long operations */}
        {dailyProjectionData.length > 14 && (
          <Button
            size="small"
            variant="outlined"
            sx={{ ml: 1, mb: 2 }}
            onClick={() => setShowAllDays(prev => !prev)}
          >
            {showAllDays
              ? `Collapse (show D1-D14)`
              : `Show all ${dailyProjectionData.length} days`}
          </Button>
        )}
        
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Day</TableCell>
                {activeChartTab === 'casualties' && (
                  <>
                    <TableCell align="right">Battle</TableCell>
                    <TableCell align="right">DNBI</TableCell>
                    <TableCell align="right">
                      {/* BUG-RTD-DAY1-001: clarify RTD source especially on Day 1 */}
                      <Tooltip title={t('tab6.return_to_duty_tooltip')} arrow>
                        <span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>RTD</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('tab6.net_loss_tooltip')} arrow>
                        <span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>Net Loss</span>
                      </Tooltip>
                    </TableCell>
                  </>
                )}
                {activeChartTab === 'cumulative' && (
                  <>
                    <TableCell align="right">Daily Total</TableCell>
                    <TableCell align="right">Cumulative</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Cumulative Growth</TableCell>
                  </>
                )}
                {activeChartTab === 'strength' && (
                  <>
                    <TableCell align="right">Losses</TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('tab6.rtd_strength_tooltip')} arrow>
                        <span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>RTD</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">Remaining Strength</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Strength %</TableCell>
                    <TableCell>Cohesion</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* BUG-110 FIX: removed hardcoded .slice(0,14)  -  use showAllDays toggle instead */}
              {(showAllDays ? dailyProjectionData : dailyProjectionData.slice(0, 14)).map((day) => (
                <TableRow key={day.day}>
                  <TableCell>{day.day}</TableCell>
                  {activeChartTab === 'casualties' && (
                    <>
                      <TableCell align="right">{formatNumber(day.bc)}</TableCell>
                      <TableCell align="right">{formatNumber(day.dnbi)}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>+{formatNumber(day.rtd)}</TableCell>
                      <TableCell align="right" sx={{ color: day.dailyLoss > 0 ? 'error.main' : 'success.main' }}>
                        {day.dailyLoss > 0 ? '-' : '+'}{formatNumber(Math.abs(day.dailyLoss))}
                      </TableCell>
                    </>
                  )}
                  {activeChartTab === 'cumulative' && (
                    <>
                      <TableCell align="right">{formatNumber(day.bc + day.dnbi)}</TableCell>
                      <TableCell align="right">{formatNumber(day.cumulative)}</TableCell>
                      <TableCell>
                        <LinearProgress 
                          variant="determinate" 
                          value={maxCumulative > 0 ? (day.cumulative / maxCumulative) * 100 : 0}
                          sx={{ height: 8, borderRadius: 1 }}
                        />
                      </TableCell>
                    </>
                  )}
                  {activeChartTab === 'strength' && (
                    <>
                      <TableCell align="right" sx={{ color: 'error.main' }}>-{formatNumber(day.bc + day.dnbi)}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>+{formatNumber(day.rtd)}</TableCell>
                      <TableCell align="right">{formatNumber(day.strength)}</TableCell>
                      <TableCell>
                        <LinearProgress 
                          variant="determinate" 
                          value={maxStrength > 0 ? (day.strength / maxStrength) * 100 : 0}
                          color="primary"
                          sx={{ height: 8, borderRadius: 1 }}
                        />
                      </TableCell>
                      <TableCell>
                        {/* IMP-008: Cohesion chip  -  worst band across brigades this day.
                            fm_color: GREEN/AMBER/RED/BLACK per FM 101-5-1 Gumball Chart. */}
                        {day.cohesion ? (
                          <Tooltip title={day.cohesion.description || ''} arrow>
                            <Chip
                              label={day.cohesion.fm_color}
                              size="small"
                              color={
                                day.cohesion.fm_color === 'GREEN'  ? 'success' :
                                day.cohesion.fm_color === 'AMBER'  ? 'warning' :
                                day.cohesion.fm_color === 'RED'    ? 'error'   :
                                'default'
                              }
                              sx={day.cohesion.fm_color === 'BLACK'
                                ? { bgcolor: 'grey.800', color: 'common.white', fontWeight: 'bold' }
                                : { fontWeight: 'bold' }
                              }
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled"> - </Typography>
                        )}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };


  // ---------------------------------------------------------------------------
  // Tab 6 Panels  -  Section 5: Supporting Data (DUPLICATED from Tab 5)
  // ---------------------------------------------------------------------------

  // ============================================================================

  const renderTab6InjuryMechanismAnalysis = (projections) => {
    const mechanismData = aggregateInjuryMechanismData(projections);
    if (!mechanismData) return null;

    const natoStandard = 2.0; // NATO standard PRBC per T1 casualty
    const percentDiff = ((mechanismData.average_prbc_per_t1 - natoStandard) / natoStandard) * 100;

    // Sort mechanisms by casualty count
    const sortedMechanisms = Object.entries(mechanismData.by_mechanism)
      .sort(([, a], [, b]) => b.casualties - a.casualties);

    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon color="primary" />
          Injury Mechanism Analysis
          {mechanismData.surge_active && (
            <Chip 
              label="SURGE DETECTED"
              size="small" 
              color="warning"
              sx={{ ml: 1 }}
            />
          )}
        </Typography>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">Operational Environment</Typography>
                <Typography variant="h6" color="primary.main">
                  {mechanismData.category}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  {mechanismData.category === 'URBAN' && 'Urban combat pattern'}
                  {mechanismData.category === 'OPEN' && 'Open terrain pattern'}
                  {mechanismData.category === 'DEFENSIVE' && 'Defensive operations'}
                  {mechanismData.category === 'OFFENSIVE' && 'Offensive operations'}
                  {mechanismData.category === 'MIXED' && 'Mixed operations'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">Average Blood Consumption</Typography>
                <Typography variant="h6" color={percentDiff > 10 ? 'warning.main' : percentDiff < -10 ? 'success.main' : 'text.primary'}>
                  {mechanismData.average_prbc_per_t1} PRBC/casualty
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    NATO standard: {natoStandard} PRBC/casualty
                  </Typography>
                  <Chip 
                    label={`${percentDiff > 0 ? '+' : ''}${Math.round(percentDiff)}%`}
                    size="small"
                    color={percentDiff > 10 ? 'warning' : percentDiff < -10 ? 'success' : 'default'}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">Total T1 Casualties</Typography>
                <Typography variant="h6">{formatNumber(mechanismData.total_t1_casualties)}</Typography>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  MTP patients: {formatNumber(mechanismData.total_mtp_patients)} ({mechanismData.total_t1_casualties > 0 ? Math.round((mechanismData.total_mtp_patients / mechanismData.total_t1_casualties) * 100) : 0}%)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Surge Warning */}
        {mechanismData.surge_active && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>MSK Surge Detected</AlertTitle>
            Blood requirements increased on {mechanismData.surge_days} day(s) due to high casualty volume exceeding medical system capacity. 
            Surge multipliers: +50% PRBC, +30% FFP, +20% Platelets.
          </Alert>
        )}

        {/* Mechanism Breakdown Table */}
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          Breakdown by Injury Mechanism
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Mechanism</TableCell>
                <TableCell align="right">T1 Casualties</TableCell>
                <TableCell align="right">% of Total</TableCell>
                <TableCell align="right">PRBC Units</TableCell>
                <TableCell align="right">FFP Units</TableCell>
                <TableCell align="right">MTP Patients</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedMechanisms.map(([key, mech]) => {
                const percentage = mechanismData.total_t1_casualties > 0 
                  ? Math.round((mech.casualties / mechanismData.total_t1_casualties) * 100) 
                  : 0;
                
                return (
                  <TableRow key={key}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{mech.name}</Typography>
                    </TableCell>
                    <TableCell align="right">{formatNumber(mech.casualties)}</TableCell>
                    <TableCell align="right">
                      <Chip label={`${percentage}%`} size="small" />
                    </TableCell>
                    <TableCell align="right">{formatNumber(mech.prbc_units)}</TableCell>
                    <TableCell align="right">{formatNumber(mech.ffp_units)}</TableCell>
                    <TableCell align="right">{formatNumber(mech.mtp_patients)}</TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>Total</strong></TableCell>
                <TableCell align="right"><strong>{formatNumber(mechanismData.total_t1_casualties)}</strong></TableCell>
                <TableCell align="right"><strong>100%</strong></TableCell>
                <TableCell align="right"><strong>{formatNumber(mechanismData.total_prbc_units)}</strong></TableCell>
                <TableCell align="right"><strong>{formatNumber(mechanismData.total_ffp_units)}</strong></TableCell>
                <TableCell align="right"><strong>{formatNumber(mechanismData.total_mtp_patients)}</strong></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Educational Note */}
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Why does consumption differ from NATO standard?</strong> Your operational environment 
            ({mechanismData.category.toLowerCase()}) produces a specific injury mechanism distribution. 
            High-energy injuries (blast, mine/IED) require significantly more blood products than 
            penetrating trauma (gunshot wounds). This analysis shows the actual injury patterns 
            from your scenario.
          </Typography>
        </Alert>
      </Paper>
    );
  };

  // BUG-210 FIX: Calculator writes 999 as sentinel for "infinite DOS" when
  // dailyPRBCConsumption = 0 (CSS/support force with no T1/T2 casualties).
  // Values >= 100 render as "N/A" instead of the raw sentinel.
  const formatDOS = (val) => {
    if (val == null) return '--';
    if (val >= 100) return 'N/A';
    return val.toFixed(1);
  };

  // ============================================================================
  const renderTab6ResupplyAnalysis = (projections) => {
    const resupplySummary = projections?.medical_requirements?.resupply_summary;

    // Don't show if resupply not configured
    if (!resupplySummary?.configured) return null;

    const { total_events, total_units, events_by_day, dos_projection } = resupplySummary;
    const opDuration = projections?.duration_days ?? null;
    
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShippingIcon color="primary" />
          Resupply Operations Summary
          {dos_projection?.stockout_day && (
            <Chip 
              label={`Stockout Day ${dos_projection.stockout_day}`}
              size="small" 
              color="error"
              sx={{ ml: 1 }}
            />
          )}
        </Typography>
        
        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Total Resupplies
                </Typography>
                <Typography variant="h4" color="primary">
                  {total_events}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 6, md: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  PRBC Received
                </Typography>
                <Typography variant="h4" color="error">
                  {total_units.prbc}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 6, md: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Minimum DOS
                </Typography>
                <Typography 
                  variant="h4" 
                  color={dos_projection?.minimum_dos >= 100 ? 'success' : dos_projection?.minimum_dos < 3 ? 'error' : dos_projection?.minimum_dos < 5 ? 'warning' : 'success'}
                >
                  {formatDOS(dos_projection?.minimum_dos)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 6, md: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Final DOS
                </Typography>
                <Typography variant="h4" color="primary">
                  {formatDOS(dos_projection?.final_dos)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* PHASE 4.6.5: Monte Carlo Probability Bands */}
        {dos_projection?.monte_carlo?.enabled && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Monte Carlo Risk Analysis (100 iterations)</AlertTitle>
            <Typography variant="body2" gutterBottom>
              Resupply delivery simulation accounting for route conditions, weather, and enemy threat.
              Environmental conditions: {dos_projection.monte_carlo.environmental_conditions?.weather || 'CLEAR'} weather, 
              {dos_projection.monte_carlo.environmental_conditions?.ad_threat || 'NONE'} AD threat.
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'error.light', color: 'error.contrastText' }}>
                  <Typography variant="caption">Worst Case (5th percentile)</Typography>
                  <Typography variant="h6">
                    {dos_projection.monte_carlo.final_dos?.p5?.toFixed(1)} days DOS
                  </Typography>
                  <Typography variant="caption" display="block">
                    Min: {dos_projection.monte_carlo.minimum_dos?.p5?.toFixed(1)} days
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                  <Typography variant="caption">Expected (Median)</Typography>
                  <Typography variant="h6">
                    {dos_projection.monte_carlo.final_dos?.p50?.toFixed(1)} days DOS
                  </Typography>
                  <Typography variant="caption" display="block">
                    Min: {dos_projection.monte_carlo.minimum_dos?.p50?.toFixed(1)} days
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'success.light', color: 'success.contrastText' }}>
                  <Typography variant="caption">Best Case (95th percentile)</Typography>
                  <Typography variant="h6">
                    {dos_projection.monte_carlo.final_dos?.p95?.toFixed(1)} days DOS
                  </Typography>
                  <Typography variant="caption" display="block">
                    Min: {dos_projection.monte_carlo.minimum_dos?.p95?.toFixed(1)} days
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2">
                <strong>Stockout Probability:</strong> {Math.round(dos_projection.monte_carlo.stockout_probability * 100)}%
                {dos_projection.monte_carlo.average_stockout_day && (
                  <> (Average Day {Math.round(dos_projection.monte_carlo.average_stockout_day)})</>
                )}
              </Typography>
              <Chip 
                label={`${Math.round(dos_projection.monte_carlo.success_rate * 100)}% Success Rate`}
                color={dos_projection.monte_carlo.success_rate > 0.95 ? 'success' : dos_projection.monte_carlo.success_rate > 0.80 ? 'warning' : 'error'}
                size="small"
              />
            </Box>
          </Alert>
        )}
        
        {/* Stockout Warning */}
        {dos_projection?.stockout_day && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
          >
            <AlertTitle>Blood Supply Stockout Predicted</AlertTitle>
            <Typography variant="body2" gutterBottom>
              CRITICAL: Blood supply exhausted on Day {dos_projection.stockout_day}. 
              Immediate resupply or reduction in casualty generation required.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
              {`-> ${t('tab6.click_fix_in_tab4')}`}
            </Typography>
          </Alert>
        )}
        
        {/* Low DOS Warning */}
        {dos_projection?.minimum_dos < 5 && dos_projection?.minimum_dos < 100 && !dos_projection?.stockout_day && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
          >
            <AlertTitle>Low Days of Supply Warning</AlertTitle>
            <Typography variant="body2">
              Blood supply drops to {formatDOS(dos_projection.minimum_dos)} days.
              NATO minimum standard is 5 days. Additional resupply events recommended.
            </Typography>
          </Alert>
        )}
        
        {/* UX-TAB6-004: Zero-quantity resupply events warning */}
        {(() => {
          const zeroEvents = Object.values(events_by_day || {})
            .flat()
            .filter(ev => !ev.units?.prbc && !ev.units?.ffp && !ev.units?.plt && !ev.units?.cryo);
          return zeroEvents.length > 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>{zeroEvents.length} resupply event{zeroEvents.length > 1 ? 's' : ''} deliver{zeroEvents.length === 1 ? 's' : ''} 0 blood products.</strong>
              {' '}Days: {zeroEvents.map(ev => `D${ev.day}`).join(', ')}. Open Tab 4 to configure product quantities for these events.
            </Alert>
          ) : null;
        })()}

        {/* Resupply Events Timeline */}
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
          Resupply Events Timeline
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>Day</strong></TableCell>
                <TableCell><strong>Package</strong></TableCell>
                <TableCell><strong>Destination</strong></TableCell>
                <TableCell><strong>Method</strong></TableCell>
                {/* PHASE 4.6.5: Add Delivery Risk column if Monte Carlo enabled */}
                {dos_projection?.monte_carlo?.enabled && (
                  <TableCell><strong>Delivery Risk</strong></TableCell>
                )}
                <TableCell align="right"><strong>PRBC</strong></TableCell>
                <TableCell align="right"><strong>FFP</strong></TableCell>
                <TableCell align="right"><strong>PLT</strong></TableCell>
                <TableCell align="right"><strong>DOS Impact</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(events_by_day || {})
                .sort(([dayA], [dayB]) => parseInt(dayA) - parseInt(dayB))
                .flatMap(([day, dayEvents]) =>
                  dayEvents.map((event, index) => {
                    // Lookup package info from PUSH_PACKAGES
                    const packageInfo = PUSH_PACKAGES[event.push_package];
                    const isZeroQty = !event.units?.prbc && !event.units?.ffp && !event.units?.plt && !event.units?.cryo;
                    const packageLabel = packageInfo?.label || event.push_package;
                    const packageDescription = packageInfo?.description || '';
                    
                    return (
                      <TableRow key={`${day}-${index}`} hover sx={isZeroQty ? { bgcolor: 'warning.light' } : {}}>
                        <TableCell>
                          Day {event.day}
                          {isZeroQty && <Chip label="0 units" size="small" color="warning" sx={{ ml: 1 }} />}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={packageDescription} arrow>
                            <span style={{ cursor: 'help' }}>
                              {packageLabel}
                            </span>
                          </Tooltip>
                        </TableCell>
                        {/* ENUM-SWEEP FIX: human-readable MTF target and delivery method labels. */}
                        <TableCell>{({ ROLE1: 'Role 1', ROLE2_BASIC: 'Role 2 Basic', ROLE2_ENHANCED: 'Role 2 Enhanced', ROLE3: 'Role 3' }[event.mtf_target] ?? event.mtf_target ?? 'All')}</TableCell>
                        <TableCell>
                          <Chip
                            label={({ AIR: 'Air', GROUND: 'Ground', BLUE_LIGHT: 'Blue Light', DRONE: 'Drone' }[event.delivery_method] ?? event.delivery_method)}
                            size="small"
                            color={event.delivery_method === 'BLUE_LIGHT' ? 'error' :
                                   event.delivery_method === 'AIR' ? 'primary' : 'default'}
                            icon={event.delivery_method === 'AIR' || event.delivery_method === 'BLUE_LIGHT'
                              ? <AirplanemodeActiveIcon />
                              : <DirectionsCarIcon />}
                          />
                        </TableCell>
                        {/* PHASE 4.6.5: Show delivery risk if Monte Carlo enabled */}
                        {dos_projection?.monte_carlo?.enabled && (() => {
                          // Find matching event in Monte Carlo results
                          const riskEvent = dos_projection.monte_carlo.events_with_risk?.find(
                            e => e.day === event.day && e.push_package === event.push_package
                          );
                          const deliveryProb = riskEvent?.delivery_analysis?.probability;
                          const deliveryTime = riskEvent?.delivery_analysis?.time;
                          
                          if (deliveryProb) {
                            const riskColor = deliveryProb.risk_level === 'LOW' ? 'success' :
                                             deliveryProb.risk_level === 'MEDIUM' ? 'warning' :
                                             deliveryProb.risk_level === 'HIGH' ? 'error' : 'error';
                            
                            return (
                              <TableCell>
                                <Tooltip 
                                  title={
                                    <Box sx={{ p: 0.5 }}>
                                      <Typography variant="caption" display="block" fontWeight="bold">
                                        Delivery Analysis:
                                      </Typography>
                                      <Typography variant="caption" display="block" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                                        Time Factors:
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        Route: {deliveryTime?.factors?.route || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        Weather: {deliveryTime?.factors?.weather || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        AD Threat: {deliveryTime?.factors?.ad_threat || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" display="block" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                                        Total Time: {deliveryTime?.total_time_hours?.toFixed(1)}h
                                      </Typography>
                                      <Typography variant="caption" display="block" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                                        Success Probability: {deliveryProb?.percentage}%
                                      </Typography>
                                    </Box>
                                  }
                                  arrow
                                >
                                  <Chip 
                                    label={`${deliveryProb.percentage}%`}
                                    size="small"
                                    color={riskColor}
                                  />
                                </Tooltip>
                              </TableCell>
                            );
                          }
                          return <TableCell>--</TableCell>;
                        })()}
                        <TableCell align="right">
                          <Chip label={`+${event.units?.prbc || 0}`} size="small" color="error" />
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={`+${event.units?.ffp || 0}`} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={`+${event.units?.plt || 0}`} size="small" />
                        </TableCell>
                        {/* BUG-T38/T42 fix: unified DOS Impact display matching Tab5 logic */}
                        <TableCell align="right">
                          {event.dos_impact == null ? (
                            <Typography variant="body2" color="text.secondary">--</Typography>
                          ) : event.dos_impact >= 0 && event.dos_impact < 0.05 ? (
                            <Typography variant="body2" color="success.main" fontWeight="bold">+&lt;0.1 days</Typography>
                          ) : event.dos_impact > 0 ? (
                            opDuration && event.dos_impact > opDuration ? (
                              <Tooltip title={`+${event.dos_impact.toFixed(1)} days at avg consumption — exceeds scenario duration (${opDuration}d). Package covers entire operation.`} arrow>
                                <Typography variant="body2" color="success.main" fontWeight="bold">
                                  &gt;{opDuration}d ✓
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="success.main" fontWeight="bold">
                                +{event.dos_impact.toFixed(1)} days
                              </Typography>
                            )
                          ) : (
                            <Typography variant="body2" color="text.secondary">--</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
            </TableBody>
          </Table>
        </TableContainer>
        

      </Paper>
    );
  };

  // ============================================================================

  const renderTab6G1Summary = (projections) => {
    if (!projections) return null;

    // Compute g1Summary inline (equivalent to Tab5 useMemo  -  same logic)
    const _corps = projections.by_corps || {};
    const _totalRTD = _corps.total_rtd || 0;
    const _totalCas = _corps.total_casualties || 0;
    const _totalWIA = _corps.total_wia || 0;
    const _totalDNBI = _corps.total_dnbi || 0;
    const _woundedAndIll = _totalWIA + _totalDNBI;
    const _wiaRTD  = _woundedAndIll > 0 ? Math.round(_totalRTD * (_totalWIA  / _woundedAndIll)) : 0;
    const _dnbiRTD = _woundedAndIll > 0 ? Math.round(_totalRTD * (_totalDNBI / _woundedAndIll)) : 0;
    const _kia = _corps.total_kia || 0;
    const _dow = _corps.total_dow || 0;
    const _wiaLong  = _totalWIA  - _wiaRTD;
    const _dnbiLong = _totalDNBI - _dnbiRTD;
    const g1Summary = {
      totalReplacement: _kia + _dow + _wiaLong + _dnbiLong,
      breakdown: { kia: _kia, dow: _dow, wia_permanent: _wiaLong, dnbi_permanent: _dnbiLong },
      priority:  { immediate: _kia + _dow, shortTerm: _wiaLong, longTerm: _dnbiLong },
      rtd:     _totalRTD,
      netLoss: _totalCas - _totalRTD
    };
    if (!g1Summary.totalReplacement && g1Summary.totalReplacement !== 0) return null;
    
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon color="primary" />
          G1 Personnel Replacement Summary
        </Typography>
        
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Total Replacement Requirement
                </Typography>
                <Typography variant="h3" color="error.main">
                  {formatNumber(g1Summary.totalReplacement)}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>KIA (immediate)</TableCell>
                      <TableCell align="right">{formatNumber(g1Summary.breakdown.kia)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>DOW (immediate)</TableCell>
                      <TableCell align="right">{formatNumber(g1Summary.breakdown.dow)}</TableCell>
                    </TableRow>
                    <TableRow>
                      {/* BUG-T43 fix: compute actual non-RTD % from data, not hardcoded 40% */}
                      <TableCell>{`WIA (non-RTD, ${_totalWIA > 0 ? Math.round(_wiaLong / _totalWIA * 100) : 0}%)`}</TableCell>
                      <TableCell align="right">{formatNumber(g1Summary.breakdown.wia_permanent)}</TableCell>
                    </TableRow>
                    <TableRow>
                      {/* BUG-T43 fix: compute actual non-RTD % from data, not hardcoded 15% */}
                      <TableCell>{`DNBI (non-RTD, ${_totalDNBI > 0 ? Math.round(_dnbiLong / _totalDNBI * 100) : 0}%)`}</TableCell>
                      <TableCell align="right">{formatNumber(g1Summary.breakdown.dnbi_permanent)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Replacement Timeline
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">Immediate (KIA/DOW)</Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={g1Summary.totalReplacement > 0 ? (g1Summary.priority.immediate / g1Summary.totalReplacement) * 100 : 0}
                    color="error"
                    sx={{ height: 10, borderRadius: 1, mb: 0.5 }}
                  />
                  <Typography variant="caption" color="text.secondary">{formatNumber(g1Summary.priority.immediate)} personnel</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">Short-term (30-90 days)</Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={g1Summary.totalReplacement > 0 ? (g1Summary.priority.shortTerm / g1Summary.totalReplacement) * 100 : 0}
                    color="warning"
                    sx={{ height: 10, borderRadius: 1, mb: 0.5 }}
                  />
                  <Typography variant="caption" color="text.secondary">{formatNumber(g1Summary.priority.shortTerm)} personnel</Typography>
                </Box>
                <Box>
                  <Typography variant="body2">Long-term (variable)</Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={g1Summary.totalReplacement > 0 ? (g1Summary.priority.longTerm / g1Summary.totalReplacement) * 100 : 0}
                    color="info"
                    sx={{ height: 10, borderRadius: 1, mb: 0.5 }}
                  />
                  <Typography variant="caption" color="text.secondary">{formatNumber(g1Summary.priority.longTerm)} personnel</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    );
  };



  // Placeholder shown in Tab6 panels when no calculated results loaded yet

  // -- Phase 3: CoA comparison (V4-TAB6-001) -----------------------------------

  function renderCoAComparison() {
    // -----------------------------------------------------------------------
    // Integrity check: 4 planning-baseline fields must be identical across CoAs
    // for the comparison to be doctrinally valid (ATP 4-02.6 / AJP-4.10C).
    // MTF Role 1/2B/2E differences are EXPECTED and intentionally NOT checked.
    // -----------------------------------------------------------------------
    function checkIntegrity(snapA, snapB) {
      const issues = [];
      // R3 beds
      const r3A = snapA?.medical_configuration?.mtf_configuration?.role3_beds;
      const r3B = snapB?.medical_configuration?.mtf_configuration?.role3_beds;
      if (r3A != null && r3B != null && r3A !== r3B)
        issues.push(`Role 3 beds differ (${r3A} vs ${r3B})`);
      // STRATEVAC weekly capacity
      const svA = snapA?.operation_parameters?.stratevac_config?.weekly_capacity
               ?? snapA?.medical_configuration?.stratevac_config?.weekly_capacity;
      const svB = snapB?.operation_parameters?.stratevac_config?.weekly_capacity
               ?? snapB?.medical_configuration?.stratevac_config?.weekly_capacity;
      if (svA != null && svB != null && svA !== svB)
        issues.push(`STRATEVAC capacity differs (${svA} vs ${svB} pts/week)`);
      // Blood stocks (PRBC as proxy)
      const bA = snapA?.logistics?.medical_supplies?.initial_stock?.prbc_units;
      const bB = snapB?.logistics?.medical_supplies?.initial_stock?.prbc_units;
      if (bA != null && bB != null && bA !== bB)
        issues.push(`Initial blood stock differs (${bA} vs ${bB} PRBC units)`);
      // DOS
      const dosA = snapA?.logistics?.days_of_supply;
      const dosB = snapB?.logistics?.days_of_supply;
      if (dosA != null && dosB != null && dosA !== dosB)
        issues.push(`Days of Supply differ (${dosA} vs ${dosB})`);
      return issues;
    }

    const bothLoaded = coaSlot1 && coaSlot2;

    // Guide state — no slots yet
    if (!coaSlot1 && !coaSlot2) {
      return (
        <Paper elevation={2} sx={{ p: 2.5, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CompareArrowsIcon color="primary" />
            <Typography variant="h6">{t('tab6.coa_comparison')}</Typography>
            <Chip label={t('tab6.beta')} size="small" color="warning" variant="outlined" />
          </Box>
          <Alert severity="info">
            <strong>No CoA slots saved yet.</strong> Run calculations in Tab 5 for each force
            disposition option, then click <em>Save as CoA A</em> and <em>Save as CoA B</em>
            in the Tab 5 calculator panel. Both slots must be populated to enable comparison.
          </Alert>
        </Paper>
      );
    }

    // Integrity check (only when both loaded)
    const integrityIssues = bothLoaded
      ? checkIntegrity(coaSlot1.snapshot, coaSlot2.snapshot)
      : [];
    const isValid = integrityIssues.length === 0;

    // Comparison table cells (reuse extractMetric + COMPARISON_METRICS)
    function getCoaCell(metric, results, refResults) {
      const val   = extractMetric(results,    metric.paths);
      const blVal = extractMetric(refResults, metric.paths);
      if (val == null) return { val: null, delta: null, direction: null };
      if (blVal == null || blVal === 0) return { val, delta: null, direction: null };
      const delta = val - blVal;
      const pct   = Math.round((delta / Math.abs(blVal)) * 1000) / 10;
      let direction = 'neutral';
      if (delta !== 0) {
        const better = metric.higherIsBetter ? delta > 0 : delta < 0;
        direction = better ? 'better' : 'worse';
      }
      return { val, delta, pct, direction };
    }

    const categories = [...new Set(COMPARISON_METRICS.map(m => m.category))];

    // IMP-S102-031: CoA Comparison JSON export
    function handleExportComparisonJSON() {
      const buildMetricRow = (slot) => {
        const out = {};
        COMPARISON_METRICS.forEach(m => {
          const val = extractMetric(slot?.results, m.paths);
          out[m.key] = val;
        });
        return out;
      };
      const comparisonRows = COMPARISON_METRICS.map(m => {
        const aVal = extractMetric(coaSlot1?.results, m.paths);
        const bVal = extractMetric(coaSlot2?.results, m.paths);
        let delta = null, pct_delta = null, direction = 'neutral';
        if (aVal != null && bVal != null) {
          delta = bVal - aVal;
          pct_delta = aVal !== 0 ? Math.round((delta / Math.abs(aVal)) * 1000) / 10 : null;
          if (delta !== 0) direction = (m.higherIsBetter ? delta > 0 : delta < 0) ? 'better' : 'worse';
        }
        return { key: m.key, label: m.label, category: m.category,
                 coaA_value: aVal, coaB_value: bVal,
                 delta, pct_delta, direction };
      });
      const payload = {
        _meta: { exported_at: new Date().toISOString(), tool: 'PSP/CRE', format: 'CoA Comparison Export v1.0' },
        integrity_check: { valid: isValid, issues: integrityIssues },
        coaA: { name: coaSlot1?.name || 'CoA A', saved_at: coaSlot1?.saved_at || null,
                metrics: buildMetricRow(coaSlot1) },
        coaB: { name: coaSlot2?.name || 'CoA B', saved_at: coaSlot2?.saved_at || null,
                metrics: buildMetricRow(coaSlot2) },
        comparison: comparisonRows,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `PSP_CoA_Comparison_${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }

    // Step 4 / Improvement 5: relabel when a CoA slot has been approved
    const refinementMode = approvedCoASlot !== null;
    const refSlot  = refinementMode
      ? (approvedCoASlot === 1 ? coaSlot1 : coaSlot2)
      : coaSlot1;
    const newSlot  = refinementMode
      ? (approvedCoASlot === 1 ? coaSlot2 : coaSlot1)
      : coaSlot2;

    return (
      <Paper elevation={2} sx={{ p: 2.5, mb: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <CompareArrowsIcon color="primary" />
          <Typography variant="h6">
            {refinementMode ? 'Approved Plan vs New Proposal' : 'CoA Comparison (V4)'}
          </Typography>
          <Chip label={t('tab6.beta')} size="small" color="warning" variant="outlined" />
          {bothLoaded && (
            <Chip
              label={
                isValid
                  ? (refinementMode ? 'Comparison: VALID' : 'Planning baseline: VALID')
                  : refinementMode
                    ? `Delta: ${integrityIssues.length} field${integrityIssues.length > 1 ? 's' : ''}`
                    : `Baseline mismatch: ${integrityIssues.length} issue${integrityIssues.length > 1 ? 's' : ''}`
              }
              size="small"
              color={isValid ? 'success' : (refinementMode ? 'warning' : 'error')}
              variant={isValid ? 'filled' : 'outlined'}
            />
          )}
          {/* IMP-S102-031: Export comparison JSON */}
          {bothLoaded && coaSlot1?.results && coaSlot2?.results && (
            <Tooltip title={t('tab6.export_json_tooltip')}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportComparisonJSON}
                sx={{ ml: 'auto', fontSize: '0.72rem' }}
              >
                Export JSON
              </Button>
            </Tooltip>
          )}
        </Box>

        {/* Slot status row */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {(refinementMode
            ? [
                { slot: refSlot, label: 'Approved Plan', baseLabel: approvedCoASlot === 1 ? 'CoA A' : 'CoA B' },
                { slot: newSlot, label: 'New Proposal',  baseLabel: approvedCoASlot === 1 ? 'CoA B' : 'CoA A' },
              ]
            : [
                { slot: coaSlot1, label: 'CoA A', baseLabel: 'CoA A' },
                { slot: coaSlot2, label: 'CoA B', baseLabel: 'CoA B' },
              ]
          ).map(({ slot, label, baseLabel }) => (
            <Grid key={label} size={{ xs: 12, sm: 6 }}>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: slot ? 'action.hover' : 'background.default' }}>
                <Typography variant="subtitle2" fontWeight={600}>{label}</Typography>
                {slot ? (
                  <>
                    <Typography variant="body2">{slot.name || baseLabel}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Saved: {new Date(slot.saved_at).toLocaleString()}
                    </Typography>
                    {!slot.results && (
                      <Chip label="No results" size="small" color="warning" sx={{ mt: 0.5, display: 'block' }} />
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Not saved — calculate in Tab 5 and click &quot;Save as {baseLabel}&quot;
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Integrity issues */}
        {bothLoaded && !isValid && (
          <Alert severity={refinementMode ? 'info' : 'warning'} sx={{ mb: 2 }}>
            {refinementMode ? (
              <>
                <strong>Refinement deltas vs approved plan.</strong>{' '}
                The following planning parameters differ between the approved plan and the new proposal.
                Review as intentional refinements:
              </>
            ) : (
              <>
                <strong>Planning baseline mismatch — comparison may be invalid.</strong>{' '}
                CoA comparison is only doctrinally valid when Role 3, STRATEVAC schedule, blood stocks,
                and DOS are identical (ATP 4-02.6). MTF Role 1/2B/2E differences are expected and not
                flagged. Detected issues:
              </>
            )}
            <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
              {integrityIssues.map((iss, i) => <li key={i}>{iss}</li>)}
            </ul>
          </Alert>
        )}

        {/* Comparison table */}
        {bothLoaded && coaSlot1.results && coaSlot2.results && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: '35%' }}>Metric</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    {refinementMode
                      ? `Approved Plan \u2014 ${refSlot?.name || 'Approved Plan'}`
                      : `CoA A \u2014 ${coaSlot1.name || 'CoA A'}`}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    {refinementMode
                      ? `New Proposal \u2014 ${newSlot?.name || 'New Proposal'}`
                      : `CoA B \u2014 ${coaSlot2.name || 'CoA B'}`}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    {refinementMode ? 'Delta (New vs Approved)' : 'Delta (B vs A)'}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map(cat => {
                  const catMetrics = COMPARISON_METRICS.filter(m => m.category === cat);
                  return [
                    <TableRow key={cat + '_hdr'}>
                      <TableCell
                        colSpan={4}
                        sx={{ bgcolor: 'action.selected', py: 0.5, fontWeight: 700, fontSize: '0.72rem',
                              letterSpacing: '0.08em', textTransform: 'uppercase' }}
                      >
                        <Chip
                          label={cat}
                          size="small"
                          color={CATEGORY_COLORS[cat] || 'default'}
                          sx={{ fontSize: '0.68rem', height: 18 }}
                        />
                      </TableCell>
                    </TableRow>,
                    ...catMetrics.map(metric => {
                      const aVal = extractMetric(coaSlot1.results, metric.paths);
                      const cell = getCoaCell(metric, coaSlot2.results, coaSlot1.results);
                      const dirColor = cell.direction === 'better' ? 'success.main'
                                     : cell.direction === 'worse'  ? 'error.main'
                                     : 'text.secondary';
                      const nullLabel = metric.nullLabel || '--';
                      return (
                        <TableRow key={metric.key} hover>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{metric.label}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.8rem' }}>
                            {aVal != null ? metric.fmt(aVal) : nullLabel}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.8rem' }}>
                            {cell.val != null ? metric.fmt(cell.val) : nullLabel}
                          </TableCell>
                          <TableCell align="center">
                            {cell.delta != null && cell.delta !== 0 ? (
                              <Typography variant="caption" sx={{ color: dirColor, fontWeight: 600 }}>
                                {cell.delta > 0 ? '+' : ''}{metric.fmt(cell.delta)}
                                {' '}({cell.pct > 0 ? '+' : ''}{cell.pct}%)
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                {cell.delta === 0 ? '\u2014' : '--'}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ];
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Partial-load guidance */}
        {bothLoaded && (!coaSlot1.results || !coaSlot2.results) && (
          <Alert severity="warning">
            {!coaSlot1.results && 'CoA A was saved without calculated results. '}
            {!coaSlot2.results && 'CoA B was saved without calculated results. '}
            Recalculate in Tab 5, then save the slot again.
          </Alert>
        )}

        {/* Commander's Decision — Approve Planning Basis */}
        {(coaSlot1?.results || coaSlot2?.results) && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Commander&apos;s Decision — Approve Planning Basis
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Select the Course of Action to execute. This locks the planning basis for Annex Q and Requests to Higher.
            </Typography>
            {/* Single-slot guidance: only one CoA saved */}
            {(coaSlot1?.results && !coaSlot2) && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Only CoA A is saved. Save CoA B in Tab 5 for a full comparison, or approve CoA A directly as the planning basis.
              </Alert>
            )}
            {(!coaSlot1 && coaSlot2?.results) && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Only CoA B is saved. Save CoA A in Tab 5 for a full comparison, or approve CoA B directly as the planning basis.
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {coaSlot1?.results && (
                <Button
                  variant={approvedCoASlot === 1 ? 'contained' : 'outlined'}
                  color="success"
                  startIcon={approvedCoASlot === 1 ? <CheckCircleIcon /> : <GavelIcon />}
                  onClick={() => handleApproveCoA(1)}
                >
                  {approvedCoASlot === 1
                    ? `Approved: ${coaSlot1.name || 'CoA A'}`
                    : `Approve CoA A \u2014 ${coaSlot1.name || 'CoA A'}`}
                </Button>
              )}
              {coaSlot2?.results && (
                <Button
                  variant={approvedCoASlot === 2 ? 'contained' : 'outlined'}
                  color="success"
                  startIcon={approvedCoASlot === 2 ? <CheckCircleIcon /> : <GavelIcon />}
                  onClick={() => handleApproveCoA(2)}
                >
                  {approvedCoASlot === 2
                    ? `Approved: ${coaSlot2.name || 'CoA B'}`
                    : `Approve CoA B \u2014 ${coaSlot2.name || 'CoA B'}`}
                </Button>
              )}
            </Box>
            {approvedCoASlot && (
              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
                <Alert severity="success" sx={{ flex: 1, minWidth: 240 }} icon={<CheckCircleIcon />}>
                  <strong>Planning basis approved: {approvedCoASlot === 1 ? (coaSlot1.name || 'CoA A') : (coaSlot2.name || 'CoA B')}</strong>
                  {' \u2014 '}Select planning variant and generate Annex Q in Phase 4 (Coordinate).
                </Alert>
                {onLoadApprovedPlan && (
                  // UX-S97-001: filled primary button — same prominence as APPROVE buttons
                  <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    startIcon={<FolderOpenIcon />}
                    onClick={() => setLoadWorkingPlanDialog(true)}
                    sx={{ whiteSpace: 'nowrap', alignSelf: 'center' }}
                  >
                    Load as Working Plan
                  </Button>
                )}
              </Box>
            )}
          </Box>
        )}
      </Paper>
    );
  }

  // -- Phase 3: Comparison view ------------------------------------------------

  function renderComparisonView() {
    // Only show when baseline has results AND at least one calculated variant
    const calcVariants = variants.filter(v => v.results != null);
    if (!baseline?.results && calcVariants.length === 0) return null;

    // Columns: baseline + up to 5 calculated variants
    const columns = [
      { id: 'baseline', label: 'Baseline', results: baseline?.results ?? null, isBl: true },
      ...calcVariants.map(v => ({ id: v.id, label: v.name, results: v.results, isBl: false,
          mods: v.modifications }))
    ];

    if (columns.filter(c2 => c2.results).length < 2) {
      // Need at least baseline + 1 variant to compare
      return (
        <Paper elevation={2} sx={{ p: 2.5, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CompareArrowsIcon color="primary" />
            <Typography variant="h6">Variant Sensitivity Analysis — Baseline vs Modified Scenarios</Typography>
          </Box>
          <Alert severity="info">
            {/* UX-6G-001 FIX: old message implied only variant recalculation was needed.
                Actual requirement: Tab 5 Baseline with results (left column) + at least one
                calculated variant (right column). Config-only snapshots cannot populate the
                baseline column regardless of variant recalculation. */}
            Load a <strong>Tab 5 Baseline with calculated results</strong> (left column) and
            recalculate at least one variant above (right column) to enable comparison.
            If you loaded via <em>Load Current Configuration</em>, recalculate a variant first —
            then load a Tab 5 Baseline to populate the comparison baseline.
          </Alert>
        </Paper>
      );
    }

    // Group metrics by category
    const categories = [...new Set(COMPARISON_METRICS.map(m => m.category))];

    // For each metric + column compute value and delta vs baseline
    function getCell(metric, col) {
      const val = extractMetric(col.results, metric.paths);
      if (col.isBl) return { val, delta: null, direction: null };
      const blVal = extractMetric(baseline?.results, metric.paths);
      if (val == null || blVal == null) return { val, delta: null, direction: null };
      const delta = val - blVal;
      const pctDelta = blVal !== 0 ? Math.round((delta / Math.abs(blVal)) * 100) : null;
      // direction: 'better'|'worse'|'neutral'
      const direction = delta === 0 ? 'neutral'
        : metric.higherIsBetter
          ? (delta > 0 ? 'better' : 'worse')
          : (delta < 0 ? 'better' : 'worse');
      return { val, delta, pctDelta, direction };
    }

    const cellBg = (direction) => {
      if (direction === 'better') return 'success.50';
      if (direction === 'worse')  return 'error.50';
      return 'transparent';
    };
    const deltaColor = (direction) => {
      if (direction === 'better') return 'success.dark';
      if (direction === 'worse')  return 'error.dark';
      return 'text.secondary';
    };
    const deltaPrefix = (delta) => delta > 0 ? '+' : '';

    return (
      <Paper elevation={2} sx={{ p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CompareArrowsIcon color="primary" />
          <Typography variant="h6">{t('tab6.side_by_side')}</Typography>
          <Chip label={`${columns.filter(c2 => c2.results).length} columns`}
            size="small" color="primary" variant="outlined" />
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Green cells = improvement vs baseline. Red cells = degradation.
          Delta shown as absolute change and percentage.
          {' '}Note: WIA Survivors ↑ when DOW ↓ = lives saved (Total Wounded = WIA Survivors + DOW).
        </Typography>

        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 160 }}>Metric</TableCell>
                {columns.filter(c2 => c2.results).map(col => (
                  <TableCell key={col.id} align="center"
                    sx={{ fontWeight: 'bold', minWidth: 120,
                          bgcolor: col.isBl ? 'primary.50' : 'transparent' }}>
                    <Typography variant="caption" fontWeight="bold">{col.label}</Typography>
                    {!col.isBl && col.mods?.length > 0 && (
                      // BUG-84-016: label as "Applied:" so user knows these are actual modifications,
                      // not slider range endpoints
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="primary.main"
                          display="block" sx={{ fontSize: '0.58rem', fontWeight: 600, mb: 0.25 }}>
                          Applied:
                        </Typography>
                        {col.mods.slice(0, 2).map((m, i) => (
                          <Typography key={i} variant="caption"
                            color="text.secondary" display="block"
                            sx={{ fontSize: '0.6rem', lineHeight: 1.3 }}>
                            {m}
                          </Typography>
                        ))}
                        {col.mods.length > 2 && (
                          <Typography variant="caption" color="text.secondary"
                            sx={{ fontSize: '0.6rem' }}>
                            +{col.mods.length - 2} more
                          </Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map(cat => {
                const catMetrics = COMPARISON_METRICS.filter(m => m.category === cat);
                return [
                  // Category header row
                  <TableRow key={'cat_' + cat}
                    sx={{ bgcolor: 'grey.50' }}>
                    <TableCell colSpan={columns.filter(c2 => c2.results).length + 1}
                      sx={{ py: 0.5 }}>
                      <Chip label={cat} size="small"
                        color={CATEGORY_COLORS[cat] || 'default'}
                        variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                    </TableCell>
                  </TableRow>,
                  // Metric rows
                  ...catMetrics.map(metric => (
                    <TableRow key={metric.key} hover>
                      <TableCell>
                        <Typography variant="caption">{metric.label}</Typography>
                      </TableCell>
                      {columns.filter(c2 => c2.results).map(col => {
                        const cell = getCell(metric, col);
                        return (
                          <TableCell key={col.id} align="center"
                            sx={{ bgcolor: col.isBl ? 'primary.50'
                                    : cellBg(cell.direction) }}>
                            <Typography variant="body2" fontWeight="bold">
                              {cell.val != null
                                ? metric.fmt(cell.val)
                                : (metric.nullLabel ?? '--')}
                            </Typography>
                            {!col.isBl && cell.delta != null && cell.delta !== 0 && (
                              <Typography variant="caption"
                                color={deltaColor(cell.direction)}
                                display="block" sx={{ fontSize: '0.65rem' }}>
                                {deltaPrefix(cell.delta)}{cell.delta.toLocaleString()}
                                {cell.pctDelta != null &&
                                  ` (${deltaPrefix(cell.pctDelta)}${cell.pctDelta}%)`}
                              </Typography>
                            )}
                            {!col.isBl && cell.delta === 0 && (
                              <Typography variant="caption" color="text.disabled"
                                display="block" sx={{ fontSize: '0.65rem' }}>
                                no change
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                ];
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Winner summary */}
        {calcVariants.length > 0 && baseline?.results && (() => {
          const winners = {};
          COMPARISON_METRICS.forEach(metric => {
            const blVal = extractMetric(baseline.results, metric.paths);
            if (blVal == null) return;
            let best = null;
            let bestVal = blVal;
            calcVariants.forEach(v => {
              const val = extractMetric(v.results, metric.paths);
              if (val == null) return;
              const isBetter = metric.higherIsBetter ? val > bestVal : val < bestVal;
              if (isBetter) { best = v.name; bestVal = val; }
            });
            if (best) winners[metric.key] = { variant: best, metric: metric.label };
          });
          const winnerList = Object.values(winners);
          if (winnerList.length === 0) return null;
          // Count wins per variant
          const winCounts = {};
          winnerList.forEach(({ variant }) => {
            winCounts[variant] = (winCounts[variant] || 0) + 1;
          });
          const topWinner = Object.entries(winCounts)
            .sort((a, b) => b[1] - a[1])[0];
          return (
            <Alert severity="success" sx={{ mt: 2 }}>
              <AlertTitle>Best overall: <strong>{topWinner[0]}</strong>
                {' '}({topWinner[1]} metric{topWinner[1] !== 1 ? 's' : ''} improved)</AlertTitle>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                {winnerList.map(({ variant, metric: ml }, i) => (
                  <Chip key={i} size="small"
                    label={`${ml}: ${variant}`}
                    color="success" variant="outlined"
                    sx={{ fontSize: '0.65rem', height: 18 }} />
                ))}
              </Box>
            </Alert>
          );
        })()}
      </Paper>
    );
  }

  // ============================================================================
  // PHASE 4: SENSITIVITY ANALYSIS
  // For each calculated variant, shows per-KPI % delta from baseline.
  // Identifies which QM modification drove the most change per outcome metric.
  // ============================================================================
  const renderSensitivityAnalysis = () => {
    const calcVariants = variants.filter(v => v.results);
    if (calcVariants.length === 0 || !baseline?.results) return null;

    // Compute delta matrix: variant x metric -> { pct, abs, better }
    const matrix = calcVariants.map(v => ({
      variant: v,
      deltas: COMPARISON_METRICS.map(m => {
        const base = extractMetric(baseline.results, m.paths);
        const vval = extractMetric(v.results,        m.paths);
        if (base == null || vval == null || base === 0)
          return { key: m.key, pct: null, better: null };
        const pct = ((vval - base) / Math.abs(base)) * 100;
        // BUG-TAB6-002: use rounded pct for direction so "0.0%" always shows neutral
        // (floating point can produce tiny non-zero values that round to "0.0%" but color red)
        const pctRounded = parseFloat(pct.toFixed(1));
        const better = pctRounded === 0 ? null : (m.higherIsBetter ? pct > 0 : pct < 0);
        return { key: m.key, pct, better };
      })
    }));

    // Per metric: which variant improved it most?
    const bestPerMetric = {};
    COMPARISON_METRICS.forEach(m => {
      let best = null; let bestScore = null;
      matrix.forEach(row => {
        const d = row.deltas.find(x => x.key === m.key);
        if (!d || d.pct == null) return;
        const score = m.higherIsBetter ? d.pct : -d.pct;
        if (bestScore == null || score > bestScore) { bestScore = score; best = row.variant.id; }
      });
      bestPerMetric[m.key] = best;
    });

    return (
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <BarChartIcon color="primary" />
          <Typography variant="h6">{t('tab6.sensitivity_analysis')}</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Percentage change vs baseline per variant. Green = improvement, Red = degradation.
          Star marks the variant with the greatest improvement per metric.
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.78rem' }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap' }}>Metric</th>
                {calcVariants.map(v => (
                  <th key={v.id} style={{ textAlign: 'center', padding: '6px 8px', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap', minWidth: 90 }}>
                    {v.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {['Casualties','Medical','Logistics'].map(cat => {
                const catMetrics = COMPARISON_METRICS.filter(m => m.category === cat);
                return [
                  <tr key={'cat-' + cat}>
                    <td colSpan={calcVariants.length + 1}
                      style={{ background: '#eeeeee', padding: '3px 10px',
                        fontWeight: 'bold', fontSize: '0.70rem', color: '#555', borderTop: '1px solid #ddd' }}>
                      {cat.toUpperCase()}
                    </td>
                  </tr>,
                  ...catMetrics.map(m => (
                    <tr key={m.key} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '4px 10px', color: '#555', whiteSpace: 'nowrap' }}>{m.label}</td>
                      {matrix.map(row => {
                        const d = row.deltas.find(x => x.key === m.key);
                        const isBest = bestPerMetric[m.key] === row.variant.id;
                        if (!d || d.pct == null) return <td key={row.variant.id} style={{ textAlign: 'center', color: '#bbb' }}> - </td>;
                        const bg    = d.better === null ? '#f5f5f5' : (d.better ? '#e8f5e9' : '#ffebee');
                        const color = d.better === null ? '#757575'  : (d.better ? '#2e7d32' : '#c62828');
                        const sign  = parseFloat(d.pct.toFixed(1)) > 0 ? '+' : '';
                        return (
                          <td key={row.variant.id} style={{ textAlign: 'center', padding: '4px 6px', background: bg, color }}>
                            {sign}{d.pct.toFixed(1)}%{isBest && d.better ? ' *' : ''}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ];
              })}
            </tbody>
          </table>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          * = best improvement for that metric across all variants
        </Typography>
      </Paper>
    );
  };

  // ============================================================================
  // PHASE 4: MEDSITREP GENERATOR
  // Generates a NATO-formatted Medical Situation Report as plain text.
  // Includes baseline results, all calculated variants, ENH-013 events.
  // Downloadable as .txt via Blob URL.
  // ============================================================================
  const generateMEDSITREPText = () => {
    const now = new Date();
    const dtg = now.toISOString().replace('T', 'Z').slice(0, 16).replace(/[-:]/g, '') + 'Z';
    const dur = baseline?.snapshot?.operation_parameters?.duration_days ?? '--';
    const nat = baseline?.snapshot?.operation_parameters?.corps_config?.overall_mission ?? 'GROUND_COMBAT';
    const bs  = extractBaselineSummary(baseline?.results);

    const lines = [
      'MEDSITREP - MEDICAL SITUATION REPORT',
      'Classification: UNCLASSIFIED // EXERCISE',
      `DTG: ${dtg}`,
      'TO: CORPS SURGEON',
      'FROM: MEDICAL PLANNER (PSP/CRE Tool v3.0)',
      '='.repeat(60),
      '',
      '1. SITUATION SUMMARY',
      `   a. Operation duration: ${dur} days`,
      `   b. Mission type: ${nat}`,
      `   c. Baseline source: ${baseline?.source === 'tab5' ? 'Tab 5 calculated results' : 'Configuration snapshot'}`,
      `   d. Variants analysed: ${variants.filter(v => v.results).length} of ${variants.length} calculated`,
      '',
      '2. BASELINE MEDICAL ESTIMATE (AJMedP-1)',
      bs ? [
        `   Total Casualties:  ${bs.total_casualties ?? '--'}`,
        `   KIA:               ${bs.kia ?? '--'}`,
        `   WIA:               ${bs.wia ?? '--'}`,
        `   DNBI:              ${bs.dnbi ?? '--'}`,
        `   RTD:               ${bs.rtd ?? '--'}`,
        `   Golden Hour:       ${bs.golden_hour_pct != null ? bs.golden_hour_pct.toFixed(1) + '%' : '--'}`,
        `   R2B MASCAL Day:    ${bs.r2b_mascal_days != null ? 'D+' + bs.r2b_mascal_days : '--'}`,
        `   Blood Stockout:    ${bs.blood_stockout_day != null ? 'Day ' + bs.blood_stockout_day : 'No stockout'}`,
      ].join('\n') : '   No baseline results available.',
      '',
      '3. VARIANT COMPARISON',
    ];

    variants.filter(v => v.results).forEach((v, i) => {
      const vs = extractBaselineSummary(v.results);
      const bs2 = extractBaselineSummary(baseline?.results);
      lines.push(`   Variant ${i + 1}: ${v.name}`);
      if (v.modifications?.length > 0) {
        lines.push(`   Modifications: ${v.modifications.join('; ')}`);
      }
      if (vs && bs2) {
        const ghBase = bs2.golden_hour_pct; const ghVar = vs.golden_hour_pct;
        lines.push(`   Golden Hour:    ${ghVar != null ? ghVar.toFixed(1) + '%' : '--'}${ghBase != null && ghVar != null ? ' (delta: ' + (ghVar - ghBase >= 0 ? '+' : '') + (ghVar - ghBase).toFixed(1) + '%)' : ''}`);
        lines.push(`   Total Cas:      ${vs.total_casualties ?? '--'}`);
        lines.push(`   Blood Stockout: ${vs.blood_stockout_day != null ? 'Day ' + vs.blood_stockout_day : 'No stockout'}`);
      }
      lines.push('');
    });

    let sectionNum = 4;

    if (approvalEvents.length > 0) {
      lines.push(`${sectionNum}. CROSS-DIVISION APPROVAL LOG`);
      sectionNum++;
      approvalEvents.forEach(e => {
        lines.push(`   D+${e.day} ${e.event_type}: ${e.requesting_unit ?? ''} -> ${e.supporting_division ?? ''} (${e.notes ?? ''})`);
      });
      lines.push('');
    }

    lines.push(`${sectionNum}. RECOMMENDATIONS`);
    const calcV = variants.filter(v => v.results);
    if (calcV.length > 0 && baseline?.results) {
      const ghBase = extractMetric(baseline.results, ['medical_system_analysis.overall_golden_hour_percent','summary.golden_hour_compliance_pct']);
      let bestV = null; let bestGH = ghBase ?? 0;
      calcV.forEach(v => {
        const gh = extractMetric(v.results, ['medical_system_analysis.overall_golden_hour_percent','summary.golden_hour_compliance_pct']);
        if (gh != null && gh > bestGH) { bestGH = gh; bestV = v; }
      });
      if (bestV) {
        lines.push(`   Recommend: ${bestV.name} (Golden Hour +${(bestGH - (ghBase ?? 0)).toFixed(1)}% vs baseline)`);
        if (bestV.modifications?.length > 0)
          lines.push(`   Key modifications: ${bestV.modifications.join('; ')}`);
      } else {
        lines.push('   No variant shows improvement on Golden Hour metric vs baseline.');
      }
    } else {
      lines.push('   Insufficient calculated variants for recommendation.');
    }
    lines.push('');
    lines.push('='.repeat(60));
    lines.push('Generated by PSP/CRE Tool v3.0 (Dr. Tatar Radu MD)');
    lines.push('AJMedP-1 / AJP-4.10C compliant planning tool');
    return lines.join('\n');
  };

  const renderMEDSITREP = () => {
    const hasResults = baseline?.results || variants.some(v => v.results);
    if (!isLoaded) return null;

    const handleDownload = () => {
      const text = generateMEDSITREPText();
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = 'MEDSITREP_PSP_CRE.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    return (
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ArticleIcon color="primary" />
          <Typography variant="h6">{t('tab6.medsitrep')}</Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            size="small"
            startIcon={<GetAppIcon />}
            onClick={handleDownload}
            disabled={!hasResults}
          >
            Download .txt
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Generates a NATO-format Medical Situation Report (AJMedP-1) covering the baseline
          Medical Estimate, all calculated variants, cross-division approval events, and
          a planner recommendation. Plain text - suitable for exercise AAR or OPORD Annex Q appendix.
        </Typography>
        {!hasResults && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Load a Tab 5 baseline with calculated results to enable the MEDSITREP download.
          </Alert>
        )}
      </Paper>
    );
  };

  // ============================================================================
  // PHASE 4: ENH-022a — NATO OPERATION CONTEXT BANNER
  // Horizontal display of standard NATO operational sequence.
  // Maps Tab 2 phase posture data onto the sequence. Display-only, zero calc.
  // ============================================================================
  const renderNATOBanner = () => {
    const NATO_PHASES = [
      { label: 'Pre-Conflict',     color: '#78909c', sub: 'DNBI before combat' },
      { label: 'Force Buildup',    color: '#5c6bc0', sub: 'Assembly areas' },
      { label: 'Mov to Contact',   color: '#7b1fa2', sub: 'March phase' },
      { label: 'Shaping',          color: '#e65100', sub: 'Air/fires prep' },
      { label: 'Decisive Action',  color: '#c62828', sub: 'Max casualties' },
      { label: 'Exploitation',     color: '#2e7d32', sub: 'Pursuit' },
      { label: 'Consolidation',    color: '#1565c0', sub: 'Reorganise/Refit' },
    ];
    // BUG-TAB6-004: Tab 2 saves phases to corps_config.phases, not operation_parameters.phases directly
    const opPhases = baseline?.snapshot?.operation_parameters?.corps_config?.phases
                  ?? baseline?.snapshot?.operation_parameters?.phases
                  ?? [];

    return (
      <Accordion sx={{ mb: 2 }} defaultExpanded={false}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PublicIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight="bold">
              NATO Operational Sequence
            </Typography>
            <Chip label={t('tab6.display_only')} size="small" variant="outlined" />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Standard NATO operational sequence per AJP-3. DNBI casualties begin in Force
            Buildup phase — before first combat. Anticipatory medical planners pre-position
            Role 2B during Mov to Contact (12h setup cost).
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
            {NATO_PHASES.map((p, i) => (
              <Box key={i} sx={{ textAlign: 'center', minWidth: 95, p: 1,
                background: p.color + '18', border: `2px solid ${p.color}`,
                borderRadius: 1 }}>
                <Typography variant="caption" fontWeight="bold" sx={{ color: p.color, display: 'block' }}>
                  {p.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  {p.sub}
                </Typography>
              </Box>
            ))}
          </Box>
          {opPhases.length > 0 && (
            <>
              <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Configured Tab 2 phases:
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {opPhases.map((p, i) => (
                  <Chip key={i} size="small"
                    label={`Phase ${p.phase_number ?? i + 1}: ${p.operational_posture ?? p.mission_type ?? 'N/A'} (D${p.start_day}-D${p.end_day})`}
                    variant="outlined" color="primary" />
                ))}
              </Box>
            </>
          )}
          {opPhases.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No multi-phase operations configured in Tab 2.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  // ============================================================================
  // RESEARCH-REPORTS-002: OPORD ANNEX Q — MEDICAL SUPPORT PLAN (AJP-4.10C)
  // Formal five-section MSP output. Reads baseline.results + baseline.snapshot.
  // Companion to MEDSITREP — covers planning document, not situation report.
  // ============================================================================
  const generateAnnexQText = () => {
    const now  = new Date();
    const dtg  = now.toISOString().replace('T','Z').slice(0,16).replace(/[-:]/g,'') + 'Z';
    const snap = baseline?.snapshot ?? {};
    const op   = snap.operation_parameters ?? {};
    const res  = baseline?.results ?? {};
    const bs   = extractBaselineSummary(res);
    const corps = res.by_corps ?? {};
    const lg   = res.logistics ?? {};
    const med  = res.medical_system_analysis ?? {};
    const dur  = op.duration_days ?? op.corps_config?.total_duration_days ?? '--';
    const posture = op.operational_posture ?? op.corps_config?.overall_mission ?? 'GROUND_COMBAT';
    const nat  = snap.force?.nationalStandard ?? op.corps_config?.overall_mission ?? 'NATO';
    const tprpDays = op.theatre_patient_return_policy?.policy_days
                  ?? op.thp_config?.policy_days ?? 7;

    const line = (label, value) => `   ${label}: ${value ?? '--'}`;
    const hr = '='.repeat(60);
    const hr2 = '-'.repeat(60);

    const lines = [
      'OPORD ANNEX Q — MEDICAL SUPPORT PLAN',
      'Classification: UNCLASSIFIED // EXERCISE',
      `DTG: ${dtg}`,
      `Reference: AJP-4.10C Allied Joint Doctrine for Medical Support`,
      hr,
      '',
      'Q-1. SITUATION',
      hr2,
      'a. Medical intelligence (MEDINT):',
      line('  Operational posture', posture),
      line('  Duration', dur + ' days'),
      line('  National standard (lead nation)', nat),
      line('  Theatre Patient Return Policy', tprpDays + ' days (AJP-4.10C)'),
      '',
      'b. Own medical forces:',
      bs ? [
        line('  Total personnel at risk (PAR)', bs.total_casualties != null
          ? 'Calculated — see Medical Estimate (Tab 5)' : '--'),
        line('  Medical Brigade', 'Configured in Tab 3'),
        line('  Role 2B (brigade level)', 'See Tab 3 MTF configuration'),
        line('  Role 2E (division level)', 'See Tab 3 MTF configuration'),
        line('  Role 3  (corps level)',    'See Tab 3 MTF configuration'),
      ].join('\n') : '   Baseline not loaded — run Tab 5 Calculate first.',
      '',
      'c. Supporting medical forces:',
      line('  HNS', op.hns_config?.enabled ? ('Level: ' + (op.hns_config?.level ?? 'STANDARD')) : 'Not configured'),
      line('  STRATEVAC', 'See Tab 3 STRATEVAC configuration'),
      '',
      'Q-2. MISSION',
      hr2,
      `The Medical Support Element will provide continuous Role 1 through Role 3 medical`,
      `support to the Corps throughout ${dur} days of ${posture} operations, maintaining`,
      `Golden Hour compliance and Theatre Patient Return Policy of ${tprpDays} days,`,
      `in accordance with AJMedP-1 and AJP-4.10C.`,
      '',
      'Q-3. EXECUTION',
      hr2,
      'a. Concept of Medical Support:',
      bs ? [
        line('  Projected total casualties',
          corps.total_all_sources ?? corps.total_casualties ?? bs.total_casualties ?? '--'),
        line('  KIA',  corps.total_kia  ?? bs.kia  ?? '--'),
        line('  WIA',  corps.total_wia  ?? bs.wia  ?? '--'),
        line('  DNBI', corps.total_dnbi ?? bs.dnbi ?? '--'),
        line('  RTD',  bs.rtd  ?? '--'),
        line('  Golden Hour compliance',
          med.overall_golden_hour_percent != null
            ? med.overall_golden_hour_percent + '%'
            : bs.golden_hour_pct != null
              ? bs.golden_hour_pct.toFixed(1) + '%'
              : '--'),
        line('  Medical system status', med.overall_status ?? '--'),
        line('  First MASCAL event', bs.r2b_mascal_days != null
          ? 'D+' + bs.r2b_mascal_days : 'None projected'),
        line('  Peak R2B occupancy', bs.peak_r2b_pct != null
          ? bs.peak_r2b_pct.toFixed(0) + '%' : '--'),
      ].join('\n') : '   No calculated results. Load Tab 5 Baseline with results.',
      '',
      'b. Tasks to medical units:',
      '   (1) Role 1 elements: provide immediate TCCC at point of injury.',
      '   (2) Role 2B (Brigade Medical Company): resuscitative surgery, hold <= TPRP.',
      '   (3) Role 2E (Division): damage control surgery, ICU capability.',
      '   (4) Role 3 (Corps): definitive care, specialist surgery, blood bank.',
      '   (5) STRATEVAC: evacuation of non-RTD patients to Role 4 per schedule.',
      '',
      'c. Theatre Patient Return Policy (TPRP):',
      line('  TPRP', tprpDays + ' days — AJMedP-2 / AJP-4.10C'),
      '   All patients held <= TPRP days. Non-RTD patients evacuated to Role 4.',
      '',
      // BUG-NEXT-002: Brigade phase role (V5-012) — previously omitted from Annex Q
      'd. Brigade tactical missions and phase role designations (V5-012):',
      (() => {
        const brigades = getSnapshotBrigades(snap);
        if (!brigades || brigades.length === 0) {
          return '   No brigade configurations found — configure in Tab 1/2.';
        }
        // Phase labels: prefer corps phases, fall back to operation_phases, then ordinal
        const corpsPhasesArr = op.corps_config?.phases || op.operation_phases || [];
        const phaseLabel = (idx) => {
          const ph = corpsPhasesArr[Number(idx)];
          return ph?.name || ph?.description
            ? `Phase ${Number(idx) + 1} (${ph.name || ph.description})`
            : `Phase ${Number(idx) + 1}`;
        };
        const ROLE_LABELS = {
          MAIN_EFFORT:       'MAIN EFFORT (×1.5)',
          SUPPORTING:        'SUPPORTING (×1.0)',
          RESERVE:           'RESERVE (×0.2)',
          EXPLOITATION:      'EXPLOITATION (×1.3)',
          ECONOMY_OF_FORCE:  'ECONOMY OF FORCE (×0.6)',
        };
        return brigades.map(b => {
          const bName = b.name || b.brigade_id || 'Unknown Bde';
          const tab1Mission = b.operation?.mission_type ?? b.phases?.[0]?.mission_type ?? 'Not set';
          const overrides = b.phase_overrides || {};
          const overrideEntries = Object.entries(overrides).filter(([, ov]) => ov?.brigade_role);
          if (overrideEntries.length === 0) {
            return `   ${bName}: Tab 1 mission = ${tab1Mission} (no phase-specific role overrides)`;
          }
          const overrideText = overrideEntries
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([idx, ov]) => `     ${phaseLabel(idx)}: ${ROLE_LABELS[ov.brigade_role] || ov.brigade_role}`)
            .join('\n');
          return `   ${bName}: Tab 1 mission = ${tab1Mission}\n${overrideText}`;
        }).join('\n');
      })(),
      '',
      'Q-4. SUSTAINMENT',
      hr2,
      'a. Medical materiel:',
      bs ? [
        line('  Blood units required (total)',
          extractMetric(res, ['medical_requirements.blood.total_units','summary.blood_units_required'])
            ?? lg.blood_units_required
            ?? '--'),
        line('  Blood stockout projected',
          bs.blood_stockout_day != null ? 'Day ' + bs.blood_stockout_day : 'No stockout projected'),
        line('  Logistics supply status', lg.supply_status ?? lg.overall_status ?? 'See Tab 5 logistics timeline'),
        line('  NATO medical materiel', 'See Tab 5 logistics timeline for DOS projection'),
      ].join('\n') : '   No logistics data — load Tab 5 Baseline with results.',
      '',
      'b. Medical Materiel resupply (AMedP-1.12):',
      '   Threshold-triggered per AMedP-1.12. Commanded MDOS maintained throughout.',
      '   Resupply schedule: see Tab 4 logistics configuration.',
      '',
      'Q-5. COMMAND AND SIGNAL',
      hr2,
      'a. Command:',
      '   Medical advisor (MEDAD) to Corps Commander.',
      '   Coordinated through J5 per COPD / AJP-5.',
      '   Cross-division medical support requires formal approval (cross-division log).',
      '',
      'b. MEDSITREP reporting:',
      '   Frequency: per Corps SOP.',
      '   Format: STANAG 2348 / AMedP-4.1.',
      '   MEDSITREP generated separately in Tab 6.',
      '',
      'c. Medical Plan Variant Analysis:',
      `   ${variants.filter(v => v.results).length} variant(s) calculated in Tab 6 Medical Plan Variant Analysis.`,
      '   See sensitivity analysis and comparison view for variant outcomes.',
      '',
      'Q-6. TREATMENT OBLIGATIONS (MedROE)',
      hr2,
      (() => {
        const mroe = op.mroe_config;
        if (!mroe || (!mroe.state && !mroe.enabled)) {
          return '   MedROE NOT CONFIGURED — treatment obligations not defined for this scenario.';
        }
        const stateDef = mroe.state ? MROE_STATES[mroe.state] : null;
        const stateLabel = stateDef ? stateDef.label : 'NOT SET';
        const mx = mroe.matrix;
        const defaultMx = (mroe.state && MROE_DEFAULT_MATRICES[mroe.state]) || {};
        let modCount = 0;
        const matrixLines = [];
        if (mx) {
          for (const cat of PATIENT_CATEGORIES) {
            const cell    = mx[cat.key] || {};
            const defCell = defaultMx[cat.key] || {};
            const lleVal    = cell.lle    || 'Y';
            const nonLleVal = cell.nonLle || 'Y';
            const lleMod    = cat.lockTier !== 'A' && lleVal    !== (defCell.lle    || 'Y');
            const nonLleMod = cat.lockTier !== 'A' && nonLleVal !== (defCell.nonLle || 'Y');
            if (lleMod)    modCount++;
            if (nonLleMod) modCount++;
            const lockNote = cat.lockTier === 'A' ? ` [${cat.gcRef || 'LOCKED'} — cannot be modified]` : '';
            const lleNote    = (cell.lleConds?.length    > 0) ? ` [${cell.lleConds.join('; ')}]`    : '';
            const nonLleNote = (cell.nonLleConds?.length > 0) ? ` [${cell.nonLleConds.join('; ')}]` : '';
            matrixLines.push(
              `   ${cat.label.padEnd(28)} LLE: ${(lleVal + (lleMod ? '*' : '')).padEnd(4)}   Non-LLE: ${nonLleVal + (nonLleMod ? '*' : '')}${lleNote}${nonLleNote}${lockNote}`
            );
          }
        }
        const epwVol = mroe.epw_volume || mroe.epw_level || 'NONE';
        const civDen = mroe.civilian_density || mroe.civilian_level || 'NONE';
        const modNote = modCount > 0
          ? `   (* = modified from MRoE ${stateLabel} standard — ${modCount} cell(s) require OPORD auth + LEGAD sign-off)`
          : '';
        return [
          `   MRoE State: ${stateLabel}`,
          `   ${stateDef ? stateDef.description : ''}`,
          '',
          '   Treatment Authorization Matrix in effect:',
          '   ' + '-'.repeat(56),
          `   ${'Category'.padEnd(28)} LLE      Non-LLE`,
          '   ' + '-'.repeat(56),
          ...matrixLines,
          '   ' + '-'.repeat(56),
          modNote,
          '',
          `   EPW patient volume: ${epwVol}`,
          `   EPW evacuation chain: Ground only (GC III Art.19 — not eligible for MEDEVAC)`,
          `   Civilian patient density: ${civDen}`,
          `   Civilian triage entry: R2B (after point-of-injury stabilization)`,
          '',
          '   NOTE: During declared MASCAL all MedROE tiers are suspended — triage by',
          '   clinical need only (AMedP-8.19 Section 4.5).',
        ].filter(l => l !== undefined).join('\n');
      })(),
      '',
      // Q-7: CoA comparison validity (V4-TAB6-001)
      '',
      'Q-7. COA COMPARISON VALIDITY',
      hr2,
      (() => {
        if (!coaSlot1 && !coaSlot2) {
          return '   NOT CONFIGURED — No CoA slots saved. Use Tab 5 Save as CoA A/B to populate.';
        }
        if (!coaSlot1 || !coaSlot2) {
          const which = coaSlot1 ? 'CoA B' : 'CoA A';
          return `   INCOMPLETE — ${which} not saved. Both slots required for a valid comparison.`;
        }
        // Both loaded — run integrity check
        const issues = [];
        const snapA = coaSlot1.snapshot;
        const snapB = coaSlot2.snapshot;
        const r3A = snapA?.medical_configuration?.mtf_configuration?.role3_beds;
        const r3B = snapB?.medical_configuration?.mtf_configuration?.role3_beds;
        if (r3A != null && r3B != null && r3A !== r3B)
          issues.push(`Role 3 beds: ${r3A} vs ${r3B}`);
        const svA = snapA?.operation_parameters?.stratevac_config?.weekly_capacity
                 ?? snapA?.medical_configuration?.stratevac_config?.weekly_capacity;
        const svB = snapB?.operation_parameters?.stratevac_config?.weekly_capacity
                 ?? snapB?.medical_configuration?.stratevac_config?.weekly_capacity;
        if (svA != null && svB != null && svA !== svB)
          issues.push(`STRATEVAC capacity: ${svA} vs ${svB} pts/week`);
        const bA = snapA?.logistics?.medical_supplies?.initial_stock?.prbc_units;
        const bB = snapB?.logistics?.medical_supplies?.initial_stock?.prbc_units;
        if (bA != null && bB != null && bA !== bB)
          issues.push(`Blood stock (PRBC): ${bA} vs ${bB} units`);
        const dosA = snapA?.logistics?.days_of_supply;
        const dosB = snapB?.logistics?.days_of_supply;
        if (dosA != null && dosB != null && dosA !== dosB)
          issues.push(`Days of Supply: ${dosA} vs ${dosB}`);
        const savedA = new Date(coaSlot1.saved_at).toLocaleString();
        const savedB = new Date(coaSlot2.saved_at).toLocaleString();
        const validityLine = issues.length === 0
          ? '   VALID — Planning baseline identical across CoAs (Role 3, STRATEVAC, blood stocks, DOS).'
          : `   INVALID — Planning baseline mismatch in ${issues.length} field(s):\n` +
            issues.map(iss => `     - ${iss}`).join('\n') +
            '\n   LEGAD/J5 review required before Annex Q signature.';
        return [
          `   CoA A: ${coaSlot1.name || 'CoA A'} (saved ${savedA})`,
          `   CoA B: ${coaSlot2.name || 'CoA B'} (saved ${savedB})`,
          '',
          validityLine,
          '',
          '   NOTE: MTF Role 1/2B/2E differences between CoAs are expected (force disposition',
          '   varies by option) and are NOT included in the integrity check (ATP 4-02.6).',
        ].join('\n');
      })(),
      '',
      hr,
      'END ANNEX Q — MEDICAL SUPPORT PLAN',
      `Generated: PSP/CRE Tool v3.1 (Dr. Tatar Radu MD)`,
      'AJP-4.10C / AJMedP-1 / AMedP-1.12 compliant planning tool',
    ];
    return lines.join('\n');
  };

  const renderAnnexQ = () => {
    if (!isLoaded) return null;
    const hasResults = baseline?.results;
    // Planning basis: explicit CoA slot approval takes priority; falls back to legacy
    // variant toggle or baseline approve for backward compatibility.
    const legacyApprovedVariant = variants.find(v => v.approved && v.results);
    const planningBasisApproved =
      approvedCoASlot !== null ||
      !!legacyApprovedVariant ||
      (baselineApproved && !!baseline?.results);

    const approvedCoAName = approvedCoASlot === 1 ? (coaSlot1?.name || 'CoA A')
      : approvedCoASlot === 2 ? (coaSlot2?.name || 'CoA B') : null;
    const approvedVarObj  = approvedVariantId ? variants.find(v => v.id === approvedVariantId) : null;
    const approvedLabel   = approvedCoAName
      ? (approvedVarObj ? `${approvedCoAName} / ${approvedVarObj.name}` : approvedCoAName)
      : legacyApprovedVariant ? legacyApprovedVariant.name
      : 'Baseline';

    const handleDownload = () => {
      const text = generateAnnexQText();
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = 'OPORD_Annex_Q_Medical_Support_Plan.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    // V5-001: Formatted PDF export of Annex Q
    const handleDownloadPDF = async () => {
      // OBS-S97-001: warn if operation name is blank before generating PDF
      const _pdfOpName = baseline?.snapshot?.scenario_name;
      if (!_pdfOpName || _pdfOpName.trim() === '') {
        if (!window.confirm('Warning: Operation name is not set \u2014 the PDF cover will show "NOT SPECIFIED".\n\nContinue anyway?')) return;
      }
      try {
        const blob = await generateAnnexQToPDF(
          baseline,
          coaSlot1,
          coaSlot2,
          variants,
          approvedLabel
        );
        const opName = (baseline?.snapshot?.scenario_name || 'OP').replace(/\s+/g, '_').toUpperCase();
        const date   = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        downloadBlob(blob, `OPORD_AnnexQ_${opName}_${date}.pdf`);
      } catch (err) {
        console.error('Annex Q PDF export failed:', err);
        setSnackbar({ open: true, message: `Annex Q PDF failed: ${err.message}`, severity: 'error' });
      }
    };

    return (
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <GavelIcon color="primary" />
          <Typography variant="h6">OPORD Annex Q — Medical Support Plan</Typography>
          <Chip label="AJP-4.10C" size="small" color="primary" variant="outlined" />
          {planningBasisApproved && (
            <Chip label={`Approved: ${approvedLabel}`} size="small" color="success" icon={<CheckCircleIcon />} />
          )}
          <Box sx={{ flex: 1 }} />
          {/* V5-001: PDF export button */}
          <Tooltip title={!planningBasisApproved ? 'Approve a variant or the baseline to enable export' : 'Export formatted Annex Q PDF (AJP-4.10C)'}>
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleDownloadPDF}
                disabled={!planningBasisApproved}
                color="primary"
                sx={{ mr: 1 }}
              >
                Download PDF
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={!planningBasisApproved ? 'Approve a variant or the baseline to enable export' : 'Export plain-text version'}>
            <span>
          <Button
            variant="outlined"
            size="small"
            startIcon={<GetAppIcon />}
            onClick={handleDownload}
            disabled={!planningBasisApproved}
            color="primary"
          >
            .txt
          </Button>
            </span>
          </Tooltip>
        </Box>
        {!planningBasisApproved && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            <strong>Approval required.</strong> Before exporting Annex Q, confirm the planning basis:{' '}
            use the <strong>Approve CoA A / CoA B</strong> buttons in Phase 3 (Analyse) — available as soon as at least one CoA slot is saved with results —
            or use the toggle below to approve the baseline plan directly.
          </Alert>
        )}
        {/* Step 1 — CoA approval (when CoA slots exist, use buttons in Phase 3 comparison).
            Fallback: approve baseline directly when full CoA comparison is not available. */}
        {!(coaSlot1 && coaSlot2) && !legacyApprovedVariant && hasResults && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, p: 1.5, bgcolor: baselineApproved ? 'success.50' : 'grey.50', borderRadius: 1, border: '1px solid', borderColor: baselineApproved ? 'success.200' : 'grey.200' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={baselineApproved}
                  onChange={e => {
                    setBaselineApproved(e.target.checked);
                    lsWrite(EXERCISE_STORAGE_KEYS.BASELINE_APPROVED, e.target.checked);
                  }}
                  color="success"
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  <strong>Approve Baseline as planning basis</strong>
                  {' \u2014 '}
                  <Typography component="span" variant="body2" color="text.secondary">
                    Use this when the baseline scenario requires no stress-test modifications.
                  </Typography>
                </Typography>
              }
            />
          </Box>
        )}

        {/* Step 2 — Variant selection radio group (shown when a CoA is approved) */}
        {approvedCoASlot !== null && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
            <FormControl component="fieldset">
              {/* BUG-S97-010 FIX: \u2014 in JSX text renders literally — move to JS string in {} */}
              <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary', fontSize: '0.875rem' }}>
                {'Planning variant \u2014 '}{approvedCoASlot === 1 ? (coaSlot1?.name || 'CoA A') : (coaSlot2?.name || 'CoA B')}
              </FormLabel>
              <RadioGroup
                value={approvedVariantId ?? '__base__'}
                onChange={e => {
                  const val = e.target.value === '__base__' ? null : e.target.value;
                  handleApproveCoA(approvedCoASlot, val);
                }}
              >
                {/* Base CoA option — always first */}
                <FormControlLabel
                  value="__base__"
                  control={<Radio size="small" color="success" />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        Base {approvedCoASlot === 1 ? (coaSlot1?.name || 'CoA A') : (coaSlot2?.name || 'CoA B')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">No stress-test modification applied</Typography>
                    </Box>
                  }
                />
                {/* Calculated variants */}
                {variants.filter(v => v.results).map(v => (
                  <FormControlLabel
                    key={v.id}
                    value={v.id}
                    control={<Radio size="small" color="success" />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="bold">{v.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Object.entries(v.modifications || {})
                            .filter(([, val]) => val !== 0)
                            .slice(0, 3)
                            .map(([k, val]) => `${k}: ${val > 0 ? '+' : ''}${val}%`)
                            .join(' \u00b7 ') || 'Stress-test variant'}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
                {/* Uncalculated variants — disabled */}
                {variants.filter(v => !v.results).map(v => (
                  <FormControlLabel
                    key={v.id}
                    value={v.id}
                    disabled
                    control={<Radio size="small" />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.disabled">{v.name}</Typography>
                        <Chip label="not calculated" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Formal Medical Support Plan per AJP-4.10C — five sections: Situation / Mission /
          Execution / Sustainment / Command & Signal. Coordinated through J5/COPD.
          Complements the MEDSITREP (situation report).
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {['Q-1 Situation','Q-2 Mission','Q-3 Execution','Q-4 Sustainment','Q-5 Cmd & Sig','Q-6 MedROE','Q-7 CoA Validity'].map((s, i) => (
            <Chip
              key={i}
              label={s}
              size="small"
              color={hasResults ? 'success' : 'default'}
              variant={hasResults ? 'filled' : 'outlined'}
              sx={{ fontSize: '0.68rem' }}
            />
          ))}
        </Box>
        {!hasResults && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            <strong>Config snapshot — partial data.</strong> Q-1 Situation and Q-2 Mission
            sections populate from scenario configuration. Casualty estimates (Q-3 Execution)
            will show placeholder text. Load a Tab 5 Baseline with calculated results for
            full Annex Q population.
          </Alert>
        )}
        {/* Q-6 MedROE visual summary */}
        {(() => {
          const mroe = baseline?.snapshot?.operation_parameters?.mroe_config
                    ?? scenario?.operation_parameters?.mroe_config;
          if (!mroe) {
            return (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                <strong>MedROE NOT CONFIGURED</strong> — treatment obligations not defined for this scenario.
                Configure in Tab 2 Advanced Features.
              </Alert>
            );
          }
          const stateDef = mroe.state ? MROE_STATES[mroe.state] : null;
          const mx = mroe.matrix;
          const defaultMx = (mroe.state && MROE_DEFAULT_MATRICES[mroe.state]) || {};
          let modCount = 0;
          if (mx) {
            for (const cat of PATIENT_CATEGORIES) {
              if (cat.lockTier === 'A') continue;
              const cell    = mx[cat.key] || {};
              const defCell = defaultMx[cat.key] || {};
              if ((cell.lle    || 'Y') !== (defCell.lle    || 'Y')) modCount++;
              if ((cell.nonLle || 'Y') !== (defCell.nonLle || 'Y')) modCount++;
            }
          }
          const cellColor = (val) => {
            if (val === 'Y')  return 'success';
            if (val === 'Y*') return 'warning';
            if (val === 'N')  return 'error';
            return 'default';
          };
          return (
            <Box sx={{ mt: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">Q-6 — Treatment Obligations (MedROE)</Typography>
                {stateDef ? (
                  <Chip label={`MRoE: ${stateDef.label}`} size="small" color={stateDef.color} />
                ) : (
                  <Chip label={t('tab6.state_not_set')} size="small" color="default" variant="outlined" />
                )}
                {modCount > 0 && (
                  <Chip
                    label={`${modCount} cell${modCount !== 1 ? 's' : ''} modified`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>
              {mx ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>LLE</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>Non-LLE</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {PATIENT_CATEGORIES.map((cat) => {
                        const cell    = mx[cat.key] || {};
                        const defCell = defaultMx[cat.key] || {};
                        const lleVal    = cell.lle    || 'Y';
                        const nonLleVal = cell.nonLle || 'Y';
                        const lleMod    = cat.lockTier !== 'A' && lleVal    !== (defCell.lle    || 'Y');
                        const nonLleMod = cat.lockTier !== 'A' && nonLleVal !== (defCell.nonLle || 'Y');
                        return (
                          <TableRow key={cat.key}>
                            <TableCell>
                              <Typography variant="body2">{cat.label}</Typography>
                              {cat.gcRef && (
                                <Typography variant="caption" color="text.secondary">{cat.gcRef}</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={lleVal}
                                size="small"
                                color={cellColor(lleVal)}
                                variant={lleMod ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={nonLleVal}
                                size="small"
                                color={cellColor(nonLleVal)}
                                variant={nonLleMod ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Matrix not configured — select a MRoE state in Tab 2.
                </Typography>
              )}
              {modCount > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {modCount} cell{modCount !== 1 ? 's' : ''} modified from MRoE {mroe.state} standard.
                  OPORD authorization and LEGAD verification required.
                </Alert>
              )}
            </Box>
          );
        })()}
      </Paper>
    );
  };

  // ============================================================================
  // STEP 5: REQUESTS TO HIGHER
  // New accordion rendered below Annex Q — only when planningBasisApproved.
  // Calls module-scope detectPlanningGaps(); escalates all gaps to CRITICAL
  // (Improvement 6: approved with unconfirmed assumption).
  // ============================================================================
  const renderRequestsToHigher = () => {
    if (!isLoaded) return null;
    const legacyApprovedVariantRTH = variants.find(v => v.approved && v.results);
    const planningBasisApprovedRTH =
      approvedCoASlot !== null ||
      !!legacyApprovedVariantRTH ||
      (baselineApproved && !!baseline?.results);
    if (!planningBasisApprovedRTH) return null;

    // Resolve approved CoA data
    const approvedCoADataRTH = approvedVariantId
      ? variants.find(v => v.id === approvedVariantId)
      : approvedCoASlot === 1 ? coaSlot1
      : approvedCoASlot === 2 ? coaSlot2
      : legacyApprovedVariantRTH ?? baseline;

    const approvedSnapRTH    = approvedCoADataRTH?.snapshot ?? null;
    const approvedResultsRTH = approvedCoADataRTH?.results   ?? null;

    // Detect gaps from approved plan
    const rawGaps = detectPlanningGaps(approvedSnapRTH, approvedResultsRTH);

    // Improvement 6: escalate all gaps to CRITICAL (approved with unconfirmed assumption)
    const escalatedGaps = rawGaps.map(g => ({
      ...g,
      originalPriority: g.priority,
      priority: 'CRITICAL',
      escalationNote: g.priority !== 'CRITICAL'
        ? `Escalated from ${g.priority} \u2014 approved with unconfirmed assumption`
        : 'Critical gap present in approved plan',
    }));

    // Header badge counts
    const critCount = escalatedGaps.length;

    // Approved plan label
    const approvedCoANameRTH = approvedCoASlot === 1 ? (coaSlot1?.name || 'CoA A')
      : approvedCoASlot === 2 ? (coaSlot2?.name || 'CoA B') : null;
    const approvedVarNameRTH = approvedVariantId
      ? (variants.find(v => v.id === approvedVariantId)?.name || 'variant') : null;
    const basisLabelRTH = approvedCoANameRTH
      ? (approvedVarNameRTH ? `${approvedCoANameRTH} / ${approvedVarNameRTH}` : approvedCoANameRTH)
      : legacyApprovedVariantRTH ? legacyApprovedVariantRTH.name : 'Baseline';

    // Approved-at time from storage
    const approvedAtRTH = (() => {
      try {
        const ap = lsRead(APPROVED_PLAN_KEY);
        if (ap?.approved_at) {
          return new Date(ap.approved_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
      } catch (_) { /* non-fatal */ }
      return null;
    })();

    // Selected gap ids
    const selectedIdsRTH = Object.keys(rthGapSelection).filter(id => rthGapSelection[id]);

    // Build populated RFI text for a gap (replacing placeholders with user values)
    const buildGapRFIText = (gap) => gap.rfi(approvedSnapRTH, approvedResultsRTH)
      .replace('[PLANNER TO COMPLETE]', rthRequiredBy[gap.id] || '[PLANNER TO COMPLETE]')
      .replace('[OPTIONAL]', rthRemarks[gap.id] || '[OPTIONAL]');

    const priorityChipColor = (p) =>
      p === 'CRITICAL' ? 'error' : p === 'HIGH' ? 'warning' : 'default';

    return (
      <Accordion sx={{ mt: 2, border: '2px solid', borderColor: 'error.main' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
            <ReportProblemIcon color="error" />
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main', mr: 1 }}>
              {/* \u2014 must be a JS expression in JSX text nodes */}
              {'Planning Actions \u2014 Requests to Higher'}
            </Typography>
            <Chip
              label={`Approved: ${basisLabelRTH}${approvedAtRTH ? ' \u00b7 ' + approvedAtRTH : ''}`}
              size="small"
              color="success"
              icon={<CheckCircleIcon />}
              sx={{ fontSize: '0.68rem' }}
            />
            {critCount > 0 && (
              <Chip
                label={`${critCount} CRITICAL`}
                size="small"
                color="error"
                sx={{ fontSize: '0.68rem', fontWeight: 700 }}
              />
            )}
            {escalatedGaps.length === 0 && (
              <Chip label="No planning gaps detected" size="small" color="success" sx={{ fontSize: '0.68rem' }} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {escalatedGaps.length === 0 ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              No planning gaps detected in the approved CoA. All R2E / R3 / evacuation
              / logistics parameters are within acceptable bounds.
            </Alert>
          ) : (
            <>
              {/* ── Action bar ─────────────────────────────────────────────── */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5, pt: 1.5 }}>
                <Button size="small" variant="outlined" color="error"
                  onClick={() => {
                    const upd = {};
                    escalatedGaps.forEach(g => { upd[g.id] = true; });
                    setRthGapSelection(upd);
                  }}>
                  Select Critical
                </Button>
                <Button size="small" variant="outlined"
                  onClick={() => {
                    const upd = {};
                    escalatedGaps.forEach(g => { upd[g.id] = true; });
                    setRthGapSelection(upd);
                  }}>
                  Select All
                </Button>
                <Button size="small" variant="outlined"
                  onClick={() => setRthGapSelection({})}>
                  Clear
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  disabled={selectedIdsRTH.length === 0}
                  onClick={() => {
                    const texts = escalatedGaps
                      .filter(g => selectedIdsRTH.includes(g.id))
                      .map(g => buildGapRFIText(g));
                    navigator.clipboard.writeText(texts.join('\n\n' + '='.repeat(60) + '\n\n'))
                      .catch(() => {});
                    setSnackbar({ open: true, severity: 'success', message: 'Requests copied to clipboard' });
                  }}
                >
                  Copy Selected ({selectedIdsRTH.length})
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  startIcon={<SummarizeIcon />}
                  onClick={() => {
                    const chosen = selectedIdsRTH.length > 0
                      ? escalatedGaps.filter(g => selectedIdsRTH.includes(g.id))
                      : escalatedGaps;
                    const header = [
                      'PLANNING GAPS REPORT \u2014 REQUESTS TO HIGHER',
                      `Approved basis: ${basisLabelRTH}${approvedAtRTH ? ' (approved ' + approvedAtRTH + ')' : ''}`,
                      `Generated: ${new Date().toLocaleString()}`,
                      `Total gaps: ${escalatedGaps.length} | In report: ${chosen.length}`,
                      '='.repeat(60),
                    ].join('\n');
                    const body = chosen
                      .map(g => `[${g.priority}] ${g.category.toUpperCase()} \u2014 ${g.title}\n\n${buildGapRFIText(g)}`)
                      .join('\n\n' + '='.repeat(60) + '\n\n');
                    setRthReportText(header + '\n\n' + body);
                    setRthReportDialog(true);
                  }}
                >
                  Generate Report
                </Button>
              </Box>

              {/* ── Gap list ───────────────────────────────────────────────── */}
              {escalatedGaps.map((gap) => {
                const isSelected = !!rthGapSelection[gap.id];
                const isOpen     = !!rthPreviewOpen[gap.id];
                return (
                  <Paper
                    key={gap.id}
                    elevation={isSelected ? 3 : 1}
                    sx={{
                      mb: 1, p: 1.5,
                      borderLeft: '4px solid',
                      borderLeftColor: isSelected ? 'error.main' : 'divider',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={() => setRthGapSelection(prev => ({ ...prev, [gap.id]: !prev[gap.id] }))}
                        sx={{ mt: -0.5, p: 0.5 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.5 }}>
                          <Chip
                            label={gap.priority}
                            size="small"
                            color={priorityChipColor(gap.priority)}
                            sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700 }}
                          />
                          <Chip
                            label={gap.category}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                          {/* FEA-S97-001: show RFI vs RFA type */}
                          {gap.type && (
                            <Chip
                              label={gap.type}
                              size="small"
                              color={gap.type === 'RFA' ? 'error' : 'primary'}
                              variant="outlined"
                              sx={{ fontSize: '0.6rem', height: 18, fontWeight: 700 }}
                            />
                          )}
                          {gap.originalPriority !== 'CRITICAL' && (
                            <Chip
                              label={`was ${gap.originalPriority}`}
                              size="small"
                              variant="outlined"
                              color="warning"
                              sx={{ fontSize: '0.6rem', height: 18 }}
                            />
                          )}
                          <Typography variant="caption" sx={{ fontWeight: 600, ml: 0.5 }}>
                            {gap.title}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          {gap.implication}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          <strong>To:</strong> {gap.to}
                          {gap.escalationNote && (
                            <Box component="span" sx={{ ml: 1, color: 'warning.dark' }}>
                              {'\u26a0 '}{gap.escalationNote}
                            </Box>
                          )}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="text"
                        endIcon={
                          <ExpandMoreIcon
                            sx={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }}
                          />
                        }
                        onClick={() => setRthPreviewOpen(prev => ({ ...prev, [gap.id]: !prev[gap.id] }))}
                        sx={{ flexShrink: 0, fontSize: '0.7rem' }}
                      >
                        {isOpen ? 'Collapse' : `Preview ${gap.type || 'RFI'}`}
                      </Button>
                    </Box>

                    {/* ── RFI preview panel ─────────────────────────────── */}
                    {isOpen && (
                      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography
                          component="pre"
                          variant="caption"
                          sx={{
                            fontFamily: 'monospace',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            bgcolor: 'action.hover',
                            p: 1.5,
                            borderRadius: 1,
                            display: 'block',
                            fontSize: '0.7rem',
                            mb: 1,
                          }}
                        >
                          {buildGapRFIText(gap)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <TextField
                            size="small"
                            label={t('tab6.required_by_label')}
                            value={rthRequiredBy[gap.id] || ''}
                            onChange={(e) =>
                              setRthRequiredBy(prev => ({ ...prev, [gap.id]: e.target.value }))
                            }
                            placeholder="e.g. D-2, 1200hrs"
                            sx={{ flex: 1, minWidth: 160 }}
                          />
                          <TextField
                            size="small"
                            label={t('tab6.remarks_label')}
                            value={rthRemarks[gap.id] || ''}
                            onChange={(e) =>
                              setRthRemarks(prev => ({ ...prev, [gap.id]: e.target.value }))
                            }
                            placeholder={t('tab6.optional_context_placeholder')}
                            sx={{ flex: 2, minWidth: 200 }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => {
                              navigator.clipboard.writeText(buildGapRFIText(gap)).catch(() => {});
                              setSnackbar({ open: true, severity: 'success', message: 'RFI copied to clipboard' });
                            }}
                          >
                            Copy RFI
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  // ============================================================================
  // ENH-006: Dynamic MTF Coverage Gap Panel
  // Reads per_facility_evacuation_assets from the loaded baseline snapshot and
  // renders a compact timeline for any facility with activate_day != null.
  // Shows: facility, role, activate/deactivate days, setup hours, ramp, gap alert.
  // ============================================================================
  const renderDynamicMTFPanel = () => {
    const perFacility = baseline?.snapshot?.medical_configuration?.per_facility_evacuation_assets;
    if (!Array.isArray(perFacility) || perFacility.length === 0) return null;

    const dynamic = perFacility.filter(f => f.activate_day != null || f.deactivate_day != null);
    if (dynamic.length === 0) return null;

    const opDuration = baseline?.snapshot?.operation_parameters?.duration_days ?? 14;

    // Capacity fraction ramp (mirrors isFacilityOperational in casualtyCalculator)
    const getCapFraction = (f, day) => {
      const act = f.activate_day ?? 1;
      const deact = f.deactivate_day;
      if (day < act) return 0;
      if (deact != null && day > deact) return 0;
      const dAfter = day - act;
      const hrs = f.setup_hours ?? 0;
      if (hrs === 0) return 1.0;
      if (hrs <= 12) { const r = [0.5, 1.0]; return dAfter >= r.length ? 1 : r[dAfter]; }
      if (hrs <= 24) { const r = [0.0, 0.5, 1.0]; return dAfter >= r.length ? 1 : r[dAfter]; }
      if (hrs <= 48) { const r = [0.0, 0.0, 0.5, 1.0]; return dAfter >= r.length ? 1 : r[dAfter]; }
      const steps = Math.ceil(hrs / 24);
      const r = [...Array(steps).fill(0), 1.0];
      return dAfter >= r.length ? 1 : r[dAfter];
    };

    const days = Array.from({ length: Math.min(opDuration, 20) }, (_, i) => i + 1);

    return (
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimelineIcon color="primary" />
          Dynamic MTF Activation Timeline
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Facilities with non-default activation windows. Grey = inactive, amber = setup ramp, green = full capacity.
          Set in Tab 3 facility cards. Calculator applies these per-day.
        </Typography>

        {dynamic.map((f, fi) => {
          // BUG-FACILITY-NAME-001 FIX: map raw keys to human-readable NATO echelon labels
          const _FACILITY_LABELS = {
            ROLE1: 'Role 1', ROLE_1: 'Role 1',
            ROLE2_BASIC: 'Role 2 Basic', ROLE_2_BASIC: 'Role 2 Basic',
            ROLE2_ENHANCED: 'Role 2 Enhanced', ROLE_2_ENHANCED: 'Role 2 Enhanced',
            ROLE3: 'Role 3', ROLE_3: 'Role 3',
            ROLE4: 'Role 4', ROLE_4: 'Role 4',
          };
          const label = f.facility_type
            ? `${_FACILITY_LABELS[f.facility_type] ?? f.facility_type.replace(/_/g,' ')} — ${f.facility_id}`
            : f.facility_id;
          const gapDays = days.filter(d => getCapFraction(f, d) === 0).length;
          const rampDays = days.filter(d => { const c = getCapFraction(f, d); return c > 0 && c < 1; }).length;

          return (
            <Box key={fi} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle2">{label}</Typography>
                {gapDays > 0 && (
                  <Chip label={`${gapDays}d inactive`} size="small" color="error" variant="outlined" />
                )}
                {rampDays > 0 && (
                  <Chip label={`${rampDays}d ramp`} size="small" color="warning" variant="outlined" />
                )}
                <Typography variant="caption" color="text.secondary">
                  Activate D{f.activate_day ?? 1}
                  {f.deactivate_day != null ? ` · Deactivate D${f.deactivate_day}` : ''}
                  {f.setup_hours != null ? ` · ${f.setup_hours}h setup` : ''}
                </Typography>
              </Box>

              {/* Day strip */}
              <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
                {days.map(d => {
                  const frac = getCapFraction(f, d);
                  const bg = frac === 0 ? 'grey.300'
                           : frac < 1  ? 'warning.light'
                           :             'success.light';
                  return (
                    <Tooltip key={d} title={`D${d}: ${Math.round(frac * 100)}% capacity`}>
                      <Box sx={{
                        width: 22, height: 22, borderRadius: 0.5,
                        bgcolor: bg, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', cursor: 'default'
                      }}>
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600 }}>
                          {d}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
                {opDuration > 20 && (
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 0.5 }}>
                    +{opDuration - 20} more days
                  </Typography>
                )}
              </Box>

              {gapDays > 0 && (
                <Alert severity="warning" sx={{ mt: 0.5, py: 0.5 }}>
                  <Typography variant="caption">
                    <strong>Coverage gap:</strong> This facility has no capacity for {gapDays} day(s).
                    Ensure adjacent facilities can absorb patients during this window.
                  </Typography>
                </Alert>
              )}
            </Box>
          );
        })}
      </Paper>
    );
  };

  // ============================================================================
  // ENH-009: PER-NATION LOGISTICS BREAKDOWN
  // Display-only. Reads baseline.snapshot.force.brigades[], groups by
  // nationalStandard, shows consumption rates + MDOS + DOS thresholds +
  // NATO-delta chips per nation. Zero calculator changes.
  // Source: nationalLogistics.js v2.3.0
  // ============================================================================

  const renderPerNationLogistics = () => {
    const brigades = baseline?.snapshot?.force?.brigades;
    if (!Array.isArray(brigades) || brigades.length === 0) return null;

    // --- Build per-nation brigade groups -----------------------------------
    const snap        = baseline.snapshot ?? {};
    const fallbackNat = snap.force?.nationalStandard ?? 'NATO';
    const brigStds    = snap.force?.brigadeStandards ?? {};

    // Map: nationalStandard -> { brigades: [], totalStrength: 0 }
    const nationMap = {};
    for (const brig of brigades) {
      const nat = brig.nationalStandard
               ?? brigStds[brig.id]
               ?? fallbackNat;
      if (!nationMap[nat]) nationMap[nat] = { brigades: [], totalStrength: 0 };
      nationMap[nat].brigades.push(brig);
      nationMap[nat].totalStrength += (brig.strength ?? 0);
    }

    const nations = Object.keys(nationMap);

    // --- Metadata lookup helper -------------------------------------------
    const getMeta = (code) =>
      AVAILABLE_NATIONAL_STANDARDS.find(n => n.code === code) ?? {
        code,
        shortLabel: code,
        name: code,
        flag: '',
      };

    // --- Single-nation guard ----------------------------------------------
    if (nations.length <= 1) {
      return (
        <Paper elevation={2} sx={{ p: 2.5, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LocalShippingIcon color="primary" />
            <Typography variant="h6">{t('tab6.per_nation_logistics')}</Typography>
          </Box>
          <Alert severity="info">
            Single-nation force ({getMeta(nations[0] ?? fallbackNat).name}) —
            no multinational breakdown required.
            This panel activates when the force contains units from multiple
            national standards.
          </Alert>
        </Paper>
      );
    }

    // --- Rate formatting helpers ------------------------------------------
    const fmtRate = (v) => (v == null ? '—' : Number(v).toFixed(1));

    // --- Per-nation card renderer -----------------------------------------
    const renderNationCard = (natCode, group) => {
      const meta    = getMeta(natCode);
      const lgConf  = getNationalLogistics(natCode);
      const rates   = lgConf.consumption_rates ?? {};
      const mdos    = lgConf.mdos_lines        ?? {};
      const thresh  = lgConf.dos_thresholds    ?? {};
      const diffObj = getNationalDifferences(natCode);
      const notes   = (lgConf.notes && typeof lgConf.notes === 'object')
                      ? Object.values(lgConf.notes).filter(Boolean)
                      : [];

      // BUG-166 FIX: getNationalDifferences().differences may be an object, not an array.
      // ?? [] only guards null/undefined — if differences is an object .filter throws.
      // Normalise to array regardless of what the constant returns.
      const differencesArray = Array.isArray(diffObj?.differences)
        ? diffObj.differences
        : Object.values(diffObj?.differences ?? {});

      // Only show consumption_rates diffs — skip mdos labels / nested objects
      const rateDiffs = differencesArray.filter(d =>
        d?.field?.startsWith('consumption_rates.')
      );

      // Highlight color for nation header chip
      const chipColor = natCode === 'NATO'       ? 'primary'
                      : natCode === 'US'          ? 'error'
                      : natCode === 'ROMANIAN'    ? 'warning'
                      : natCode === 'GERMAN'      ? 'default'
                      : natCode === 'POLISH'      ? 'success'
                      : natCode === 'BRITISH'     ? 'info'
                      : 'default';

      return (
        <Paper key={natCode} variant="outlined" sx={{ p: 2, mb: 2 }}>
          {/* Nation header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Chip
              label={`${meta.flag ? meta.flag + ' ' : ''}${meta.shortLabel}`}
              color={chipColor}
              size="small"
              sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
            />
            <Typography variant="subtitle1" fontWeight="bold">
              {meta.name}
            </Typography>
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              <Chip
                label={`${group.brigades.length} brigade${group.brigades.length !== 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${group.totalStrength.toLocaleString()} pers`}
                size="small"
                variant="outlined"
                color="primary"
              />
            </Box>
          </Box>

          <Grid container spacing={2}>
            {/* Blood consumption rates */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Blood Consumption (units / T1 casualty)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ py: 0.5, fontSize: '0.7rem' }}>Product</TableCell>
                      <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>T1</TableCell>
                      <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>T2</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { label: 'PRBC',       t1: rates.prbc_per_t1,      t2: rates.prbc_per_t2 },
                      { label: 'FFP',        t1: rates.ffp_per_t1,       t2: rates.ffp_per_t2  },
                      { label: 'Platelets',  t1: rates.platelets_per_t1, t2: rates.platelets_per_t2 },
                      { label: 'IV Fluids L',t1: rates.iv_fluids_per_t1, t2: rates.iv_fluids_per_t2 },
                    ].map(row => (
                      <TableRow key={row.label} hover>
                        <TableCell sx={{ py: 0.5, fontSize: '0.7rem' }}>{row.label}</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', fontWeight: 'bold' }}>
                          {fmtRate(row.t1)}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          {fmtRate(row.t2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* MDOS structure */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                MDOS Structure
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ py: 0.5, fontSize: '0.7rem' }}>Line</TableCell>
                      <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>Days</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.7rem' }}>Location</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { line: 'Line 1', data: mdos.LINE_1, role: 'Role 1' },
                      { line: 'Line 2', data: mdos.LINE_2, role: 'Role 2' },
                      { line: 'Line 3', data: mdos.LINE_3, role: 'Role 2E/3' },
                    ].map(row => (
                      <TableRow key={row.line} hover>
                        <TableCell sx={{ py: 0.5, fontSize: '0.7rem' }}>{row.line}</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', fontWeight: 'bold' }}>
                          {row.data?.days ?? '—'}
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.7rem', color: 'text.secondary' }}>
                          {row.role}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: 'primary.50' }}>
                      <TableCell sx={{ py: 0.5, fontSize: '0.7rem', fontWeight: 'bold' }}>Total</TableCell>
                      <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', fontWeight: 'bold' }}>
                        {mdos.TOTAL?.days ?? '—'}
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.7rem' }} />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              {/* DOS thresholds */}
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
                <Chip label={`Critical: ${thresh.critical ?? '—'}d`}
                  size="small" color="error" variant="outlined"
                  sx={{ fontSize: '0.6rem', height: 18 }} />
                <Chip label={`Minimum: ${thresh.minimum ?? '—'}d`}
                  size="small" color="warning" variant="outlined"
                  sx={{ fontSize: '0.6rem', height: 18 }} />
                <Chip label={`Adequate: ${thresh.adequate ?? '—'}d`}
                  size="small" color="success" variant="outlined"
                  sx={{ fontSize: '0.6rem', height: 18 }} />
              </Box>
            </Grid>

            {/* NATO differences */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                {natCode === 'NATO' ? 'NATO Baseline (no overrides)' : `Differences from NATO Baseline`}
              </Typography>
              {natCode === 'NATO' || rateDiffs.length === 0 ? (
                <Typography variant="caption" color="text.secondary" fontStyle="italic">
                  {natCode === 'NATO'
                    ? 'This is the NATO baseline — all other nations are measured against it.'
                    : 'No consumption rate differences from NATO baseline.'}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {rateDiffs.map((d, i) => {
                    const fieldName = d.field.replace('consumption_rates.', '').replace(/_/g, ' ');
                    const delta = d.national_value - d.nato_value;
                    const isHigher = delta > 0;
                    return (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          label={isHigher ? `+${delta.toFixed(1)}` : delta.toFixed(1)}
                          size="small"
                          color={isHigher ? 'error' : 'success'}
                          variant="outlined"
                          sx={{ fontSize: '0.6rem', height: 18, minWidth: 48 }}
                        />
                        <Typography variant="caption" color="text.secondary"
                          sx={{ fontSize: '0.65rem' }}>
                          {fieldName}
                          {' '}({d.national_value} vs NATO {d.nato_value})
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Doctrinal notes */}
              {notes.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary"
                    display="block" sx={{ mb: 0.25, fontWeight: 'bold' }}>
                    Doctrinal notes
                  </Typography>
                  {notes.map((note, i) => (
                    <Typography key={i} variant="caption" color="text.secondary"
                      display="block" sx={{ fontSize: '0.65rem', lineHeight: 1.4 }}>
                      {note}
                    </Typography>
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>

          {/* Brigade list */}
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Brigades under this standard:{' '}
              {group.brigades.map(b => b.name ?? b.id ?? '?').join(', ')}
            </Typography>
          </Box>
        </Paper>
      );
    };

    return (
      <Paper elevation={2} sx={{ p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LocalShippingIcon color="primary" />
          <Typography variant="h6">{t('tab6.per_nation_logistics')}</Typography>
          <Chip
            label={`${nations.length} nations`}
            size="small" color="primary" variant="outlined"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each contributing nation has distinct consumption rates, MDOS structure, and DOS
          thresholds derived from national doctrine. The corps-level calculator uses blended
          planning factors; this breakdown shows nation-specific logistics doctrine for
          educational and coordination purposes.
          Source: nationalLogistics.js v2.3.0 / NATO AMedP-9.1 / national medical doctrine.
        </Typography>

        {nations.map(nat => renderNationCard(nat, nationMap[nat]))}

        {/* BUG-6H-001 FIX: planning note was hardcoded to US/GBR/ROU/POL regardless of
            actual scenario nations. Now dynamically lists actual nations from nationMap. */}
        {(() => {
          const nationMdos = nations
            .map(nat => {
              const lg = getNationalLogistics(nat);
              const name = AVAILABLE_NATIONAL_STANDARDS.find(n => n.code === nat)?.name ?? nat;
              // BUG-T44 fix: correct path is mdos_lines.TOTAL.days, not mdos_structure.total_mdos
              const mdos = lg?.mdos_lines?.TOTAL?.days ?? null;
              return { nat, name, mdos };
            })
            .filter(n => n.mdos !== null)
            .sort((a, b) => (b.mdos ?? 0) - (a.mdos ?? 0));
          const highNat = nationMdos.filter((_, i) => i < Math.ceil(nationMdos.length / 2));
          const lowNat  = nationMdos.filter((_, i) => i >= Math.ceil(nationMdos.length / 2));
          const fmt = arr => arr.map(n => `${n.name} ${n.mdos}d`).join(', ') || '—';
          const hasDivergence = nationMdos.length > 1
            && ((nationMdos[0]?.mdos ?? 0) - (nationMdos[nationMdos.length - 1]?.mdos ?? 0)) >= 5;
          return (
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="caption">
                <strong>Planning note:</strong> Multinational logistics coordination requires
                pre-operation alignment on resupply standards.
                {hasDivergence
                  ? ` Nations with higher MDOS requirements (${fmt(highNat)}) may need separate
                    supply chains from nations with shorter standards (${fmt(lowNat)}).`
                  : ` This scenario's nations have comparable MDOS requirements: ${fmt(nationMdos)}.`
                }
                {' '}Address in OPORD Annex Q Sustainment paragraph.
                See AJP-4.10C §5.4 (HNS) and STANAG 2228.
              </Typography>
            </Alert>
          );
        })()}
      </Paper>
    );
  };

  const renderNoResultsPlaceholder = (panelTitle) => (
    <Paper variant="outlined" sx={{ p: 3, mb: 2, textAlign: 'center' }}>
      <InfoOutlinedIcon color="disabled" sx={{ fontSize: 40, mb: 1 }} />
      <Typography variant="body2" color="text.secondary">
        {panelTitle} - no calculated results available.
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Load a Tab 5 Baseline (Section 1) to view this panel.
      </Typography>
    </Paper>
  );

  // -- TAB6-PRELIMINARY-ASSESS: Preliminary Assessment export ------------------
  function renderPreliminaryAssessment() {
    if (!baseline?.results) return null;
    const s = baselineSummary;
    if (!s) return null;

    const ghPct    = s.golden_hour_pct ?? null;
    const ghTarget = 90;
    const medevacDeficit = (ghPct != null && ghPct < ghTarget)
      ? `${(ghTarget - ghPct).toFixed(1)}% of WIA below golden-hour threshold`
      : 'Within acceptable limits (≥90%)';

    const netLoss = (s.total_casualties != null && s.rtd != null)
      ? s.total_casualties - s.rtd
      : null;

    const handleExportPA = () => {
      const opName  = baseline.snapshot?.scenario_name ?? 'UNNAMED OP';
      const opDays  = baseline.snapshot?.operation_parameters?.duration_days ?? '?';
      const genTime = new Date().toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
      const lines = [
        '═══════════════════════════════════════════════════════════════',
        'PRELIMINARY MEDICAL PLANNING ASSESSMENT',
        `Operation: ${opName}   Duration: ${opDays} days`,
        `Generated: ${genTime}   Tool: PSP/CRE v3.0`,
        '═══════════════════════════════════════════════════════════════',
        '',
        '1. SURGICAL CAPACITY',
        `   First MASCAL overload day: ${s.r2b_mascal_days != null ? `Day ${s.r2b_mascal_days}` : 'No overload projected'}`,
        `   Recommendation: Pre-position surgical augmentation before Day ${s.r2b_mascal_days ?? 'N/A'}.`,
        '',
        '2. BLOOD SUPPLY',
        `   Blood stockout day: ${s.blood_stockout_day != null ? `Day ${s.blood_stockout_day}` : 'No stockout projected'}`,
        `   Recommendation: ${s.blood_stockout_day != null ? `Request LOGFAS blood resupply NLT Day ${s.blood_stockout_day - 3}.` : 'Current stock sufficient for planned duration.'}`,
        '',
        '3. MEDEVAC / GOLDEN HOUR',
        `   Overall golden-hour compliance: ${ghPct != null ? `${ghPct.toFixed(1)}%` : 'N/A'} (target ≥90%)`,
        `   Assessment: ${medevacDeficit}`,
        '',
        '4. PERSONNEL',
        `   Total projected casualties: ${s.total_casualties ?? 'N/A'}`,
        `   Return to duty (RTD): ${s.rtd ?? 'N/A'}`,
        `   Net non-recoverable loss: ${netLoss != null ? netLoss : 'N/A'}`,
        '',
        '5. RISK STATEMENT',
        `   ${s.r2b_mascal_days != null || s.blood_stockout_day != null || (ghPct != null && ghPct < ghTarget)
            ? 'SIGNIFICANT MEDICAL RISK identified. Commander action required before operation start.'
            : 'Medical support adequate for planned operation. Monitor R2B occupancy daily.'}`,
        '',
        '───────────────────────────────────────────────────────────────',
        'NOTE: This is a preliminary estimate based on baseline scenario only.',
        'Run stress-test variants (Phase 2) to validate risk bounds.',
        'AJP-4.10C / AJMedP-1 — PSP/CRE Tool v3.0 (Dr. Tatar Radu MD)',
      ];
      const text = lines.join('\n');
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = 'Preliminary_Medical_Assessment.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    const riskCount = [
      s.r2b_mascal_days != null,
      s.blood_stockout_day != null,
      ghPct != null && ghPct < 90,
    ].filter(Boolean).length;
    const riskColor = riskCount === 0 ? 'success' : riskCount === 1 ? 'warning' : 'error';

    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: `${riskColor}.main` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <AssessmentIcon color={riskColor} />
          <Typography variant="h6" sx={{ flex: 1 }}>{t('tab6.prelim_assessment_title')}</Typography>
          <Chip
            label={riskCount === 0 ? t('tab6.low_risk') : riskCount === 1 ? t('tab6.moderate_risk') : t('tab6.high_risk')}
            color={riskColor}
            size="small"
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<GetAppIcon />}
            onClick={handleExportPA}
            color={riskColor}
          >
            {t('tab6.export_txt_btn')}
          </Button>
        </Box>
        <Grid container spacing={1}>
          {[
            { label: t('tab6.surgical_gap_day'),       value: s.r2b_mascal_days != null ? `${t('tab6.day_label')} ${s.r2b_mascal_days}` : t('tab6.no_overload'),    warn: s.r2b_mascal_days != null },
            { label: t('tab6.blood_stockout_day_label'), value: s.blood_stockout_day != null ? `${t('tab6.day_label')} ${s.blood_stockout_day}` : t('tab6.no_stockout'), warn: s.blood_stockout_day != null },
            { label: t('tab6.golden_hour_compl'),      value: ghPct != null ? `${ghPct.toFixed(1)}%` : 'N/A',                                                         warn: ghPct != null && ghPct < 90 },
            { label: t('tab6.net_nonrecov_loss'),      value: netLoss != null ? String(netLoss) : 'N/A',                                                              warn: false },
          ].map(({ label, value, warn }) => (
            <Grid size={{ xs: 6, sm: 3 }} key={label}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="body2" fontWeight="bold" color={warn ? 'error.main' : 'text.primary'}>
                {value}
              </Typography>
            </Grid>
          ))}
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {t('tab6.coa_comparison_hint')}
        </Typography>
      </Paper>
    );
  }

  // -- Main render ------------------------------------------------------------
  return (
    <Box sx={{ p: 2 }}>

      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <CompareArrowsIcon color="primary" fontSize="large" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight="bold">
            {t('tab6.page_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('tab6.page_subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Status alerts — always visible */}
      {renderStatusAlerts()}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* PHASE 1 — ASSESS                                               */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Accordion
        expanded={phaseExpanded.assess}
        onChange={(_, open) => setPhaseExpanded(prev => ({ ...prev, assess: open }))}
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <AssessmentIcon color="primary" />
            <Typography fontWeight="bold">{t('tab6.phase1_title')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {t('tab6.phase1_subtitle')}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {isLoaded && baselineSummary && (
              <>
                <Chip label={`${baselineSummary.total_casualties ?? '?'} cas`} size="small" color="default" sx={{ fontSize: '0.68rem' }} />
                <Chip label={`GH ${baselineSummary.golden_hour_pct != null ? `${baselineSummary.golden_hour_pct.toFixed(0)}%` : '?'}`}
                  size="small" color={baselineSummary.golden_hour_pct != null && baselineSummary.golden_hour_pct >= 90 ? 'success' : 'warning'} sx={{ fontSize: '0.68rem' }} />
                {baselineSummary.blood_stockout_day != null && (
                  <Chip label={`Stk D${baselineSummary.blood_stockout_day}`} size="small" color="error" sx={{ fontSize: '0.68rem' }} />
                )}
              </>
            )}
            {!isLoaded && <Chip label={t('tab6.no_baseline_loaded')} size="small" color="default" variant="outlined" sx={{ fontSize: '0.68rem' }} />}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderNATOBanner()}
          {renderLoaderSection()}
          {isLoaded && renderBaselinePanel()}
          {isLoaded && renderPreliminaryAssessment()}
        </AccordionDetails>
      </Accordion>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* PHASE 2 — STRESS TEST                                          */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Accordion
        expanded={phaseExpanded.stress_test}
        onChange={(_, open) => setPhaseExpanded(prev => ({ ...prev, stress_test: open }))}
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <FlashOnIcon color="warning" />
            <Typography fontWeight="bold">{t('tab6.phase2_title')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {t('tab6.phase2_subtitle')}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {variants.length > 0 && (
              <Chip label={`${variants.filter(v => v.results).length}/${variants.length} calculated`} size="small" color="info" sx={{ fontSize: '0.68rem' }} />
            )}
            {approvedVariantId && (
              <Chip label={t('tab6.variant_selected')} size="small" color="success" icon={<CheckCircleIcon />} sx={{ fontSize: '0.68rem' }} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderVariantScaffold()}
        </AccordionDetails>
      </Accordion>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* PHASE 3 — ANALYSE                                              */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Accordion
        expanded={phaseExpanded.analyse}
        onChange={(_, open) => setPhaseExpanded(prev => ({ ...prev, analyse: open }))}
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <BarChartIcon color="secondary" />
            <Typography fontWeight="bold">{t('tab6.phase3_title')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {t('tab6.phase3_subtitle')}
            </Typography>
            <Box sx={{ flex: 1 }} />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {/* ── CoA A vs CoA B ── (BUG-TAB6-ARCH-001: doctrine comparison, two independent full scenarios) */}
          <Typography variant="overline" color="text.secondary" sx={{ pl: 0.5 }}>
            Section 1 — Course of Action Comparison (Doctrine)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 0.5, mb: 1 }}>
            Two independent full scenarios saved from Tab 5. Each CoA has its own force structure,
            medical configuration, and logistics. Valid per ATP 4-02.6 / AJP-4.10C.
          </Typography>
          {renderCoAComparison()}

          <Divider sx={{ my: 3 }} />

          {/* ── Variant Sensitivity Analysis ── (baseline + parameter tweaks) */}
          <Typography variant="overline" color="text.secondary" sx={{ pl: 0.5 }}>
            Section 2 — Variant Sensitivity Analysis
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 0.5, mb: 1 }}>
            Baseline scenario with parameter modifications. Shows how individual changes affect
            outcomes — not a doctrine CoA comparison. Use for uncertainty quantification only.
          </Typography>
          {renderComparisonView()}
          {renderSensitivityAnalysis()}

          <Divider sx={{ my: 3 }} />

          {/* ── MTF timeline ── */}
          {renderDynamicMTFPanel()}
        </AccordionDetails>
      </Accordion>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* PHASE 4 — COORDINATE                                           */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Accordion
        expanded={phaseExpanded.coordinate}
        onChange={(_, open) => setPhaseExpanded(prev => ({ ...prev, coordinate: open }))}
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <AltRouteIcon color="info" />
            <Typography fontWeight="bold">{t('tab6.phase4_title')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {t('tab6.phase4_subtitle')}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {approvalEvents.length > 0 && (
              <Chip label={`${approvalEvents.length} event${approvalEvents.length !== 1 ? 's' : ''}`} size="small" color="default" sx={{ fontSize: '0.68rem' }} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {renderENH013Panel()}
        </AccordionDetails>
      </Accordion>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* PHASE 5 — REPORT                                               */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Accordion
        expanded={phaseExpanded.report}
        onChange={(_, open) => setPhaseExpanded(prev => ({ ...prev, report: open }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <SummarizeIcon color="success" />
            <Typography fontWeight="bold">{t('tab6.phase5_title')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {t('tab6.phase5_subtitle')}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {variants.some(v => v.approved) && (
              <Chip label={t('tab6.annex_q_ready')} size="small" color="success" icon={<GavelIcon />} sx={{ fontSize: '0.68rem' }} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          {/* Evacuation Analysis */}
          <Paper elevation={2} sx={{ p: 2, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AirplanemodeActiveIcon color="primary" fontSize="small" />
              <Typography variant="h6">{t('tab6.evac_analysis')}</Typography>
              <Chip label={t('tab6.from_tab5')} size="small" color="primary" variant="outlined" />
            </Box>
          </Paper>
          {baseline?.results ? renderTab6StratevacCapacity(baseline.results)         : renderNoResultsPlaceholder('STRATEVAC Capacity')}
          {baseline?.results ? renderTab6StratevacScheduleAnalysis(baseline.results) : renderNoResultsPlaceholder('STRATEVAC Schedule Analysis')}
          {baseline?.results ? renderTab6EvacuationRoutes(baseline.results)          : renderNoResultsPlaceholder('Evacuation Routes')}
          {baseline?.results ? renderTab6TacticalEvacCapacity(baseline.results)      : renderNoResultsPlaceholder('Tactical Evacuation Capacity')}
          {baseline?.results ? renderTab6DailyProjectionChart(baseline.results)      : renderNoResultsPlaceholder('Daily Projection Chart')}

          {/* Supporting Data */}
          <Paper elevation={2} sx={{ p: 2, mb: 1, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon color="secondary" fontSize="small" />
              <Typography variant="h6">Supporting Data</Typography>
              <Chip label={t('tab6.also_in_tab5')} size="small" color="secondary" variant="outlined" />
            </Box>
          </Paper>
          {baseline?.results ? renderTab6InjuryMechanismAnalysis(baseline.results) : renderNoResultsPlaceholder('Injury Mechanism Analysis')}
          {baseline?.results ? renderTab6ResupplyAnalysis(baseline.results)         : null}
          {baseline?.results ? renderTab6G1Summary(baseline.results)                : null}
          {isLoaded && renderPerNationLogistics()}

          {/* Formal Reports */}
          {renderReportsSection()}
          {renderMEDSITREP()}
          {renderAnnexQ()}
          {renderRequestsToHigher()}
        </AccordionDetails>
      </Accordion>

      {/* Bottom idle prompt */}
      {loadStatus === LOAD_STATUS.IDLE && (
        <Alert severity="info" sx={{ mt: 1 }}>
          <AlertTitle>{t('tab6.no_baseline_loaded')}</AlertTitle>
          Open Phase 1 (Assess) above and load a baseline to begin. The <em>Tab 5
          Baseline</em> path is recommended — it includes pre-calculated results and enables
          full comparison metrics in Phase 3.
        </Alert>
      )}

      {/* ENH-030: Report Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Report Preview — {previewReportId && Object.values(REPORT_TYPES).find(r => r.id === previewReportId)?.name}
        </DialogTitle>
        <DialogContent dividers>
          <iframe
            srcDoc={previewContent}
            style={{ width: '100%', height: '70vh', border: 'none' }}
            title={t('tab6.report_preview_title')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
          <ButtonGroup variant="contained">
            <Tooltip title={t('tab6.download_html')}>
              <Button onClick={() => handleExport(previewReportId, 'html')}><ArticleIcon /></Button>
            </Tooltip>
            <Tooltip title={t('tab6.download_pdf')}>
              <Button onClick={() => handleExport(previewReportId, 'pdf')}><PictureAsPdfIcon /></Button>
            </Tooltip>
            <Tooltip title={t('tab6.download_word')}>
              <Button onClick={() => handleExport(previewReportId, 'docx')}><DescriptionIcon /></Button>
            </Tooltip>
            <Tooltip title={t('tab6.download_excel')}>
              <Button onClick={() => handleExport(previewReportId, 'xlsx')}><TableChartIcon /></Button>
            </Tooltip>
            <Tooltip title={t('tab6.download_json')}>
              <Button onClick={() => handleExport(previewReportId, 'json')}><CodeIcon /></Button>
            </Tooltip>
          </ButtonGroup>
        </DialogActions>
      </Dialog>

      {/* Improvement 3: Load approved plan as working plan — confirmation dialog */}
      <Dialog
        open={loadWorkingPlanDialog}
        onClose={() => setLoadWorkingPlanDialog(false)}
      >
        <DialogTitle>Load as Working Plan?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This will overwrite the current Tabs 2/3/4 configuration with the snapshot from the approved plan:
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            {approvedCoASlot === 1 ? (coaSlot1?.name || 'CoA A') : (coaSlot2?.name || 'CoA B')}
          </Typography>
          <Alert severity="warning" sx={{ mt: 1 }}>
            All unsaved changes to Operations, Medical, and Logistics tabs will be replaced.
            The current calculation in Tab 5 will need to be re-run.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadWorkingPlanDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FolderOpenIcon />}
            onClick={() => {
              const approvedSnap = approvedCoASlot === 1
                ? coaSlot1?.snapshot
                : coaSlot2?.snapshot;
              if (approvedSnap && onLoadApprovedPlan) {
                onLoadApprovedPlan(approvedSnap);
              }
              setLoadWorkingPlanDialog(false);
            }}
          >
            Confirm Load
          </Button>
        </DialogActions>
      </Dialog>

      {/* Re-approval confirmation dialog */}
      <Dialog
        open={reApproveDialog.open}
        onClose={() => setReApproveDialog({ open: false, pendingSlot: null, pendingVariantId: null })}
      >
        <DialogTitle>Change Planning Basis?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            You are changing the approved planning basis from{' '}
            <strong>
              {approvedCoASlot === 1 ? (coaSlot1?.name || 'CoA A') :
               approvedCoASlot === 2 ? (coaSlot2?.name || 'CoA B') : 'current plan'}
              {approvedVariantId ? ` / ${variants.find(v => v.id === approvedVariantId)?.name || 'variant'}` : ''}
            </strong>
            {' '}to{' '}
            <strong>
              {reApproveDialog.pendingSlot === 1 ? (coaSlot1?.name || 'CoA A') : (coaSlot2?.name || 'CoA B')}
              {reApproveDialog.pendingVariantId ? ` / ${variants.find(v => v.id === reApproveDialog.pendingVariantId)?.name || 'variant'}` : ''}
            </strong>.
          </Typography>
          <Alert severity="warning">
            This will update the Tab 7 exercise baseline. If an exercise is in progress,
            existing inject calculations will remain but new ones will use the updated baseline.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReApproveDialog({ open: false, pendingSlot: null, pendingVariantId: null })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => handleCommitApproval(reApproveDialog.pendingSlot, reApproveDialog.pendingVariantId)}
          >
            Confirm Change
          </Button>
        </DialogActions>
      </Dialog>

      {/* Step 5: Requests to Higher — consolidated report dialog */}
      <Dialog
        open={rthReportDialog}
        onClose={() => setRthReportDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{'Planning Gaps Report \u2014 Requests to Higher'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            multiline
            fullWidth
            minRows={12}
            value={rthReportText}
            onChange={(e) => setRthReportText(e.target.value)}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRthReportDialog(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<ContentCopyIcon />}
            onClick={() => {
              navigator.clipboard.writeText(rthReportText).catch(() => {});
              setSnackbar({ open: true, severity: 'success', message: 'Report copied to clipboard' });
            }}
          >
            Copy to Clipboard
          </Button>
        </DialogActions>
      </Dialog>

      {/* ENH-030: Export notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ── Bottom Navigation ───────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => onNavigate && onNavigate(4)}
        >
          {t('tab6.back_to_tab5')}
        </Button>

        <Button
          variant="contained"
          color="warning"
          endIcon={<ArrowForwardIcon />}
          startIcon={<ConstructionIcon />}
          onClick={() => {
            onComplete && onComplete();
            onNavigate && onNavigate(6);
          }}
          sx={{ fontWeight: 700 }}
        >
          {t('tab6.go_to_tab7')}
        </Button>
      </Box>

    </Box>
  );
});

export default Tab6COAAnalysis;
